"use client"

import type React from "react"

import { useState, useEffect, useRef, useMemo, Fragment } from "react"
import { Table, TableBody, TableCell, TableHead, Header, TableRow } from "@/components/ui/table"
import { Checkbox } from "@/components/ui/checkbox"
import { EditColumns } from "@/components/edit-columns"
import { ExportModal } from "@/components/export-modal"
import { EditFieldModal } from "@/components/edit-field-modal"
import { ColumnContextMenu } from "@/components/column-context-menu"
import { GridCell } from "@/components/grid/grid-cell"
import { GridHeader } from "@/components/grid/grid-header"
import { useWorkflow } from "@/context/workflow-context-simple"
import type { WorkflowStage, WorkflowItem } from "@/types/workflow"
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { CustomFilterIcon } from "@/components/custom-filter-icon"
import { defaultBooleanOptions } from './constants'
import { ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Pin, Lock, Info } from 'lucide-react'
import DOMPurify from 'isomorphic-dompurify'

import { ModernDatePicker } from "@/components/modern-date-picker"
import { getLocalizedField } from '@/utils/language-utils'
import { useTranslation } from '@/hooks/use-translation'
import { useLanguage } from '@/context/language-context'
import { useWorkflowApi } from "@/hooks/use-workflow-api"
import { useEntityType } from "@/contexts/entity-type-context"
import { getMessageEntityType } from "@/lib/client-request-action-utils"
import { resolveMessageConversationKey } from '@/lib/message-conversation-key'
import { mergeRetainedFromParent } from '@/lib/retained-child-values'
import { useMessageReadActions } from '@/hooks/use-message-read-actions'
import { useMessageBadgeRefresh } from '@/hooks/use-message-badge-refresh'
import { useMessageNotifications } from '@/hooks/use-message-notifications'
import { useStageFacets } from "@/hooks/use-stage-facets"
import { useToast } from '@/hooks/use-toast'
import { BulkEditModal } from './bulk-edit-modal'
import { getSearchColumnPath, getFacetKeysFromConfiguration, getSelectFieldsFromConfiguration, getDisplayValueFromColumn } from '@/utils/column-type-helpers';
import { MoveToModal } from '@/components/move-to-modal';
import { useCodeValues } from '@/hooks/use-code-values';
import { isBooleanField, isDateField, isNumberField, isSearchableField, isFacetableField, isFilterableField, hasCodeValueSource, isTextListField, isCollectionField, buildCollectionFilter, buildSearchableFilter, buildFacetedValuesFilter } from '@/utils/column-type-helpers';
import { WORKER_NAME_COLUMN_CODE, buildWorkerScopeFilter } from '@/lib/deep-link-params';
import { SORT_COLUMN_OVERRIDES } from '@/constants/sort-column-overrides';
import { WindowRefService } from '@/lib/window-ref-service';
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking';
import { AuditHistoryPanel } from '@/components/audit-history-panel';
import { VmsEmailPreviewPanel } from '@/components/vms/vms-email-preview-panel';
import { ChecklistPanel } from '@/components/checklist-panel';
import { MessageTrigger } from '@/components/message-trigger';
import { AppliedFilterPills, GroupedPillList, getFilterValuesPreview, type AppliedFilterPill } from '@/components/grid/applied-filter-pills';
import { ActionConfirmationModal } from '@/components/action-confirmation-modal';
import { useApi } from "@/lib/api";
import { useAtsPlacementApi } from "@/hooks/ats/use-ats-placements-api";
import { ExtendWorkOrderModal } from '@/components/extend-work-order-modal';
import { JumpToColumnDialog, type JumpToColumnOption } from '@/components/jump-to-column-dialog';
import { useJumpToColumn } from '@/hooks/use-jump-to-column';
import { useAddColumnToView } from '@/hooks/use-add-column-to-view';
import { ClientRequestModal } from '@/components/client-request-modal';
import { RelatedRequestsPanel } from '@/components/related-requests-panel';
import { ClientRequestReadonlyDetail } from '@/components/client-request-readonly-detail';
import { OnboardingIframePanel } from '@/components/onboarding-iframe-panel';
import { useClientRequestsApi } from '@/hooks/use-client-requests-api';
import { useVmsApi } from '@/hooks/vms/use-vms-api';
import { sendMessageToParent } from '@/lib/app-messaging';
import { useParentApp } from '@/contexts/parent-app-context';
import { buildOnboardingPath } from '@/lib/client-request-action-utils';
import type { SubmitResult } from '@/types/onboarding';
import type { ClientRequestStatus } from '@/types/client-requests';
import { getOnboardingSubmitFeedback } from '@/lib/onboarding-submit-feedback';
import { Clock, MessageCircle, MoreVertical, Loader2, Check, ExternalLink, FileText, Grip, GripVertical, ArrowRight, XCircle, Mail, MailOpen, Bell, BellOff, History, RefreshCw, Trash2, Eye, Ban, Play, Pause } from 'lucide-react';
import { useFeatureFlags } from '@/contexts/feature-flag-context';
import { useAuth } from '@/contexts/auth-context';
import { Z_INDEX } from '@/lib/z-index';
import { processExternalSources, substituteFieldsInUrl, evaluateCondition } from '@/lib/external-source-utils';
import { getAvailableActions, executeAction, buildConditionContext, buildSentIdToLocalIdMap } from '@/lib/action-utils';
import type { ActionUserContext } from '@/lib/action-utils';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';

interface WorkflowGridProps {
  stage: string
  /**
   * Hide the pin dock (drag-to-pin) tile in the header. VMS sub-tabs don't
   * use column pinning — pass `true` there to keep the header tidy.
   */
  hidePinDock?: boolean
  /**
   * Hide the Jump-to-Column (Cmd/Ctrl+K) tile in the header. Same rationale.
   */
  hideJumpToColumn?: boolean
  /**
   * Replaces the title (e.g. "Onboarding (108)") on the left side of the
   * header. Used by VMS to inline its Requests/Emails sub-tab selector.
   */
  headerTitleSlot?: React.ReactNode
  /**
   * Extra buttons appended just before the Filter button. Used by VMS to
   * surface the Settings entry point inside the header instead of a separate
   * top toolbar.
   */
  headerExtraControls?: React.ReactNode
  /**
   * Buttons that render BEFORE Edit columns (at the front of the right-side
   * action group). VMS uses this for the round Settings icon button so it
   * lands first in the row, matching the ATS tab pattern.
   */
  headerLeadingControls?: React.ReactNode
  /**
   * Fired when a kebab action of type `open_linked_request` is invoked on a
   * row — currently only the VmsEmail "View Linked Request" action wires
   * this. The host (vms/tab.tsx) responds by switching the VMS sub-tab to
   * Requests and scoping the grid to the linked request id. Left undefined,
   * the action is a no-op and logs a console warning, matching the other
   * optional dispatch callbacks (onEmailPreview, onCustomModal).
   */
  onOpenLinkedRequest?: (requestId: string, itemTitle: string) => void
}

// Helper to get cell value by case-insensitive columnCode converted to camelCase
function getCellValue(item: any, column: any) {
  if (!item || !column || !column.columnCode) return undefined;
  
  // Convert column code to camelCase
  const targetKey = column.columnCode.charAt(0).toLowerCase() + column.columnCode.slice(1);
  // First try exact match
  if (item[targetKey] !== undefined) {
    return item[targetKey];
  }
  
  // Then try case-insensitive match
  const foundKey = Object.keys(item).find(k => k.toLowerCase() === targetKey.toLowerCase());
  return foundKey !== undefined ? item[foundKey] : undefined;
}



// Add this helper at the top of the file (after imports)
function getFacetOptionsForColumn(facets: any, column: any): { value: string; count: number }[] {
  if (!facets || !column) return [];
  
  const searchPath = getSearchColumnPath(column);
  
  // 1. Direct match with search path
  if (Array.isArray(facets[searchPath]) && facets[searchPath].length > 0) {
    return facets[searchPath].filter((opt: { value: string; count: number }) => opt.value !== "");
  }
  // 2. Direct match with column code
  if (Array.isArray(facets[column.columnCode]) && facets[column.columnCode].length > 0) {
    return facets[column.columnCode].filter((opt: { value: string; count: number }) => opt.value !== "");
  }
  // 3. Case-insensitive match
  const foundKey = Object.keys(facets).find(
    k => k.toLowerCase() === searchPath.toLowerCase()
  );
  if (foundKey && Array.isArray(facets[foundKey])) {
    return facets[foundKey].filter((opt: { value: string; count: number }) => opt.value !== "");
  }
  // 4. Try replacing / with nothing (for edge cases)
  const altKey = searchPath.replace(/\//g, '');
  const foundAlt = Object.keys(facets).find(
    k => k.replace(/\//g, '').toLowerCase() === altKey.toLowerCase()
  );
  if (foundAlt && Array.isArray(facets[foundAlt])) {
    return facets[foundAlt].filter((opt: { value: string; count: number }) => opt.value !== "");
  }
  return [];
}

const CHECKBOX_COLUMN_WIDTH = 80;
const CHEVRON_ONLY_WIDTH = 28;

/**
 * Terminal-status filter applied when the Show Completed toggle is OFF on
 * the VMS sub-tabs. The two entity types share the toggle plumbing but
 * have distinct status vocabularies — VmsRequest carries the request
 * lifecycle (RequestAccepted = done, Canceled = dead) while VmsEmail
 * carries the email lifecycle (Processed = done, Discarded = soft-deleted).
 *
 * Returning an empty string for an unrecognised entity type means "no
 * exclusion" — safer than throwing here because the caller already gates
 * on `stageConfig.showCompletedToggle` and a future VMS entity getting
 * added without an entry would simply mean its toggle has no effect
 * until the table is updated.
 */
function getVmsHideCompletedFilter(entityType: string | null | undefined): string {
  if (entityType === 'VmsRequest') {
    return `Status ne 'RequestAccepted' and Status ne 'Canceled'`;
  }
  if (entityType === 'VmsEmail') {
    return `Status ne 'Processed' and Status ne 'Discarded'`;
  }
  if (entityType === 'ClientRequest') {
    // Mirrors TERMINAL_STATUSES from the bespoke client-requests-grid
    // (RequestAccepted = lifecycle done; Canceled = terminal). Same shape as the
    // VmsRequest filter above. Function name kept for blame continuity now that
    // the helper covers more than just VMS — rename to e.g. getHideCompletedFilter
    // when ATS / PreOnboard join.
    return `Status ne 'RequestAccepted' and Status ne 'Canceled'`;
  }
  if (entityType === 'AtsPlacementRequest') {
    // ATS terminal statuses (COMPLETED_STATUSES in types/ats/ats-placements): all three pre-WO
    // entities now share the canonical RequestAccepted / Canceled. 'Accepted' / 'Cancelled' are the
    // pre-alignment ATS spellings, kept as READ tolerance until the Phase 7 status migration — drop
    // them there, alongside AtsPlacementRequestStatuses.Normalize.
    // Latent today (this function is only reached for VmsRequest / VmsEmail), but the branch exists
    // precisely to be switched on later, and listing only the legacy spellings would leak every
    // RequestAccepted / Canceled row into the active tabs the moment it is.
    return `Status ne 'RequestAccepted' and Status ne 'Canceled' and Status ne 'Accepted' and Status ne 'Cancelled'`;
  }
  return '';
}

// Helper to get display name for AssignedToUser/Email
function getAssignedUserDisplay(email: string, assignedUsers: { email: string; fullName: string }[]): string {
  const user = assignedUsers.find(u => u.email?.toLowerCase() === email?.toLowerCase());
  if (user) {
    return user.fullName ? user.fullName.replace(/\w\S*/g, (txt: string) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()) : user.email;
  }
  return email;
}

// For customValues (code/titleEnglish/titleFrench), use opt.code for value and opt.titleEnglish/titleFrench for label
const isCustomValueOption = (opt: any): opt is { code: string; titleEnglish: string; titleFrench: string; isDeleted: boolean } => 'code' in opt && 'titleEnglish' in opt;
const getOptionValue = (opt: any) => isCustomValueOption(opt) ? opt.code : (typeof opt.value === 'string' ? opt.value : String(opt.value ?? ''));
const getOptionLabel = (opt: any, column: any, assignedUsers: any[], language: string) => {
  const value = getOptionValue(opt);
  if (column.columnCode === 'AssignedToUser' || column.columnCode === 'AssignedToUser/Email') {
    return getAssignedUserDisplay(value, assignedUsers);
  }
  if (isCustomValueOption(opt)) {
    return language === 'fr' ? (opt.titleFrench || opt.code) : (opt.titleEnglish || opt.code);
  }
  // Facet-derived options carry only { value, count } (no title). Resolve their
  // display label against the column's CustomValues (Code→Title) when defined.
  // Covers fields like Status that declare CustomValues but no CodeValueSource:
  // those take the raw-facet branch in both the column popover and the global
  // filter sidebar, so without this they render the bare code (e.g.
  // "PreVerificationRequested" instead of "Pre-Verification Requested").
  if (Array.isArray(column.customValues) && column.customValues.length > 0) {
    const cv = column.customValues.find((c: any) => c.code === value);
    if (cv) return language === 'fr' ? (cv.titleFrench || cv.code) : (cv.titleEnglish || cv.code);
  }
  return typeof opt.label === 'string' ? opt.label : value;
};

// Helper to determine if a column is filtered for the current stage (for blue filter icon)
function isColumnFiltered(currentStageFilters: any[], column: any): boolean {
  const filterObj = currentStageFilters.find(f => f.fieldConfigurationId === column.fieldConfigurationId);
  return !!(filterObj && filterObj.filter && filterObj.filter.length > 0);
}

/**
 * Width of the two icon-only columns, Actions (kebab) and Message.
 *
 * Both are **fixed and not resizable**: they hold a single centred icon, so a user-dragged width is
 * never an improvement, and a persisted one silently follows the view around. Kept equal so the two
 * read as a pair. Changing this is the one place to do it — the seeding, the sticky-offset maths and
 * the resize guard all derive from it.
 */
const FIXED_ACTION_COLUMN_WIDTH = 50;
// The message column carries the md chat bubble (32px) plus a count badge hanging off its corner, so it
// needs more room than the bare kebab/actions column.
const MESSAGE_COLUMN_WIDTH = 58;

/**
 * Whether a column is one of the two fixed-width icon columns.
 *
 * Matched on the column OBJECT, not its id: the Message column is keyed by a GUID
 * `fieldConfigurationId`, so `getSafeColumnWidth` — which only ever sees an id — cannot recognise it.
 * That is why a width saved into `UserViewState` before these were pinned kept coming back and
 * widening the column: seeding and persistence were fixed, but the render still read the map.
 */
function isFixedWidthColumn(column: any): boolean {
  return Boolean(column?.isActionColumn) && (column.actionType === 'menu' || column.actionType === 'message');
}

/** Rendered width for a column. Authoritative for the fixed pair regardless of what is stored. */
function getColumnWidth(columnWidths: Record<string, number>, column: any): number {
  if (isFixedWidthColumn(column)) {
    // The message column is wider than the bare actions/kebab column (it carries the chat bubble plus a
    // count badge) — see MESSAGE_COLUMN_WIDTH. Both are pinned here regardless of what a stale
    // UserViewState left in the map.
    return column?.actionType === 'message' ? MESSAGE_COLUMN_WIDTH : FIXED_ACTION_COLUMN_WIDTH;
  }
  return getSafeColumnWidth(columnWidths, column?.fieldConfigurationId);
}

// Helper function to safely get column width with fallbacks for fixed columns
function getSafeColumnWidth(columnWidths: Record<string, number>, columnId: string): number {
  // V2: Checkbox column with chevron always returns 80px regardless of what's stored
  if (columnId === 'checkbox') {
    return 80;
  }
  // V2: Actions column always returns 50px
  if (columnId === 'actions-column') {
    return FIXED_ACTION_COLUMN_WIDTH;
  }
  // The Message column is keyed by a GUID fieldConfigurationId, so it cannot be recognised here.
  // It is pinned by never letting another value into columnWidths for it — see the seeding effect,
  // the resize guard and the save payload below.
  return columnWidths[columnId] || 160;
}

// Sortable wrapper for draggable column headers
function SortableTableHead({ id, disabled, children, style, className, ...rest }: {
  id: string;
  disabled?: boolean;
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  [key: string]: any;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  const [showGrabHandle, setShowGrabHandle] = useState(false);
  const combinedStyle: React.CSSProperties = {
    ...style,
    transform: CSS.Transform.toString(transform ? { ...transform, scaleX: 1, scaleY: 1 } : null),
    transition,
    opacity: isDragging ? 0.5 : undefined,
    outline: isDragging ? '2px solid #3B82F6' : undefined,
  };
  return (
    <TableHead
      ref={setNodeRef}
      className={className}
      style={combinedStyle}
      {...rest}
      onMouseEnter={(e: React.MouseEvent) => {
        if (!disabled) setShowGrabHandle(true);
        rest.onMouseEnter?.(e);
      }}
      onMouseLeave={(e: React.MouseEvent) => {
        if (!disabled) setShowGrabHandle(false);
        rest.onMouseLeave?.(e);
      }}
    >
      {/* Grab handle for drag-and-drop — slides down from top on hover */}
      {!disabled && (
        <div
          {...attributes}
          {...listeners}
          className="column-grab-handle"
          style={{
            position: 'absolute',
            top: showGrabHandle || isDragging ? 0 : -6,
            left: '50%',
            transform: 'translateX(-50%)',
            opacity: showGrabHandle || isDragging ? 1 : 0,
            pointerEvents: showGrabHandle || isDragging ? 'auto' : 'none',
            transition: 'opacity 0.2s ease, top 0.2s ease',
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 14,
            borderRadius: '0 0 6px 6px',
            background: isDragging ? '#3B82F6' : '#e2e2f0',
            cursor: 'grab',
            boxShadow: '0 1px 3px rgba(0,0,0,0.10)',
          }}
          title="Drag to reorder"
        >
          <GripVertical size={10} style={{ color: isDragging ? '#fff' : '#6b7280' }} />
        </div>
      )}
      {children}
    </TableHead>
  );
}


export function WorkflowGrid({
  stage,
  hidePinDock,
  hideJumpToColumn,
  headerTitleSlot,
  headerExtraControls,
  headerLeadingControls,
  onOpenLinkedRequest,
}: WorkflowGridProps) {
  // VMS docs in the shared client-request index don't carry a `Stage` field —
  // they have `Status`. The default base filter (`Stage eq '<code>'`) below
  // would silently reject every row, so we skip it for VMS entity types and
  // let the backend's RequestType-scoped filter do the heavy lifting.
  const { entityType: contextEntityType } = useEntityType()
  // Parent shell (FBO vs client Portal) — drives onboarding form routing so client
  // users land on the portal onboarding form, internal users on the sync form.
  const { parentApp } = useParentApp()
  // The Requests-tab entities (VmsRequest, VmsEmail, ClientRequest, AtsPlacementRequest).
  // These have NO `Stage` column — they're status-based, not stage-based: they bypass the
  // WorkOrder Assignment+Stage filter prefix and live on the shared client-request index
  // with their own DocumentType discriminator. Status transitions update in place (no
  // stage row-removal), and the visibility toggle only includes terminal statuses.
  // Only WorkOrder/Assignment are stage-based. (Was `isVmsEntityType` — renamed for clarity
  // since it always covered all four Requests entities, not just VMS.)
  const isRequestEntityType = contextEntityType === 'VmsRequest' || contextEntityType === 'VmsEmail'
    || contextEntityType === 'ClientRequest' || contextEntityType === 'AtsPlacementRequest'
  const headerRef = useRef<HTMLDivElement>(null);
  const paginationRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const mainContainerRef = useRef<HTMLDivElement>(null);

  const {
    workOrders,
    totalCount,
    hasNextPage,
    hasPreviousPage,
    configuration,
    selectedItems,
    toggleItemSelection,
    selectAllItems,
    deselectAllItems,
    updateWorkOrderField,
    updateWorkOrderFieldOptimistically,
    applyOptimisticUpdates,
    isLoadingConfiguration,
    isLoadingWorkOrders,
    isLoadingRows,
    currentPage,
    pageSize,
    setCurrentPage,
    setPageSize,
    setItemsPerPage,
    stages,
    updateFilter,
    refreshData,
    removeWorkOrder,
    removeWorkOrdersBulk,
    facets,
    assignedUsers,
    stageFilters,
    globalFilters,
    setStageFilters,
    setGlobalFilters,
    // Temporary OData filter (e.g. `id eq '<X>'`) applied via cross-tab
    // navigation flows like VmsEmail → linked VmsRequest. Surfaced as a
    // dismissible chip above the grid; cleared via clearTemporaryFilter
    // (or Clear Filters, which clears it alongside stage/global filters).
    temporaryQueryFilter,
    temporaryQueryFilterLabel,
    temporaryQueryFilterPausesStageFilters,
    applyTemporaryQueryFilter,
    clearTemporaryFilter,
    workOrdersFacets,
    setSelectedItems,
    setColumnWidths: setColumnWidthsFromContext,
    state,
    setPinnedColumn,
    sortByStage,
    setSortForStage,
    showCompletedByStage,
    setShowCompleted,
    refreshConfiguration,
    updateVisibleColumns,
    currentView,
    setCurrentView,
    setUnsavedChanges,
    viewsResponse,
  } = useWorkflow()

  // Path 2: imperative refresh after mutations
  const { refreshStageCounts } = useStageFacets()

  // Tracks pending refresh timers so a new action cancels the previous ones
  const refreshTimersRef = useRef<ReturnType<typeof setTimeout>[]>([])

  // Clear all pending refresh timers on unmount to prevent state updates on a dead component
  useEffect(() => {
    return () => {
      refreshTimersRef.current.forEach(clearTimeout)
      refreshTimersRef.current = []
    }
  }, [])

  // State management
  const [isEditColumnsOpen, setIsEditColumnsOpen] = useState(false)
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)
  const [isEditFieldModalOpen, setIsEditFieldModalOpen] = useState(false)
  const [editFieldConfiguration, setEditFieldConfiguration] = useState<any>(null)
  const [isBrowser, setIsBrowser] = useState(false)
  const [editingCell, setEditingCell] = useState<{ rowId: string; columnId: string } | null>(null)
  const [contextMenu, setContextMenu] = useState<{ position: { top: number; left: number }; columnId: string } | null>(
    null,
  )
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({})
  // State for filter popup
  const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null)
  const [activeFilterColumn, setActiveFilterColumn] = useState<string | null>(null)
  // Checklist panel state
  const [checklistPanelState, setChecklistPanelState] = useState<{
    isOpen: boolean;
    itemId: string;
    workOrderId: string;
    fieldConfigurationId: string;
    itemTitle?: string;
    canEdit?: boolean;
  } | null>(null)

  // Action menu state for right-click row actions
  const [rowActionMenu, setRowActionMenu] = useState<
    | { open: boolean; fboId?: string; obId?: string; rowId: string; x: number; y: number }
    | null
  >(null)
  const [hoveredHeader, setHoveredHeader] = useState<string | null>(null)
  const [headerHeight, setHeaderHeight] = useState(0);
  const [paginationHeight, setPaginationHeight] = useState(0);
  // Global filter sidebar state
  const [isGlobalFilterSidebarOpen, setIsGlobalFilterSidebarOpen] = useState(false);
  // Drawer width constant
  const FILTER_DRAWER_WIDTH = 360;
  // For smooth closing animation
  const [drawerVisible, setDrawerVisible] = useState(false);
  // State for global filter search
  const [globalFilterSearch, setGlobalFilterSearch] = useState("");
  // Ensure sortState is defined as local state — initialized from saved per-stage sort if available
  const [sortState, setSortState] = useState<{ columnCode: string | null, direction: 'asc' | 'desc' | null }>(() => {
    // The grid remounts on every stage change, so we can read the saved sort here
    const stageConf = configuration?.stageConfigurations?.find(
      (s) => s.stageCode.toLowerCase() === stage?.toLowerCase()
    )
    const saved = stageConf?.stageId ? (sortByStage || {})[stageConf.stageId] : null
    return saved ? { columnCode: saved.columnCode, direction: saved.direction } : { columnCode: null, direction: null }
  });
  // Sync sort state from sortByStage when configuration updates after mount
  // (e.g. initial load without viewId returns stale sort, then second load with viewId returns correct sort)
  useEffect(() => {
    const stageConf = configuration?.stageConfigurations?.find(
      (s) => s.stageCode.toLowerCase() === stage?.toLowerCase()
    )
    const saved = stageConf?.stageId ? (sortByStage || {})[stageConf.stageId] : null
    if (saved) {
      setSortState(prev => {
        if (prev.columnCode === saved.columnCode && prev.direction === saved.direction) return prev
        return { columnCode: saved.columnCode, direction: saved.direction }
      })
    } else {
      setSortState(prev => {
        if (prev.columnCode === null && prev.direction === null) return prev
        return { columnCode: null, direction: null }
      })
    }
  }, [sortByStage, configuration, stage])

  // Ensure dateFilter is defined as local state
  const [dateFilter, setDateFilter] = useState<{ start: string | null, end: string | null }>({ start: null, end: null });
  // Restore previous working sidebar state and handlers
  const [globalFilterSelections, setGlobalFilterSelections] = useState<Record<string, string[]>>({});
  const [globalFilterShowCount, setGlobalFilterShowCount] = useState<Record<string, number>>({});
  // Add facetsFetched state
  const [facetsFetched, setFacetsFetched] = useState(false);
  // Reset facetsFetched whenever the active entity type changes so switching
  // between VMS sub-tabs (or back to WorkOrder) re-fetches the facet aggregations
  // for the new scope rather than reusing stale Requests-side counts.
  useEffect(() => {
    setFacetsFetched(false);
  }, [contextEntityType]);
  // Add local state for popover filter values
  const [popoverFilterValues, setPopoverFilterValues] = useState<Record<string, any>>({});
  // Add this at the top level of the WorkflowGrid component
  const fetchingFacets = useRef(false);
  // Bulk edit modal state
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  // Add state for MoveTo modal
  const [isMoveToOpen, setIsMoveToOpen] = useState(false);
  // Add local state for popover search
  const [popoverSearch, setPopoverSearch] = useState<string>("");
  // Add state to track if initial height calculation is complete
  const [isHeightCalculated, setIsHeightCalculated] = useState(false);
  // Add ref to track if we're just changing pages (vs changing stage/filters)
  const isJustPageChange = useRef(false);
  // Add state for audit history panel
  const [auditHistoryPanel, setAuditHistoryPanel] = useState<{
    isOpen: boolean;
    itemId: string;
    itemTitle?: string;
    entityType?: string;
  } | null>(null);

  // VMS email preview slide-out. Open when the kebab fires ViewEmail (on a
  // VmsRequest, resolving the linked email id) or Preview (on a VmsEmail row).
  // Single state since only one preview is ever open at a time.
  const [emailPreviewPanel, setEmailPreviewPanel] = useState<{
    isOpen: boolean;
    emailId: string;
    title?: string;
  } | null>(null);
  
  // Add state for action confirmation modal.
  //
  // Two flavors live here:
  //   - stage_transition actions (default): `onConfirm` is omitted, the
  //     modal falls back to `handlePerformStageTransition` — same path the
  //     WorkOrder lifecycle has always used.
  //   - custom_modal actions that need a confirmation prompt (Reprocess on
  //     VmsRequest / VmsEmail, MarkAsSpam / MarkAsValid /
  //     MoveFromSpamAndProcess on VmsEmail): pass a bespoke `onConfirm`
  //     when opening the modal — the modal forwards (actionCode, items,
  //     notes) and the dispatcher's caller handles the API call + toast +
  //     refresh.
  const [actionConfirmationModal, setActionConfirmationModal] = useState<{
    isOpen: boolean;
    action: any;
    items: any[];
    onConfirm?: (actionCode: string, items: any[], notes?: string) => Promise<void>;
  } | null>(null);

  // Add state for extend work order modal
  const [extendModalItem, setExtendModalItem] = useState<WorkflowItem | null>(null);
  const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
  const extendSucceededRef = useRef(false);
  const [processingItemIds, setProcessingItemIds] = useState<Set<string>>(new Set());
  const [clientRequestModalItem, setClientRequestModalItem] = useState<WorkflowItem | null>(null);
  const [clientRequestModalType, setClientRequestModalType] = useState<'Extension' | 'Termination' | 'Redeployment' | 'General'>('General');
  const [isClientRequestModalOpen, setIsClientRequestModalOpen] = useState(false);
  const [relatedRequestsPanelItem, setRelatedRequestsPanelItem] = useState<WorkflowItem | null>(null);
  // Read-only ClientRequest detail side panel (request main info + documents). Restored from the
  // bespoke CR grid; opened from the kebab "View Details" action. Holds the ClientRequest doc id
  // (the /api/client-request/{id} key, which is the row's RequestId for non-Onboarding types).
  const [detailPanelRequestId, setDetailPanelRequestId] = useState<string | null>(null);

  // VMS-only: state for the Start Onboarding iframe handoff. Mirrors what
  // client-requests-grid.tsx does for ClientRequest's "Proceed to Onboarding"
  // — onboardingId returned by createOnboarding goes here, and the
  // OnboardingIframePanel renders against it.
  //
  // `externalRequestId` captures the row's VmsExternalRequestId (the SEID /
  // SOW# / Job ID the source VMS issued), so the drawer header surfaces a
  // recognizable reference instead of a Cosmos guid. `requestId` (the doc id)
  // is the fallback when the LLM couldn't extract an external ref from the
  // email — at least the operator can correlate to the row.
  const [vmsOnboardingContext, setVmsOnboardingContext] = useState<{
    onboardingId: string;
    requestId: string;
    externalRequestId: string | null;
    // Generalised so the same onboarding-form panel serves VMS, ClientRequest,
    // and ATS. When omitted, the panel falls back to the VMS labelling.
    sourceName?: string;
    idDisplayText?: string;
    // Grid row id (SHA index id) + entity type, captured at initiation so onSubmitted can
    // optimistically flip the row's status and report a source the server did not accept.
    rowId?: string;
    entityType?: string;
    // A client request's status from its row, which routes the drawer's View Pre-Verification link on the Portal host.
    clientRequestStatus?: ClientRequestStatus;
  } | null>(null);
  const { mapVmsRequestToOnboarding, mapRequestToOnboarding, createOnboarding } = useClientRequestsApi();
  // ATS lifecycle actions hit AtsService endpoints (consolidated PATCH + the
  // dedicated hold/cancel/start-pre-verification/resync routes). Only used
  // by the ATS branch of onCustomModal below; harmless for other entity types.
  const { atsServiceApi } = useApi();
  const atsApi = useAtsPlacementApi(atsServiceApi);
  // VMS-specific API surface. Pulls in the pre-verification orchestration
  // call plus the email-kebab handlers (Reprocess / MarkAsSpam /
  // MarkAsValid / MoveFromSpamAndProcess) and the request-side Reprocess
  // (retryParse). All five power the custom_modal action branches below
  // and all five route through ActionConfirmationModal so the operator
  // sees a "Are you sure?" prompt before any data is mutated.
  const {
    startPreVerification,
    retryParse,
    reprocessEmail,
    markEmailAsSpam,
    markEmailAsValid,
    moveEmailFromSpamAndProcess,
    // Manual-create override: surfaced today on emails parked at
    // VmsEmailStatus.PendingManualCreate (sender domain on the public-mail-provider
    // list — auto-create was skipped). Hits POST /api/vms/emails/{id}/create-request.
    // Same shape as moveEmailFromSpamAndProcess so the response handling reuses the
    // requestId-bearing branch.
    createRequestFromEmail,
    deleteEmail,
    bulkDeleteEmails,
  } = useVmsApi();
  
  // Add state for message sidebar monitoring
  const [isMessageSidebarOpen, setIsMessageSidebarOpen] = useState(false);
  // Add state to track which message cell was clicked
  const [clickedMessageRowId, setClickedMessageRowId] = useState<string | null>(null);

  // V2: State for expandable rows
  const [expandedAssignments, setExpandedAssignments] = useState<Set<string>>(new Set());
  const [childWorkOrders, setChildWorkOrders] = useState<Record<string, any[]>>({});
  const [loadingChildren, setLoadingChildren] = useState<Set<string>>(new Set());
  
  // Tracking
  const { trackWorkflowEvent, trackFilterAction, trackGridAction } = useWorkflowTracking();
  // Add state to track previous stage and columns for smooth transitions
  const [previousStage, setPreviousStage] = useState<string | null>(null);
  const [previousColumns, setPreviousColumns] = useState<any[]>([]);
  const [isStageTransitioning, setIsStageTransitioning] = useState(false);
  // Add refs to track previous state without causing re-renders
  const previousStageRef = useRef<string | null>(null);
  const previousColumnsRef = useRef<any[]>([]);
  // Tracks effective checkbox column width for use in resize/layout handlers
  const checkboxColWidthRef = useRef(CHECKBOX_COLUMN_WIDTH);

  // Suggestions state moved into SearchableFieldWithSuggestions to avoid parent re-renders that cause focus loss
  // Add flag to track when a suggestion was selected
  const [selectedFromSuggestions, setSelectedFromSuggestions] = useState<Record<string, boolean>>({});
  // Draft values for searchable inputs to avoid parent re-render/caret jump
  const searchableDraftRef = useRef<Record<string, string>>({});
  // Suggestions cache keyed by columnCode — survives popover remounts so reopening
  // the filter with an existing draft doesn't require a re-fetch or re-typing.
  // Stores { query, suggestions } so a stale draft reopens with matching results only.
  const suggestionsCacheRef = useRef<Record<string, { query: string; suggestions: any[] }>>({});

  // Clears the persistent searchable-filter state (draft input + cached suggestions)
  // for a given column. Passing no columnCode clears state for every column —
  // used by "clear all filters" so reopening a searchable filter afterward cannot
  // restore stale suggestions from before the clear.
  const clearSearchableFilterState = (columnCode?: string) => {
    if (columnCode) {
      searchableDraftRef.current[columnCode] = '';
      delete suggestionsCacheRef.current[columnCode];
    } else {
      searchableDraftRef.current = {};
      suggestionsCacheRef.current = {};
    }
  };

  // State for resizable columns - using refs for global event listeners
  const isResizingRef = useRef(false);
  const resizeStartXRef = useRef(0);
  const resizeStartWidthRef = useRef(0);
  const resizingColumnRef = useRef<string | null>(null);
  // Pre-computed at resize start so handleResizeMove never reads stale closure values
  const resizeColumnIndexRef = useRef<number>(-1);
  const resizeTotalColumnsRef = useRef<number>(0);
  const resizeInitialWidthsRef = useRef<Record<string, number>>({});
  const resizeDisplayColumnsSnapshotRef = useRef<any[]>([]);
  // Tracks the exact handler references added to document so they can be cleaned up
  // even when mouseup fires outside the iframe (where the iframe's document never sees it)
  const pendingResizeHandlersRef = useRef<{ move: (e: MouseEvent) => void; up: (e: MouseEvent) => void } | null>(null);
  
  // Keep useState versions for React rendering
  const [isResizing, setIsResizing] = useState(false);
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);

  const { t } = useTranslation();
  const { language } = useLanguage();
  const { searchWorkflowItems, fetchSearchSuggestions, fetchChildWorkOrders, performStageTransition, validateAtsPlacement, extendWorkOrder, bulkUpdateReports, saveViewState } = useWorkflowApi();
  const { isFeatureEnabled } = useFeatureFlags();
  const { isInternalUser, userProfileTypeId } = useAuth();
  const codeValues = useCodeValues();
  const { toast } = useToast();

  const actionUserContext: ActionUserContext = useMemo(() => ({
    userProfileTypeId,
    isInternalUser,
  }), [userProfileTypeId, isInternalUser]);

  // Feature flag to hide message column
  const isMessageColumnHidden = !isFeatureEnabled('messages');

  // Pending items filter constants
  const pendingStatusValues = useMemo(
    () => [
      "Draft",
      "PendingReview",
      "Recalled",
      "ComplianceDraft",
      "RecalledCompliance",
    ],
    []
  );

  // Find the current stage configuration
  const stageConfig = configuration?.stageConfigurations?.find(
    (s) => s.stageCode.toLowerCase() === stage?.toLowerCase()
  )
  
  const columns = stageConfig?.fieldConfigurations || []
  
  // V2: Check if editing is enabled for current stage
  const isEditingEnabled = stageConfig?.isInlineEditsEnabled ?? false;

  // Per-stage show-completed state (false = hide completed, true = show all)
  const showCompleted = (showCompletedByStage ?? {})[stageConfig?.stageId ?? ''] ?? false;
  
  // Check if Message column exists in the fieldConfigurations
  const messageColumn = columns.find(col => col.columnCode === 'Message');
  
  // V2: Create Actions column dynamically
  const actionsColumn: any = {
    fieldConfigurationId: 'actions-column',
    columnCode: 'Actions',
    columnTypeCode: 'Action',
    columnNameEnglish: 'Actions',
    columnNameFrench: 'Actions',
    color: '',
    isEditable: false,
    customValuesSource: '',
    customValuesType: '',
    customValues: [],
    order: 0,
    isStaticField: true,
    isSortable: false,
    isFacetable: false,
    isFilterable: false,
    isSearchable: false,
    isActionColumn: true,
    actionType: 'menu',
    isStaticPosition: true
  };
  
  if (messageColumn && !isMessageColumnHidden) {
    (messageColumn as any).isActionColumn = true;
    (messageColumn as any).actionType = 'message';
    (messageColumn as any).isStaticPosition = true;
    messageColumn.isSortable = false;
    messageColumn.isFacetable = false;
    messageColumn.isFilterable = false;
    messageColumn.isSearchable = false;
    messageColumn.isEditable = false;
  }
  
  // The Actions (kebab) column is configurable: a tenant can hide it by not
  // granting EnableActionsColumn (the default seed grants it to all internal users, mirroring
  // EnableCheckboxColumn). Computed here because displayColumns (built just below) needs it,
  // and the component-level `authorizedActions` const is declared later in render.
  const showActionsColumn =
    (configuration?.authorizedActions || [])
          .map((a: any) => (typeof a === 'string' ? a : a.actionCode))
          .includes('EnableActionsColumn');

  // V2: Arrange columns with Actions first, then Message, then all others (after checkbox)
  let displayColumns: any[] = [];
  if (isStageTransitioning && previousColumns.length > 0) {
    const regularColumns = previousColumns.filter(col => col.columnCode !== 'Message' && col.columnCode !== 'Actions');
    
    const fixedColumns = [];
    // Actions (kebab) column — internal-only + configurable (EnableActionsColumn). When hidden,
    // omit the whole column (header + cells + sticky width) rather than render an empty column.
    if (showActionsColumn) fixedColumns.push(actionsColumn); // Actions first
    if (messageColumn && !isMessageColumnHidden) {
      fixedColumns.push(messageColumn); // Message second (if enabled)
    }
    displayColumns = [...fixedColumns, ...regularColumns];
  } else {
    const regularColumns = columns.filter(col => col.columnCode !== 'Message' && col.columnCode !== 'Actions');
    
    const fixedColumns = [];
    // Actions (kebab) column — internal-only + configurable (EnableActionsColumn). When hidden,
    // omit the whole column (header + cells + sticky width) rather than render an empty column.
    if (showActionsColumn) fixedColumns.push(actionsColumn); // Actions first
    if (messageColumn && !isMessageColumnHidden) {
      fixedColumns.push(messageColumn); // Message second (if enabled)
    }
    displayColumns = [...fixedColumns, ...regularColumns];
  }
  const editableFields = (configuration?.fieldsInUse || []).filter(col => col.isEditable);

  // Jump-to-column command palette: Cmd/Ctrl+K opens, type + Enter scrolls to
  // the picked column. Visible columns scroll immediately. Hidden columns get
  // appended to the user's view first (via useAddColumnToView), then scrolled
  // to once they appear in displayColumns.
  const jumpToColumn = useJumpToColumn();
  const addColumnToView = useAddColumnToView();
  const pendingScrollTargetRef = useRef<string | null>(null);

  const visibleColumnIdSet = new Set(displayColumns.map((c: any) => c.fieldConfigurationId));
  const visibleJumpOptions: JumpToColumnOption[] = displayColumns
    .filter(col => col.columnCode !== 'Actions' && col.columnCode !== 'Message')
    .map(col => ({
      fieldConfigurationId: col.fieldConfigurationId,
      displayName: getLocalizedField(col, 'columnName', language),
      isHidden: false,
    }));
  const hiddenJumpOptions: JumpToColumnOption[] = (configuration?.fieldsInUse || [])
    .filter(f => !visibleColumnIdSet.has(f.fieldConfigurationId))
    .filter(f => f.columnCode !== 'Actions' && f.columnCode !== 'Message')
    .map(f => ({
      fieldConfigurationId: f.fieldConfigurationId,
      displayName: getLocalizedField(f, 'columnName', language),
      isHidden: true,
    }));
  const jumpToColumnOptions: JumpToColumnOption[] = [...visibleJumpOptions, ...hiddenJumpOptions];

  const scrollToColumn = (column: JumpToColumnOption) => {
    const container = tableContainerRef.current;
    if (!container) return;
    // fieldConfigurationId is a GUID (alphanumeric + hyphens), safe in a selector
    // without escaping. NOTE: the global CSS object is shadowed by dnd-kit's CSS
    // import in this file, so we can't use CSS.escape here anyway.
    const header = container.querySelector<HTMLElement>(
      `[data-field-configuration-id="${column.fieldConfigurationId}"]`
    );
    if (!header) return;
    header.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });

    // Highlight the entire column (header + every body cell at the same index)
    // via the `data-flash` attribute. CSS animation is defined in globals.css.
    // Perf: ~50 rows × 1 attr set is trivial; native grid with no virtualization,
    // so only DOM-mounted rows are touched.
    const headerRow = header.parentElement;
    const columnIndex = headerRow ? Array.prototype.indexOf.call(headerRow.children, header) : -1;
    const flashed: HTMLElement[] = [header];
    if (columnIndex >= 0) {
      const table = container.querySelector('table');
      const rows = table?.querySelectorAll<HTMLTableRowElement>('tbody > tr') ?? [];
      rows.forEach(row => {
        const cell = row.children[columnIndex] as HTMLElement | undefined;
        if (cell) flashed.push(cell);
      });
    }
    flashed.forEach(el => el.setAttribute('data-flash', 'true'));
    window.setTimeout(() => flashed.forEach(el => el.removeAttribute('data-flash')), 2500);
  };

  const handleJumpToColumnPick = (column: JumpToColumnOption) => {
    if (!column.isHidden) {
      scrollToColumn(column);
      return;
    }
    // Hidden column → append to current stage view first, then scroll once it
    // appears in displayColumns (effect below watches for that).
    pendingScrollTargetRef.current = column.fieldConfigurationId;
    addColumnToView(column.fieldConfigurationId).then(success => {
      if (!success) pendingScrollTargetRef.current = null;
    });
  };

  // Watches displayColumns for a freshly-added hidden column. Once it shows up,
  // we schedule a scroll on the next frame so the new <th> is in the DOM.
  const displayColumnsKey = displayColumns.map(c => c.fieldConfigurationId).join(',');
  useEffect(() => {
    const target = pendingScrollTargetRef.current;
    if (!target) return;
    const isPresent = displayColumns.some((c: any) => c.fieldConfigurationId === target);
    if (!isPresent) return;
    pendingScrollTargetRef.current = null;
    // Wait for React to commit + browser to paint before reading DOM coords.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const targetCol = displayColumns.find((c: any) => c.fieldConfigurationId === target);
        scrollToColumn({
          fieldConfigurationId: target,
          displayName: targetCol ? getLocalizedField(targetCol, 'columnName', language) : '',
          isHidden: false,
        });
      });
    });
    // displayColumnsKey is the dep that signals the column list changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayColumnsKey]);

  // Initialize browser state and column widths
  useEffect(() => {
    setIsBrowser(true)
    
    // Load saved column widths from view state or use defaults
    const savedWidths = state.columnWidths || {};
    
    const initialWidths: Record<string, number> = {}
    
    // V2: Set checkbox column to CHECKBOX_COLUMN_WIDTH to accommodate chevron + checkbox
    initialWidths['checkbox'] = CHECKBOX_COLUMN_WIDTH;
    
    // V2: Set default width for action columns (actions and message)
    // Deliberately NOT `savedWidths[...] || …`: these two are fixed, so a width persisted before they
    // were pinned (or by a view saved elsewhere) must not win.
    initialWidths['actions-column'] = FIXED_ACTION_COLUMN_WIDTH;

    if (messageColumn && !isMessageColumnHidden) {
      initialWidths[messageColumn.fieldConfigurationId] = MESSAGE_COLUMN_WIDTH;
    }
    
    // Use all fields from fieldsInUse instead of just current stage columns
    const allFields = configuration?.fieldsInUse || [];
    
    allFields.forEach((field) => {
      // Set default width based on column type or content
      let defaultWidth = 190;
      if (field.columnCode === 'AssignedToUser' || field.columnCode?.includes('Email') || field.columnCode?.includes('Name')) {
        defaultWidth = 230;
      } else if (field.columnTypeCode?.toLowerCase() === 'boolean') {
        defaultWidth = 150;
      } else if (field.columnTypeCode?.toLowerCase() === 'date') {
        defaultWidth = 170;
      } else if (field.columnTypeCode?.toLowerCase() === 'number') {
        defaultWidth = 130;
      }
      
      initialWidths[field.fieldConfigurationId] = savedWidths[field.fieldConfigurationId] || defaultWidth
    })
    
    setColumnWidths(initialWidths)
  }, [configuration?.fieldsInUse, state.columnWidths, isMessageColumnHidden, messageColumn])

  // Resizable column handlers
  const handleResizeStart = (e: React.MouseEvent, columnId: string) => {
    e.preventDefault();
    e.stopPropagation();

    // Clean up any stuck resize from a previous interaction where mouseup fired outside the
    // iframe — in that case the iframe's document never sees the mouseup, so handleResizeEnd
    // is never called and the old listeners accumulate.
    if (pendingResizeHandlersRef.current) {
      document.removeEventListener('mousemove', pendingResizeHandlersRef.current.move);
      document.removeEventListener('mouseup', pendingResizeHandlersRef.current.up);
      pendingResizeHandlersRef.current = null;
    }
    
    const currentWidth = columnWidths[columnId] || 160;

    // Snapshot values that handleResizeMove needs into refs so that the stale closure
    // created before setIsResizing() re-renders the component still uses correct data.
    resizeInitialWidthsRef.current = { ...columnWidths };
    resizeDisplayColumnsSnapshotRef.current = displayColumns;
    // The leading checkbox/chevron column is rendered only when !hideCheckboxColumn (now hidden
    // for Requests-group rows when checkboxes aren't enabled). The DOM th/td index offset and the
    // total column count must match that same render predicate — otherwise a drag targets the
    // neighbouring column. Use 0 when there's no leading column, 1 when there is.
    const leadingColumnCount = hideCheckboxColumn ? 0 : 1;
    resizeColumnIndexRef.current = columnId === 'checkbox'
      ? 0
      : displayColumns.findIndex(col => col.fieldConfigurationId === columnId) + leadingColumnCount;
    resizeTotalColumnsRef.current = displayColumns.length + leadingColumnCount;
    
    isResizingRef.current = true;
    resizeStartXRef.current = e.clientX;
    resizeStartWidthRef.current = currentWidth;
    resizingColumnRef.current = columnId;
    
    setIsResizing(true);
    setResizingColumn(columnId);
    
    // Track the exact references so handleResizeStart can remove them on the next call
    // if handleResizeEnd was never invoked (e.g. mouse released outside the iframe).
    pendingResizeHandlersRef.current = { move: handleResizeMove, up: handleResizeEnd };
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  };

  const handleResizeMove = (e: MouseEvent) => {
    if (!isResizingRef.current || !resizingColumnRef.current) return;
    
    const deltaX = e.clientX - resizeStartXRef.current;
    const newWidth = Math.max(50, resizeStartWidthRef.current + deltaX); // Minimum width of 50px
    
    // Directly manipulate DOM for immediate visual feedback
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      const table = tableContainer.querySelector('table');
      if (table) {
        // Use ref snapshots instead of closure values — the closure captured at mousedown
        // time becomes stale after setIsResizing() triggers a re-render, which would cause
        // wrong column indices if displayColumns.length changed or columnWidths drifted.
        const snapshotColumns = resizeDisplayColumnsSnapshotRef.current;
        const snapshotWidths = resizeInitialWidthsRef.current;

        const currentDataColumnsWidth = snapshotColumns.reduce((total: number, column: any) => {
          if (column.fieldConfigurationId === resizingColumnRef.current) {
            return total + newWidth;
          }
          return total + getSafeColumnWidth(snapshotWidths, column.fieldConfigurationId);
        }, 0);
        const totalWidth = checkboxColWidthRef.current + currentDataColumnsWidth;
        table.style.width = `${totalWidth}px`;
        table.style.minWidth = `${totalWidth}px`;
        
        const headerCells = table.querySelectorAll('th');
        const bodyCells = table.querySelectorAll('td');
        
        // Use pre-computed column index from ref — avoids stale displayColumns.findIndex()
        const columnIndex = resizeColumnIndexRef.current;
        
        if (columnIndex >= 0) {
          if (headerCells[columnIndex]) {
            headerCells[columnIndex].style.width = `${newWidth}px`;
            headerCells[columnIndex].style.minWidth = `${newWidth}px`;
            headerCells[columnIndex].style.maxWidth = `${newWidth}px`;
          }
          
          // Use pre-computed total columns from ref — avoids stale displayColumns.length
          const totalColumns = resizeTotalColumnsRef.current;
          for (let i = 0; i < bodyCells.length; i++) {
            const cellIndex = i % totalColumns;
            if (cellIndex === columnIndex) {
              bodyCells[i].style.width = `${newWidth}px`;
              bodyCells[i].style.minWidth = `${newWidth}px`;
              bodyCells[i].style.maxWidth = `${newWidth}px`;
            }
          }
        }
      }
    }
  };

  const handleResizeEnd = (e?: MouseEvent) => {
    if (!isResizingRef.current || !resizingColumnRef.current) return;
    
    // Calculate final width
    const deltaX = (e?.clientX || 0) - resizeStartXRef.current;
    const finalWidth = Math.max(50, resizeStartWidthRef.current + deltaX);
    
    // Update React state to trigger re-render
    const updatedWidths = {
      ...columnWidths,
      [resizingColumnRef.current!]: finalWidth
    };
    setColumnWidths(updatedWidths);
    
    // Save column widths to view state via context, ensuring checkbox is always fixed
    const widthsToSave = {
      ...updatedWidths,
      checkbox: CHECKBOX_COLUMN_WIDTH,
    };
    setColumnWidthsFromContext(widthsToSave);
    
    // Reset both refs and state
    isResizingRef.current = false;
    resizingColumnRef.current = null;
    pendingResizeHandlersRef.current = null;
    setIsResizing(false);
    setResizingColumn(null);
    
    // Remove global event listeners
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  };

  // Cleanup resize listeners on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, []);

  // V2: Toggle expansion of assignment to show/hide child WorkOrders
  const toggleAssignmentExpansion = async (item: WorkflowItem) => {
    if (!item.id) {
      console.error('assignmentId is missing on item:', item);
      return;
    }
    
    const assignmentId: string = item.id;
    const fboAssignmentId = (item as any).fboAssignmentId;
    
    if (!fboAssignmentId) {
      console.error('fboAssignmentId is missing on item:', item);
      return;
    }
    
    if (expandedAssignments.has(assignmentId)) {
      // Collapse and clear cached data
      setExpandedAssignments(prev => {
        const next = new Set(prev);
        next.delete(assignmentId);
        return next;
      });
      setChildWorkOrders(prev => {
        const next = { ...prev };
        delete next[assignmentId];
        return next;
      });
    } else {
      // Expand and fetch fresh children (no caching)
      setLoadingChildren(prev => new Set(prev).add(assignmentId));
      
      try {
        // Build select fields same way as main Assignment search
        // Get current stage config
        const currentStageConfig = stages.find(s => s.stageCode === stage);
        if (!currentStageConfig) {
          throw new Error(`No stage configuration found for stage '${stage}'`);
        }
        
        // Get field configurations from current stage (excluding static fields)
        let selectFields = currentStageConfig.fieldConfigurations
          .filter(f => !f.isStaticField)
          .map(f => f.columnCode);
        
        // Always include fields from configuration
        const alwaysInclude = configuration?.alwaysFetchedFieldCodes || ["FboId", "ObId", "RecordType", "InformationRequestType"];
        for (const field of alwaysInclude) {
          if (!selectFields.includes(field)) {
            selectFields.push(field);
          }
        }
        const select = selectFields.length > 0 ? selectFields.join(",") : "*";
        
        // Get current FboId to exclude from child results
        const currentFboId = (item as any).fboId;
        
        // Build filter for child WorkOrders: exclude current FboId from results.
        //
        // The `DocumentType eq 'WorkOrder'` clause alone is now the correct
        // discriminator: gw-sync writes VmsRequest rows with
        // DocumentType="VmsRequest" directly (the previous WorkOrder+EntityType
        // convention has been retired). Pinning DocumentType also opts this
        // call out of the server-side AzureSearchService.UseEntityTypeFilter
        // default exclusion — fine here because the explicit
        // DocumentType='WorkOrder' filter is already narrower.
        const childFilter = currentFboId
            ? `DocumentType eq 'WorkOrder' and FboAssignmentId eq ${fboAssignmentId} and FboId ne ${currentFboId} and RequestType ne 'ChangeRequest' and RequestType ne 'DocumentRequest'`
            : `DocumentType eq 'WorkOrder' and FboAssignmentId eq ${fboAssignmentId} and RequestType ne 'ChangeRequest' and RequestType ne 'DocumentRequest'`;
        
        // Use the same searchWorkflowItems method to ensure consistent data processing
        const response = await searchWorkflowItems(
          undefined, // no stage filter
          undefined, // no custom filters
          [], // fieldConfigurations (not used when filter is provided)
          1, // page
          100, // pageSize - get all children
          '*', // search term
          select, // same select fields as main grid
          childFilter, // custom filter for WorkOrders
          'StartDate desc', // orderBy
          undefined // no facets
        );
        
        setChildWorkOrders(prev => ({ 
          ...prev, 
          [assignmentId]: response.data 
        }));
        setExpandedAssignments(prev => new Set(prev).add(assignmentId));
      } catch (error) {
        console.error('Failed to fetch child work orders:', error);
        // You can add toast notification here if needed
      } finally {
        setLoadingChildren(prev => {
          const next = new Set(prev);
          next.delete(assignmentId);
          return next;
        });
      }
    }
  };

  // Start Onboarding opens the onboarding-form microapp on a new onboarding session. AtsService maps an ATS
  // placement and creates the session in one call. A ClientRequest is mapped by gw-sync, which owns the Request
  // mirror (GET Request/{preVerificationRequestId}/map-to-onboarding), and a VmsRequest by FBO-vms, where its
  // data lives (GET /api/vms/requests/{requestId}/onboarding-source); OnboardingFormService then creates the
  // session. Returns the context the drawer renders against, or null when the session could not be created,
  // after toasting the error.
  const openOnboardingDrawer = async (item: WorkflowItem): Promise<NonNullable<typeof vmsOnboardingContext> | null> => {
    try {
      if (contextEntityType === 'AtsPlacementRequest') {
        const recordId = (item as any).requestId || item.id || '';
        const onboardingId = await atsApi.createOnboardingSessionAsync(recordId);
        if (!onboardingId) throw new Error('No onboarding session id returned');
        const atsPlacementId = (item as any).atsPlacementId ?? (item as any).AtsPlacementId;
        return {
          onboardingId,
          requestId: recordId,
          externalRequestId: null,
          sourceName: 'ATS',
          idDisplayText: `ATS-${atsPlacementId ?? recordId}`,
          rowId: (item as any).id,
          entityType: 'AtsPlacementRequest',
        };
      }

      const isClientRequest = contextEntityType === 'ClientRequest';
      const source = isClientRequest
        ? await mapRequestToOnboarding((item as any).preVerificationRequestId || '')
        : await mapVmsRequestToOnboarding(item.requestId || '');
      const result = await createOnboarding(source);
      if (isClientRequest) {
        // Restore the bespoke ClientRequest header: "From Client Portal"
        // (sourceName) + "CR-{PreVerificationId}" (idDisplayText). These are
        // rendered inside the onboarding-form iframe from the query params.
        return {
          onboardingId: result.id!,
          requestId: (item as any).preVerificationRequestId || '',
          externalRequestId: null,
          sourceName: 'Client Portal',
          idDisplayText: `CR-${(item as any).preVerificationRequestId || ''}`,
          rowId: item.id,
          entityType: 'ClientRequest',
          clientRequestStatus: typeof (item as any).status === 'string'
            ? (item as any).status as ClientRequestStatus
            : undefined,
        };
      }
      // Row data is camelCased on the wire; check the legacy
      // PascalCase shape too in case any caller path didn't go
      // through the standard search projection. VMS keeps its existing
      // labelling (panel falls back to "VMS-{externalRequestId|requestId}").
      const externalRequestId =
        ((item as any).vmsExternalRequestId
          ?? (item as any).VmsExternalRequestId
          ?? null) as string | null;
      return {
        onboardingId: result.id!,
        requestId: item.requestId || '',
        externalRequestId: externalRequestId && externalRequestId.trim().length > 0
          ? externalRequestId
          : null,
        rowId: item.id,
        entityType: 'VmsRequest',
      };
    } catch (err) {
      toast({
        variant: 'destructive',
        title: t('error'),
        description: err instanceof Error ? err.message : (t('actionFailed') || 'Failed to start onboarding'),
      });
      return null;
    }
  };

  // V2: Handle action clicks from the Actions column
  const handleActionClick = (action: any, item: any) => {
    executeAction(action, item, {
      onEmailPreview: (emailId, itemTitle) => {
        // ViewEmail (VmsRequest) and Preview (VmsEmail) both land here. The
        // email-id resolution (linkedEmailId vs item.id) lives inside
        // executeAction so the grid stays unaware of entity-specific plumbing.
        setEmailPreviewPanel({ isOpen: true, emailId, title: itemTitle });
      },
      onOpenLinkedRequest: (requestId, itemTitle) => {
        // VmsEmail → linked VmsRequest cross-tab navigation. The grid itself
        // has no notion of sibling sub-tabs; the host (vms/tab.tsx) does.
        // Bubble up if a handler was passed, otherwise warn — the action's
        // Condition already gates on LinkedVmsRequestId being non-null so a
        // missing handler is a wiring bug, not a data issue.
        if (onOpenLinkedRequest) {
          onOpenLinkedRequest(requestId, itemTitle);
        } else {
          console.warn('open_linked_request fired but no host handler is wired', { requestId, itemTitle });
        }
      },
      onViewDuplicates: (currentRowId, duplicateIds, itemTitle) => {
        // VmsRequest "View Duplicates" — scope the grid to the current row
        // PLUS every id in PossibleDuplicateOfRequestIds. Same-tab filter,
        // so we drive it from the grid directly via applyTemporaryQueryFilter
        // (no host bubble needed — unlike open_linked_request which has to
        // switch sub-tabs).
        //
        // OData id literals: stringly-typed in the index, single-quote
        // escape by doubling per OData rules. Composing the filter as
        // `search.in(id, '...', ',')` matches the existing pattern in
        // the index for set membership; the value list is comma-joined
        // and the separator-character argument tells Azure Search how
        // to split it. Self-included so the cluster reads naturally
        // (source row + siblings).
        const allIds = [currentRowId, ...duplicateIds]
          .map(s => s.replace(/'/g, "''"))
          .filter(s => s.length > 0);
        if (allIds.length === 0) {
          // Defensive — Condition gating + executeAction filter should
          // have already prevented this branch.
          return;
        }
        const filter = `search.in(id, '${allIds.join(',')}', ',')`;
        // Label users see in the chip — "duplicates of <title>" reads
        // naturally and survives translation since both halves are
        // independent strings.
        applyTemporaryQueryFilter(
          filter,
          `Duplicates of ${itemTitle} (${allIds.length})`
        );
      },
      onHistory: (itemId, itemTitle) => {
        // Pass the row's documentType verbatim so the audit endpoint can
        // dispatch correctly across every entity (WorkOrder / VmsRequest /
        // VmsEmail / future). Assignment is the historical default — leaving
        // entityType undefined falls back to that path on the backend.
        const documentType = (item.documentType || item.DocumentType) as string | undefined;
        const entityType = documentType && documentType !== 'Assignment' ? documentType : undefined;
        setAuditHistoryPanel({ isOpen: true, itemId, itemTitle, entityType });
      },
      onExternalLink: (url) => {
        WindowRefService.openUrl(url);
      },
      onStageTransition: (action, item) => {
        // Show confirmation modal for stage transitions that require confirmation or notes
        if (action.requiresConfirmation || action.allowNotes) {
          setActionConfirmationModal({
            isOpen: true,
            action,
            items: [item]
          });
        } else {
          // Execute immediately without confirmation
          handlePerformStageTransition(action.actionCode, [item]);
        }
      },
      onCustomModal: (action, item) => {
        // ── ATS lifecycle actions ───────────────────────────────────────────
        // AtsPlacementRequest rows route to AtsService endpoints, which are
        // distinct from the VMS/ClientRequest flows below. Isolated here with an
        // early return so the existing branches are untouched. The id sent is the
        // canonical ATS doc id = the row's RequestId (the index `id` is the SHA).
        if (contextEntityType === 'AtsPlacementRequest') {
          const recordId = (item as any).requestId || item.id || '';
          const code = action.actionCode;

          // Hold / Cancel — reason captured via the shared confirmation modal
          // (AllowNotes=true on the action config) and forwarded as Comments,
          // which AtsService stores as StatusReason.
          if (code === 'Hold' || code === 'Cancel') {
            const onConfirm = async (_ac: string, _items: any[], notes?: string) => {
              try {
                if (code === 'Hold') await atsApi.holdAsync(recordId, notes ?? '');
                else await atsApi.cancelAsync(recordId, notes ?? '');
                // Flip the row's status in place once AtsService confirms, so the change
                // shows instantly; the pendingOptimistic overlay holds it until the refresh
                // below confirms (fetched status === target) or the safety timeout elapses.
                // Flipped only after the await succeeds, so a failed call (caught below) never
                // leaves a stale optimistic value. ATS status values mirror ATS_STATUS_BY_ACTION.
                // Canonical 'Canceled' — the overlay only clears when the fetched value deep-equals
                // this expected one, and the server now returns the canonical spelling, so the legacy
                // 'Cancelled' left every ATS Cancel pinned for the full OPTIMISTIC_TIMEOUT_MS.
                applyOptimisticUpdates([{ id: item.id, updates: { status: code === 'Hold' ? 'OnHold' : 'Canceled' } }], { silent: true });
                toast({
                  variant: 'success',
                  title: t('success') || 'Success',
                  description: t('actionSuccess')?.replace('{action}', action.titleEnglish || code)
                    || `${action.titleEnglish || code} complete.`,
                });
                triggerDataFetch({ force: true, silent: true });
              } catch (err) {
                const apiErr = err as { detail?: string; message?: string };
                toast({
                  variant: 'destructive',
                  title: t('error') || 'Error',
                  description: apiErr?.detail || (err instanceof Error ? err.message : undefined)
                    || (t('actionFailed') || `Failed to ${code}`),
                });
                throw err; // keep modal open for retry
              }
            };
            setActionConfirmationModal({ isOpen: true, action, items: [item], onConfirm });
            return;
          }

          // Start / Resume Pre-verification — navigate to an existing gw-sync
          // request if one's already stamped, otherwise orchestrate a new one.
          if (code === 'StartPreVerification') {
            (async () => {
              try {
                const existing = (item as any).preVerificationRequestId;
                const reqId = existing || await atsApi.startVerificationAsync(recordId);
                if (reqId) {
                  triggerDataFetch({ force: true, silent: true });
                  sendMessageToParent({ action: 'NAVIGATE', route: `/next/mfe/sync/requests/onboarding/${reqId}` });
                }
              } catch (err) {
                toast({
                  variant: 'destructive',
                  title: t('error'),
                  description: err instanceof Error ? err.message : (t('actionFailed') || 'Failed to start pre-verification'),
                });
              }
            })();
            return;
          }

          // Resync — re-pull Phoenix snapshots onto the canonical doc.
          if (code === 'Resync') {
            (async () => {
              try {
                await atsApi.resyncAsync(recordId);
                toast({
                  variant: 'success',
                  title: t('success') || 'Success',
                  description: t('actionSuccess')?.replace('{action}', action.titleEnglish || code) || 'Resynced.',
                });
                triggerDataFetch({ force: true, silent: true });
              } catch (err) {
                toast({
                  variant: 'destructive',
                  title: t('error'),
                  description: err instanceof Error ? err.message : (t('actionFailed') || 'Failed to resync'),
                });
              }
            })();
            return;
          }

          // Start Onboarding (== Accept for ATS) — AtsService creates the onboarding
          // session in one call (maps the placement + creates the session) and returns
          // the onboarding id; we open the shared onboarding-form panel. There is no
          // separate manual "Accept": the onboarding service accepts the placement on
          // the server when the form is submitted.
          if (code === 'StartOnboarding' || code === 'Accept') {
            (async () => {
              const context = await openOnboardingDrawer(item);
              if (context) setVmsOnboardingContext(context);
            })();
            return;
          }

          return;
        }

        // Handle custom modal actions (like WillExtend)
        if (action.actionCode === 'WillExtend') {
          setExtendModalItem(item);
          setIsExtendModalOpen(true);
        } else if (action.actionCode === 'RequestExtension') {
          setClientRequestModalItem(item);
          setClientRequestModalType('Extension');
          setIsClientRequestModalOpen(true);
        } else if (action.actionCode === 'RequestTermination') {
          setClientRequestModalItem(item);
          setClientRequestModalType('Termination');
          setIsClientRequestModalOpen(true);
        } else if (action.actionCode === 'RequestRedeployment') {
          setClientRequestModalItem(item);
          setClientRequestModalType('Redeployment');
          setIsClientRequestModalOpen(true);
        } else if (action.actionCode === 'RequestOtherChanges') {
          setClientRequestModalItem(item);
          setClientRequestModalType('General');
          setIsClientRequestModalOpen(true);
        } else if (action.actionCode === 'ViewClientRequests') {
          setRelatedRequestsPanelItem(item);
        } else if (action.actionCode === 'ViewDetails') {
          // Open the read-only ClientRequest detail panel (info + documents). The detail
          // endpoint keys on the ClientRequest doc id = the row's RequestId (non-Onboarding).
          const rid = ((item as any).requestId ?? (item as any).RequestId ?? null) as string | null;
          setDetailPanelRequestId(rid);
        } else if (action.actionCode === 'StartPreVerification') {
          if (contextEntityType === 'ClientRequest') {
            // ClientRequest originates from the client portal and already carries
            // its portal onboarding id in preVerificationRequestId — there is
            // nothing to eager-create (and no ClientRequest start-pre-verification
            // endpoint exists). Navigate straight to the existing pre-verification
            // request. Calling the VMS startPreVerification endpoint here (with the
            // CR requestId) was the bug — wrong id AND wrong, VMS-only endpoint.
            const preVerId = (item as any).preVerificationRequestId;
            if (!preVerId) {
              toast({
                variant: 'destructive',
                title: t('error'),
                description: t('actionFailed') || 'This request has no pre-verification id yet.',
              });
            } else {
              sendMessageToParent({
                action: 'NAVIGATE',
                route: `/next/mfe/sync/requests/onboarding/${preVerId}`,
              });
            }
          } else {
            // VMS Start Pre-verification — eager-create flow. Our backend
            // orchestrates the gw-sync Draft Request creation and returns the
            // new Request id; we then hand a NAVIGATE to the parent window
            // pointing at the existing-Request route on the sync MFE. The form
            // loads the Draft with no VMS-specific branch on the form side.
            //
            // Backend refuses (400) if PreVerificationRequestId is already set
            // — the kebab gate (Condition on the action config) should already
            // be hiding Start in that case, but the backend guard backstops
            // races / replay scenarios.
            (async () => {
              try {
                // Use requestId, not the search-index id. The search-index id is
                // a SHA256-derived guid (SearchIndexId.Generate) and is NOT the
                // source VmsRequest Cosmos doc id — the backend's
                // start-pre-verification endpoint addresses the source doc and
                // 404s against the index id. requestId carries the source doc
                // id verbatim (VmsRequestProjections.ToSearchDto: RequestId =
                // doc.id) so it's always the right key for downstream actions.
                const result = await startPreVerification(item.requestId || item.id || '');
                const newId = result?.preVerificationRequestId;
                if (!newId) {
                  throw new Error('Backend returned no preVerificationRequestId');
                }
                // Optimistically refresh so the row picks up the new
                // PreVerificationRequestId from the index (which flips the
                // kebab over to Resume). The navigate fires either way.
                triggerDataFetch({ force: true, silent: true });
                sendMessageToParent({
                  action: 'NAVIGATE',
                  route: `/next/mfe/sync/requests/onboarding/${newId}`,
                });
              } catch (err) {
                toast({
                  variant: 'destructive',
                  title: t('error'),
                  description: err instanceof Error
                    ? err.message
                    : (t('actionFailed') || 'Failed to start pre-verification'),
                });
              }
            })();
          }
        } else if (
          action.actionCode === 'Reprocess'
          || action.actionCode === 'MarkAsSpam'
          || action.actionCode === 'MarkAsValid'
          || action.actionCode === 'MoveFromSpamAndProcess'
          || action.actionCode === 'CreateRequest'
          || action.actionCode === 'Delete'
        ) {
          // VMS request + email kebab actions that mutate data and want a
          // confirmation prompt before firing. All five share the same
          // ActionConfirmationModal — we just inject a per-action onConfirm
          // that calls the right API hook, toasts, and refreshes the grid.
          //
          // Two reasons we route ALL of these through the modal even when
          // the action config didn't set `requiresConfirmation`:
          //   1. Each mutates extracted data or lifecycle state on the row;
          //      a misclick can re-run LLM extraction or hide a row from
          //      the active queue, both of which are annoying to undo.
          //   2. Modal already centralizes the "show spinner + disable
          //      while submitting" UX, so getting the action through this
          //      path keeps the keyboard / focus behavior consistent with
          //      every other confirmed action.
          //
          // The Reprocess action exists on BOTH VmsRequest (id is the
          // request, retryParse re-runs against the linked email) and
          // VmsEmail (id is the email itself, reprocessEmail re-runs
          // against this email). We pick the API by row documentType to
          // avoid relying on action code alone.
          const docType = (
            (item as any).documentType
              || (item as any).DocumentType
              || ''
          ).toString().toLowerCase();

          const apiCall = async (): Promise<{ requestId?: string } | void> => {
            // VmsEmail kebab actions (MarkAsSpam, MarkAsValid, Delete,
            // MoveFromSpamAndProcess, and Reprocess on email rows) all key
            // off the email doc id. VmsEmail rows are indexed in
            // gw-vms-email-index where id = source doc id (no SHA256 hash,
            // see VmsEmailProjections.ToSearchDto), so item.id is the right
            // value to send.
            //
            // Reprocess on a VmsRequest row hits retryParse, which addresses
            // the source VmsRequest Cosmos doc — same SHA256-vs-source-id
            // trap as Start Pre-verification. Use requestId there.
            //
            // The fallback to item.id is defensive in case a row in the index
            // somehow has no requestId populated (transition rows, indexer
            // gaps). It would still pass the SHA256 guid which 404s backend-
            // side; the warning log makes that failure visible to console
            // rather than silently dying as "Reprocess failed" toast.
            const emailId = item.id || '';
            const vmsRequestId = (item as any).requestId || item.id || '';
            if (docType === 'vmsrequest' && !(item as any).requestId) {
              console.warn(
                'Reprocess on VmsRequest row falling back to item.id — row has no requestId. Backend may 404. Row:',
                item,
              );
            }
            if (action.actionCode === 'Reprocess') {
              if (docType === 'vmsemail') {
                return await reprocessEmail(emailId) as { requestId?: string };
              }
              return await retryParse(vmsRequestId) as void;
            }
            if (action.actionCode === 'MarkAsSpam') return await markEmailAsSpam(emailId) as void;
            if (action.actionCode === 'MarkAsValid') return await markEmailAsValid(emailId) as void;
            if (action.actionCode === 'Delete') {
              // Single-row soft delete. Backend refuses if the email is linked
              // to a VmsRequest (returns 400 with the reason); the catch below
              // surfaces that as a destructive toast so the operator knows why.
              return await deleteEmail(emailId) as void;
            }
            // CreateRequest — manual override for emails parked at PendingManualCreate
            // (public-domain sender). Distinct from Reprocess: Reprocess re-runs the LLM
            // against an already-linked request; CreateRequest produces a fresh request
            // from the email's existing parse. Surfaces the new requestId on success the
            // same way MoveFromSpamAndProcess does so the caller can route to the new row.
            if (action.actionCode === 'CreateRequest') {
              return await createRequestFromEmail(emailId) as { requestId?: string };
            }
            // MoveFromSpamAndProcess
            return await moveEmailFromSpamAndProcess(emailId) as { requestId?: string };
          };

          // Modal forwards (actionCode, items, notes). We ignore the notes
          // arg today — none of these six actions surface a notes field
          // (allowNotes=false in their configs) — but the parameter is in
          // the prop contract.
          const onConfirm = async (_actionCode: string, _items: any[], _notes?: string) => {
            try {
              await apiCall();
              // Optimistic effects — only where the post-API state is knowable
              // client-side. Spam is a flag (IsSpam), not a status: MarkAsSpam /
              // MarkAsValid flip it directly. Delete is a soft-delete — the row
              // leaves every view, so remove it. The Reprocess family runs the
              // parse pipeline to a final state we can't predict here (Processed /
              // Error / PendingManualCreate), so those rows update via the silent
              // refetch below instead of a guessed flip.
              const optimisticRowId = item.id;
              if (optimisticRowId) {
                if (action.actionCode === 'MarkAsSpam') {
                  applyOptimisticUpdates([{ id: optimisticRowId, updates: { isSpam: true } }], { silent: true });
                } else if (action.actionCode === 'MarkAsValid') {
                  applyOptimisticUpdates([{ id: optimisticRowId, updates: { isSpam: false } }], { silent: true });
                } else if (action.actionCode === 'Delete') {
                  removeWorkOrder(optimisticRowId);
                }
              }
              // "Processing → Success" two-phase toast — mirrors the bulk
              // stage-transition flow (handlePerformStageTransition above).
              // Reasoning: every action in this branch (Reprocess,
              // MarkAsSpam/Valid, MoveFromSpamAndProcess, CreateRequest,
              // Delete) writes to Cosmos AND requires Azure Search index
              // propagation before the grid reflects the new state. Index
              // lag is typically 1–3s; a single "Success" toast that fires
              // before the row visibly updates is confusing ("did it
              // work?"). Show "Processing…" right after the API returns,
              // then mutate the same toast into the success message once
              // the refresh fires so the operator sees one continuous
              // notification rather than two popups racing the grid.
              const successDescription = action.actionCode === 'Reprocess'
                ? (t('reprocessTriggered') || 'Reprocess started — refreshing in a moment.')
                : (t('actionSuccess')?.replace('{action}', action.titleEnglish || action.actionCode)
                   || `${action.titleEnglish || action.actionCode} complete.`);

              const { id: processingToastId, update: updateProcessingToast, dismiss: dismissProcessingToast } = toast({
                variant: 'default',
                title: t('processing') || 'Processing...',
                description: t('processingActionSingle') || 'Applying your changes, this may take a moment...',
              });

              // Cancel any pending refresh timers from previous actions so
              // a rapid-fire kebab click doesn't pile up duplicate refreshes.
              refreshTimersRef.current.forEach(clearTimeout)
              refreshTimersRef.current = [
                // First refresh at ~1.5s — same cadence as before, plus the
                // toast transitions to the success state in-place.
                setTimeout(() => {
                  triggerDataFetchRef.current({ force: true, silent: true });
                  updateProcessingToast({
                    id: processingToastId,
                    variant: 'success',
                    title: t('success') || 'Success',
                    description: successDescription,
                  });
                }, 1500),
                // Auto-dismiss at 12s so the success toast doesn't linger.
                setTimeout(() => { dismissProcessingToast(); }, 12000),
                // Safety net for slow index updates — re-fetch once more
                // in case the first refresh raced index propagation. Matches
                // the stage-transition flow's 20s safety net. Via the ref, so
                // it refreshes whatever view the user is on by then.
                setTimeout(() => { triggerDataFetchRef.current({ force: true, silent: true }); }, 20000),
              ]
            } catch (err) {
              // Surface the BE-provided reason rather than the generic
              // "API request failed: POST ... - 409" wrapper text. ApiError
              // exposes `.detail` — the response-body `error` / `message`
              // pre-extracted — so the toast shows e.g. "The linked request
              // was canceled. Use Move from Spam and Process if you intended
              // to revive it." instead of a meaningless URL + status code.
              // Falls back to `err.message` for non-ApiError exceptions and
              // to the i18n string for the unknown-error case.
              const apiErr = err as { detail?: string; message?: string };
              const description = apiErr?.detail
                || (err instanceof Error ? err.message : undefined)
                || (t('actionFailed') || `Failed to ${action.actionCode}`);
              toast({
                variant: 'destructive',
                title: t('error') || 'Error',
                description,
              });
              throw err;     // Modal stays open on failure so the operator can retry.
            }
          };

          setActionConfirmationModal({
            isOpen: true,
            action,
            items: [item],
            onConfirm,
          });
        } else if (action.actionCode === 'StartOnboarding') {
          // ClientRequest / VmsRequest: openOnboardingDrawer maps the source and has
          // OnboardingFormService create the draft session, then we open the iframe
          // drawer. No status change on click — lifecycle lives on the
          // OnboardingSession (matches the Pre-verification design).
          (async () => {
            const context = await openOnboardingDrawer(item);
            if (context) setVmsOnboardingContext(context);
          })();
        }
      }
    });
  };

  // V2: Perform stage transition
  const handlePerformStageTransition = async (
    actionCode: string,
    items: any[],
    notes?: string
  ) => {
    try {
      const result = await performStageTransition(actionCode, items, notes);

      // Prepare toast messages
      let toastVariant: 'success' | 'destructive' | 'default' = 'success';
      let toastTitle = t('success');
      let toastDescription = t('itemsUpdatedSuccess')
        ?.replace('{count}', String(result.successCount)) || 
        `${result.successCount} item(s) updated successfully`;

      if (result.failedCount === 0) {
        toastVariant = 'success';
        toastTitle = t('success');
        toastDescription = t('itemsUpdatedSuccess')
          ?.replace('{count}', String(result.successCount)) || 
          `${result.successCount} item(s) updated successfully`;
      } else if (result.successCount === 0) {
        toastVariant = 'destructive';
        toastTitle = t('error');
        const errorMessages = result.results
          .filter((r: any) => !r.success)
          .map((r: any) => r.errorMessage)
          .join(', ');
        toastDescription = errorMessages || t('failedToUpdateItems') || 'Failed to update items';
      } else {
        toastVariant = 'default';
        toastTitle = t('partialSuccess') || 'Partial Success';
        const messages = result.results
          .filter((r: any) => !r.success)
          .map((r: any) => r.errorMessage)
          .filter(Boolean);
        const errorDetails = messages.length > 0 
          ? `:\n${messages.map((msg: string, index: number) => `${index + 1}. ${msg}`).join('\n')}` 
          : '';
        toastDescription = t('moveToPartial')
          ?.replace('{success}', String(result.successCount))
          .replace('{failed}', String(result.failedCount))
          .replace('{details}', errorDetails) || 
          `${result.successCount} succeeded, ${result.failedCount} failed${errorDetails}`;
      }

      // Only continue if there were any successful updates
      if (result.successCount > 0) {
        // Build a map from the ID the API received (which may be currentWorkflowHubReportId
        // for Assignment documents) back to the item's own local state id, so we can
        // correctly remove the right rows regardless of document type.
        const sentIdToLocalId = buildSentIdToLocalIdMap(items);

        const succeededIds = result.results
          .filter((r: any) => r.success)
          .map((r: any) => r.itemId);

        const idsToRemove = succeededIds.length > 0
          ? succeededIds.map((id: string) => sentIdToLocalId.get(String(id)) ?? id).filter(Boolean)
          : items.map(item => item.id);
        // WorkflowHub's grid is scoped to a single stage at a time, so a stage
        // transition genuinely takes the row out of the current view — the
        // optimistic remove keeps the UI in sync until the search index
        // catches up. VMS doesn't filter by stage; rows stay visible across
        // status changes (Show Completed toggle controls terminal-state
        // visibility separately). Optimistically deleting a VMS row when the
        // user Acknowledges it would just make the row vanish for a few
        // seconds until the refresh re-fetches it, which is worse than no
        // animation at all. Skip the optimistic remove for VMS — instead flip
        // the row's status in place so the change is visible immediately.
        if (!isRequestEntityType) {
          removeWorkOrdersBulk(idsToRemove);
        } else {
          // Requests entities (VmsRequest/ClientRequest/…) are status-based and stay
          // in the grid. Flip the succeeded rows' `status` to the action's TargetStage
          // (== the target status the backend persists) optimistically, so the new
          // status shows instantly. The pendingOptimistic overlay holds the value until
          // the refresh below confirms it (fetched status === target) or the 20s safety
          // timeout elapses. Only succeeded ids are flipped, so failed items keep their
          // prior status — no revert needed in this success branch.
          const targetStatus = (configuration?.actionConfigurations || [])
            .find((a: any) => a.actionCode === actionCode)?.targetStage;
          if (targetStatus) {
            const optimisticUpdates = idsToRemove
              .filter(Boolean)
              .map((id: string) => ({ id: String(id), updates: { status: targetStatus } }));
            if (optimisticUpdates.length > 0) applyOptimisticUpdates(optimisticUpdates, { silent: true });
          }
        }

        setActionConfirmationModal(null);
        setSelectedItems([]);

        // Show "processing" toast immediately — item(s) are gone from the list but the
        // backend pipeline (DB write → search index propagation) is still running.
        // We update the same toast in-place once the first refresh fires so the user
        // sees a smooth single-notification flow rather than two separate popups.
        const processingDescription =
          result.successCount === 1
            ? (t('processingActionSingle') || 'Applying your changes, this may take a moment...')
            : (t('processingActionMultiple')?.replace('{count}', String(result.successCount)) ||
               `Applying changes to ${result.successCount} items, this may take a moment...`);

        const { id: processingToastId, update: updateProcessingToast, dismiss: dismissProcessingToast } = toast({
          variant: 'default',
          title: t('processing') || 'Processing...',
          description: processingDescription,
        });

        // Cancel any pending refresh timers from previous actions
        refreshTimersRef.current.forEach(clearTimeout)
        refreshTimersRef.current = [
          // First shot at ~5s: refresh data then transition the toast to the final result
          setTimeout(() => {
            triggerDataFetchRef.current({ force: true, silent: true });
            refreshStageCounts();
            updateProcessingToast({
              id: processingToastId,
              variant: toastVariant,
              title: toastTitle,
              description: toastDescription,
            });
          }, 5000),
          // Auto-dismiss at 12s so the toast doesn't linger
          setTimeout(() => { dismissProcessingToast(); }, 12000),
          // Safety net for slow index updates — re-fetch rows and stage counts
          setTimeout(() => { triggerDataFetchRef.current({ force: true, silent: true }); refreshStageCounts(); }, 20000),
        ]
      } else {
        // All failed — close modal and show error immediately (nothing to process)
        setActionConfirmationModal(null);

        toast({
          variant: toastVariant,
          title: toastTitle,
          description: toastDescription
        });

        // Refresh to show correct state after failure
        refreshTimersRef.current.forEach(clearTimeout)
        refreshTimersRef.current = [
          setTimeout(() => { triggerDataFetchRef.current({ force: true, silent: true }); refreshStageCounts(); }, 1000),
        ]
      }
    } catch (error: any) {
      // Close modal and show error toast
      setActionConfirmationModal(null);

      toast({
        variant: 'destructive',
        title: t('error'),
        description: error?.message || t('actionFailed') || 'Failed to perform action'
      });

      // Refresh to restore correct state after error
      refreshTimersRef.current.forEach(clearTimeout)
      refreshTimersRef.current = [
        setTimeout(() => { triggerDataFetchRef.current({ force: true, silent: true }); refreshStageCounts(); }, 1000),
      ]
    }
  };

  // V2: Helper to get icon for action
  const getActionIcon = (action: any) => {
    if (action.icon) {
      // Map icon names to Lucide icons
      // Lowercase keys to match `action.icon.toLowerCase()` below. Names are the
      // ones our action-config seed JSONs use server-side
      // (BuildVmsActionConfigurations + the existing WO actions). Adding a new
      // action with an icon? Map it here so the kebab actually renders the glyph.
      const iconMap: Record<string, any> = {
        'check': Check,
        'external-link': ExternalLink,
        'file-text': FileText,
        'clock': Clock,
        'grip': Grip,
        'arrow-right': ArrowRight,
        'x-circle': XCircle,
        // VMS additions — referenced by the VmsRequest/VmsEmail action configs.
        'mail': Mail,
        'history': History,
        'refresh': RefreshCw,
        'trash': Trash2,
        'eye': Eye,
        'ban': Ban,
        // Lifecycle glyphs — match the ClientRequest action set (Play for
        // start-something, Pause for park-something) so the two grids feel
        // consistent. Without these in the map, BuildVmsActionConfigurations
        // ships the icon names but iconMap[...] returns undefined and the
        // kebab renders the action without a glyph.
        'play': Play,
        'pause': Pause,
      };
      const IconComponent = iconMap[action.icon.toLowerCase()];
      return IconComponent ? <IconComponent className="h-4 w-4 mr-2" /> : null;
    }
    // Default icons only for specific action types
    if (action.actionType === 'history') {
      return <Clock className="h-4 w-4 mr-2" />;
    }
    if (action.actionType === 'external_link') {
      return <ExternalLink className="h-4 w-4 mr-2" />;
    }
    if (action.actionCode === 'ViewClientRequests') {
      return <FileText className="h-4 w-4 mr-2" />;
    }
    // No default icon for stage_transition - icon must be explicitly configured
    return null;
  };

  // Force table layout update when column widths change
  useEffect(() => {
    // Don't interfere with resize operations
    if (isResizing || Object.keys(columnWidths).length === 0) return;
    
    // Force a re-render by updating the table container
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      tableContainer.style.width = '100%';
      tableContainer.style.minWidth = '100%';
    }
    
    // Apply column widths directly to DOM elements
    const table = tableContainer?.querySelector('table');
    if (table) {
      // V2: Calculate and set total table width (checkbox with chevron + data columns)
      const dataColumnsWidth = displayColumns.reduce((total, column) => {
        return total + (getColumnWidth(columnWidths, column));
      }, 0);
      const totalWidth = checkboxColWidthRef.current + dataColumnsWidth;
      
      table.style.width = `${totalWidth}px`;
      table.style.minWidth = `${totalWidth}px`;
      
      // V2: Apply widths to header cells
      const headerCells = table.querySelectorAll('th');
      const cbWidth = `${checkboxColWidthRef.current}px`;
      const hasCheckboxCol = checkboxColWidthRef.current > 0;
      headerCells.forEach((cell, index) => {
        if (index === 0 && hasCheckboxCol) {
          // Checkbox column with chevron - fixed width
          cell.style.width = cbWidth;
          cell.style.minWidth = cbWidth;
          cell.style.maxWidth = cbWidth;
        } else {
          const colIndex = hasCheckboxCol ? index - 1 : index;
          const column = displayColumns[colIndex];
          if (column) {
            const width = getColumnWidth(columnWidths, column);
            cell.style.width = `${width}px`;
            cell.style.minWidth = `${width}px`;
            cell.style.maxWidth = `${width}px`;
          }
        }
      });
      
      // V2: Apply widths to body cells
      const colsPerRow = displayColumns.length + (hasCheckboxCol ? 1 : 0);
      const bodyCells = table.querySelectorAll('td');
      bodyCells.forEach((cell, index) => {
        const columnIndex = index % colsPerRow;
        if (columnIndex === 0 && hasCheckboxCol) {
          // Checkbox column with chevron - fixed width
          cell.style.width = cbWidth;
          cell.style.minWidth = cbWidth;
          cell.style.maxWidth = cbWidth;
        } else {
          const colIndex = hasCheckboxCol ? columnIndex - 1 : columnIndex;
          const column = displayColumns[colIndex];
          if (column) {
            const width = getColumnWidth(columnWidths, column);
            cell.style.width = `${width}px`;
            cell.style.minWidth = `${width}px`;
            cell.style.maxWidth = `${width}px`;
          }
        }
      });
    }
  }, [columnWidths, displayColumns, isResizing]);

  // Calculate pagination info
  const totalPages = Math.ceil(totalCount / (state.itemsPerPage !== null ? state.itemsPerPage : pageSize))

  // Add this effect to update page size based on window height and available space
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    // Calculate initial page size with a longer delay to ensure DOM is fully ready
    const initialTimeout = setTimeout(() => {
      // The calculation will be handled by the refs useEffect
    }, 200);
    
    // Add window resize listener with debouncing
    let resizeTimeout: NodeJS.Timeout;
    let lastWindowWidth = window.innerWidth;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const currentWidth = window.innerWidth;
                
        // Reset height calculation flag on any resize to allow recalculation
        setIsHeightCalculated(false);
        lastWindowWidth = currentWidth;
        // The calculation will be handled by the refs useEffect
      }, 200); // Increased debounce time for width changes
    };
    
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
      clearTimeout(initialTimeout);
    };
  }, [headerHeight, paginationHeight, pageSize, setPageSize, setCurrentPage]);

  // Reset height calculation flag when stage changes
  // Note: sortState is initialized from saved sortByStage in useState initializer
  // (grid remounts on stage change, so the initializer re-runs with the correct saved sort)
  useEffect(() => {
    setIsHeightCalculated(false);
  }, [stage]);

  // Trigger height calculation when refs are available
  useEffect(() => {
    if (!isHeightCalculated) {
      // Add a small delay to ensure layout has settled after width changes
      const timeoutId = setTimeout(() => {
        // Force a layout recalculation to get accurate dimensions
        if (mainContainerRef.current) {
          mainContainerRef.current.offsetHeight; // Force reflow
        }
        if (tableContainerRef.current) {
          tableContainerRef.current.offsetHeight; // Force reflow
        }
        
        // Recalculate page size when refs become available
        const mainContainer = mainContainerRef.current;
        if (mainContainer) {
          const containerRect = mainContainer.getBoundingClientRect();
          const actualContainerHeight = containerRect.height;
          
          // Get actual table header height from the DOM
          const tableHeader = tableContainerRef.current?.querySelector('thead');
          const actualTableHeaderHeight = tableHeader ? tableHeader.getBoundingClientRect().height : 50;
          
          const tableHeaderHeight = headerHeight || actualTableHeaderHeight;
          const paginationHeightActual = paginationHeight || 50;
          const buffer = 40; // Increased buffer to prevent page scroll
          let availableHeight = Math.max(0, actualContainerHeight - tableHeaderHeight - paginationHeightActual - buffer);
          
          if (actualContainerHeight < 100) {
            const windowHeight = window.innerHeight;
            const estimatedTopBarHeight = 120; // Increased to be more conservative
            const estimatedBottomPadding = 50; // Increased to be more conservative
            availableHeight = Math.max(0, windowHeight - estimatedTopBarHeight - estimatedBottomPadding - tableHeaderHeight - paginationHeightActual);
          }
          
          const rowHeight = 48;
          const minRowsPerPage = 5;
          const maxRowsPerPage = Math.floor((window.innerHeight * 0.75) / rowHeight); // Reduced to 75% to be more conservative
          // Add a small buffer to ensure we don't show partial rows
          const bufferForPartialRows = 8; // 8px buffer to prevent showing partial rows
          const calculatedRowsPerPage = Math.floor((availableHeight - bufferForPartialRows) / rowHeight);
          const rowsPerPage = Math.max(minRowsPerPage, Math.min(calculatedRowsPerPage, maxRowsPerPage));
          
          if (rowsPerPage > 0 && rowsPerPage !== pageSize) {
            setCurrentPage(1);
            setPageSize(rowsPerPage);
          }
          setIsHeightCalculated(true);
        } else {
          // Fallback to window-based calculation if refs are not available
          const windowHeight = window.innerHeight;
          const estimatedTopBarHeight = 120; // Increased to be more conservative
          const estimatedBottomPadding = 50; // Increased to be more conservative
          const tableHeaderHeight = headerHeight || 50;
          const paginationHeightActual = paginationHeight || 50;
          const availableHeight = Math.max(0, windowHeight - estimatedTopBarHeight - estimatedBottomPadding - tableHeaderHeight - paginationHeightActual);
          
          const rowHeight = 48;
          const minRowsPerPage = 5;
          const maxRowsPerPage = Math.floor((window.innerHeight * 0.75) / rowHeight); // Reduced to 75% to be more conservative
          // Add a small buffer to ensure we don't show partial rows
          const bufferForPartialRows = 8; // 8px buffer to prevent showing partial rows
          const calculatedRowsPerPage = Math.floor((availableHeight - bufferForPartialRows) / rowHeight);
          const rowsPerPage = Math.max(minRowsPerPage, Math.min(calculatedRowsPerPage, maxRowsPerPage));

          if (rowsPerPage > 0 && rowsPerPage !== pageSize) {
            setCurrentPage(1);
            setPageSize(rowsPerPage);
          }
          setIsHeightCalculated(true);
        }
      }, 100); // 100ms delay to ensure layout has settled
      
      return () => clearTimeout(timeoutId);
    }
  }, [mainContainerRef.current, tableContainerRef.current, isHeightCalculated, headerHeight, paginationHeight, pageSize, setPageSize, setCurrentPage]);

  // Detect stage changes and preserve old state during transitions
  useEffect(() => {
    
    if (previousStageRef.current && previousStageRef.current !== stage) {
      setIsStageTransitioning(true);
      setPreviousColumns(previousColumnsRef.current);
    } else if (!previousStageRef.current) {
      setIsStageTransitioning(false);
    }
    
    // Update refs
    previousStageRef.current = stage;
    previousColumnsRef.current = columns;
    setPreviousStage(stage);
  }, [stage, columns]);

  // Clear transition flag when data is loaded
  useEffect(() => {    
    if (isStageTransitioning && !isLoadingWorkOrders && !isLoadingRows && !isLoadingConfiguration) {

      setIsStageTransitioning(false);
    }
  }, [isStageTransitioning, isLoadingWorkOrders, isLoadingRows, isLoadingConfiguration]);

  // Helper to update or add a filter for a column
  function updateStageFilter(fieldConfigurationId: string, filter: string, values: any) {
    const stageId = stageConfig?.stageId;
    if (!stageId) return;
    // A manual column-filter edit while a "Showing: <row>" scope is pausing
    // the stage filters (Requests tabs) means the user is taking over the
    // narrowing — dismiss the scope first so their filter takes effect
    // immediately instead of being silently ignored by the pause.
    if (temporaryQueryFilter && (isRequestEntityType || temporaryQueryFilterPausesStageFilters)) {
      dismissTemporaryFilter();
    }
    const existingFilters = stageFilters.filter(f => !(f.stageId === stageId && f.fieldConfigurationId === fieldConfigurationId));
    if (filter) {
      setStageFilters([...existingFilters, { stageId, fieldConfigurationId, filter, values }]);
    } else {
      setStageFilters(existingFilters);
    }
  }

  // Get current stage filters
  const currentStageFilters = useMemo(() => stageFilters.filter(f => f.stageId === stageConfig?.stageId), [stageFilters, stageConfig]);

  // Cross-tab handoff from the global worker search: clicking a pre-onboarding
  // result switches the hub tab and stashes a single-row scope in
  // sessionStorage (the tabs mount isolated providers, so props/context can't
  // carry it). Adopt it here as the dismissible green "Showing: <worker>"
  // pill — the same temporary-filter mechanism as the VmsEmail "View Linked
  // Request" jump, scoping to the exact RequestId the user clicked.
  //
  // Lifetime: the stash is keyed per entity type and NOT consumed on
  // adoption — Requests tabs unmount their providers on hub-tab switches, so
  // re-adopting on remount is what makes the pill survive moving away and
  // back (matching WO, where search filters persist as stage filters). The
  // stash dies only when the user dismisses the pill or clears filters
  // (dismissTemporaryFilter below), or when the browser session ends.
  const PENDING_SEARCH_FILTER_KEY = `wfh_pending_search_filter.${contextEntityType}`;
  const adoptPendingSearchFilter = () => {
    if (!configuration || !stageConfig?.stageId) return;
    try {
      const raw = sessionStorage.getItem(PENDING_SEARCH_FILTER_KEY);
      if (!raw) return;
      const pending = JSON.parse(raw);
      if (pending?.entityType !== contextEntityType) return;

      // Two stash shapes:
      //   { filter }     — a prebuilt row scope; the writer knew the exact row.
      //   { workerName } — a declarative worker scope from a ?worker= deep
      //                    link. The writer only had the ROOT WorkOrder
      //                    configuration, so it couldn't resolve THIS tab's
      //                    WorkerFullName field to a clause. Building it here
      //                    against our own fieldsInUse gets the right clause
      //                    shape per entity type from the shared
      //                    buildFilterForField, and hands us the
      //                    missing-column guard for free: a tab without the
      //                    column lands unfiltered rather than sending Azure
      //                    Search a filter on a field its index doesn't have,
      //                    which is a 400 — an error state instead of rows.
      let filter = pending.filter || '';
      // Saved column filters on this stage have nothing to do with a worker
      // deep link and would AND with it, so a link into a stage the user had
      // filtered by something else would land on an empty grid. Row hand-offs
      // don't set this: Requests tabs pause unconditionally already, and on
      // WorkOrder the assignment search sets its stage filters AS the scope.
      let pauseStageFilters = false;
      if (!filter && pending.workerName) {
        const workerFilter = buildWorkerScopeFilter(configuration?.fieldsInUse, pending.workerName);
        if (!workerFilter) {
          console.warn(
            `Ignoring worker scope: ${contextEntityType} has no ${WORKER_NAME_COLUMN_CODE} column`,
            { worker: pending.workerName }
          );
          // Drop it so this doesn't re-warn on every adoption pass.
          try { sessionStorage.removeItem(PENDING_SEARCH_FILTER_KEY); } catch { /* ignore */ }
          return;
        }
        filter = workerFilter;
        pauseStageFilters = true;
        if (!isRequestEntityType) {
          // Consume it here, unlike the row-scope stash above. The
          // survive-remount rationale in the Lifetime comment is specific to
          // Requests tabs, whose providers unmount on a tab switch; the root
          // WorkOrder provider never unmounts, so this pill already lives in
          // provider state and needs no stash to come back.
          //
          // Keeping it would let the scope RE-ARM over a later navigation.
          // This grid remounts per stage, so the adoption effect runs again on
          // every stage change — and the worker-search modal's WorkOrder path
          // sets its row filters via setStageFilters directly, never through
          // updateStageFilter, so nothing dismisses this scope on the way
          // through. The user would click Bob, land on his stage, and have
          // pauseStageFilters collapse his filters away in favour of a
          // re-adopted Jane — with the paused pills hidden, so nothing on
          // screen would explain it.
          try { sessionStorage.removeItem(PENDING_SEARCH_FILTER_KEY); } catch { /* ignore */ }
        }
      }

      if (!filter) return;
      if (temporaryQueryFilter === filter) return;

      // Pre-existing stage filters are NOT cleared here — setStageFilters
      // persists to UserViewState, so clearing would durably rewrite saved
      // view state with no restore on dismissal. Instead the row scope PAUSES
      // the stage's column filters transiently: triggerDataFetch excludes
      // them from the query and the pill strip hides them while the
      // temporary filter is active, and any manual column-filter edit
      // auto-dismisses the scope (see updateStageFilter). Saved filters
      // come back untouched the moment the pill dies.

      // The landed row may be terminal (e.g. a RequestAccepted VMS request).
      // While a temporary filter is active, the data fetch skips the
      // hide-completed base clause entirely (see triggerDataFetch), so the
      // row is visible WITHOUT flipping the user's persisted Show Completed
      // toggle — that flip used to be saved into UserViewState and survived
      // the pill's dismissal.
      applyTemporaryQueryFilter(filter, pending.label ?? pending.workerName, { pauseStageFilters });
    } catch { /* ignore corrupt storage */ }
  };
  // Ref keeps the persistent event listener below calling the CURRENT render's
  // adoption closure (fresh configuration/stageFilters) instead of the one
  // captured at mount.
  const adoptPendingSearchFilterRef = useRef(adoptPendingSearchFilter);
  adoptPendingSearchFilterRef.current = adoptPendingSearchFilter;

  // Cross-tab handoff: the target grid mounts AFTER the stash is written, so
  // adoption runs when its configuration + stage resolve.
  useEffect(() => {
    adoptPendingSearchFilterRef.current();
    // Deliberately narrow deps: fire once when the grid becomes ready — the
    // stash is consumed on that pass, so later churn needn't re-enter.
  }, [configuration, stageConfig?.stageId, contextEntityType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Same-tab handoff: when the user is already on the target tab, nothing in
  // this provider changes (setActiveTab is a no-op), so the hub pings this
  // event right after writing the stash. Grids on other tabs / entity types
  // hear it too and bail on the entityType check.
  useEffect(() => {
    const handler = () => adoptPendingSearchFilterRef.current();
    window.addEventListener('wfh-pending-search-filter', handler);
    return () => window.removeEventListener('wfh-pending-search-filter', handler);
  }, []);

  // User-initiated dismissal of the "Showing" pill (pill ×, Clear Filters).
  // Removing the entity-scoped stash is what actually ends the search
  // handoff's lifetime — the stash intentionally survives adoption so the
  // pill can be restored when the tab remounts (see adoptPendingSearchFilter).
  const dismissTemporaryFilter = () => {
    try { sessionStorage.removeItem(PENDING_SEARCH_FILTER_KEY); } catch { /* ignore */ }
    clearTemporaryFilter();
  };

  // ── Applied-filter pills (toolbar strip + Filter sidebar section) ──
  // One pill per active filter so users can see everything narrowing the grid
  // without hunting for blue funnel icons. Soft blue = global (view-level)
  // filters, light lilac = stage (column) filters (see SCOPE_STYLES in
  // applied-filter-pills.tsx).
  const resolveFieldLabel = (fieldConfigurationId: string): string => {
    const field = columns.find(c => c.fieldConfigurationId === fieldConfigurationId)
      || configuration?.fieldsInUse?.find((f: any) => f.fieldConfigurationId === fieldConfigurationId);
    return field ? (getLocalizedField(field, 'columnName', language) || field.columnCode) : (t('filter') || 'Filter');
  };

  // Pill × removals force the next fetch (bypassing the 50ms debounce AND the
  // fetchKey dedup) via this flag, mirroring the isJustPageChange pattern.
  // The passive filters-changed → debounced-fetch path can be starved or
  // deduped by unrelated state churn landing in the same window, which leaves
  // the grid showing rows for a filter that no longer exists.
  const pendingRemovalFetchRef = useRef(false);

  const removeGlobalFilter = (fieldConfigurationId: string) => {
    setGlobalFilters(globalFilters.filter(f => f.fieldConfigurationId !== fieldConfigurationId));
    // Keep the Filter sidebar's selection state in sync so its checkboxes and
    // count badge don't resurrect the removed filter on the next Apply.
    setGlobalFilterSelections(prev => {
      const next = { ...prev };
      delete next[fieldConfigurationId];
      return next;
    });
    setCurrentPage(1);
    pendingRemovalFetchRef.current = true;
  };

  const removeStageFilter = (fieldConfigurationId: string) => {
    updateStageFilter(fieldConfigurationId, '', []);
    // Clear the column's pending popover selections so reopening the funnel
    // doesn't show checks for a filter that no longer exists.
    const col = columns.find(c => c.fieldConfigurationId === fieldConfigurationId);
    if (col) setPopoverFilterValues(prev => ({ ...prev, [col.columnCode]: [] }));
    setCurrentPage(1);
    pendingRemovalFetchRef.current = true;
  };


  // Helper function to get current orderBy value from sortState.
  // Applies SORT_COLUMN_OVERRIDES so columns with mismatched storage types
  // (e.g. WorkOrderNumber stored as string but needing numeric sort) can
  // be transparently redirected to a better-suited index field.
  const currentOrderBy = useMemo(() => {
    if (sortState.columnCode && sortState.direction) {
      const sortCol = columns.find(c => c.columnCode === sortState.columnCode);
      if (sortCol) {
        const columnPath = getSearchColumnPath(sortCol);
        const effectivePath = SORT_COLUMN_OVERRIDES[columnPath] ?? columnPath;
        return `${effectivePath} ${sortState.direction}`;
      }
    }
    return undefined;
  }, [sortState, columns]);

  // Build filters and trigger data fetch.
  // Has built-in dedup: skips the API call if the exact same parameters were already sent.
  // Pass `force: true` to bypass dedup (e.g. post-action refreshes where data may have
  // changed server-side even though the query params haven't).
  // Pass `silent: true` for post-mutation reconciliation refetches whose result is
  // already on screen via the optimistic overlay — rows update in place with no
  // whole-grid spinner (which would otherwise flash on every timed safety refetch).
  const lastFetchKeyRef = useRef('')
  const triggerDataFetch = (options?: { force?: boolean; silent?: boolean }) => {
    if (!configuration) return;

    const applicableGlobalFilters = (stageConfig?.fixedFilter || stageConfig?.excludeGlobalFilters)
      ? []
      : globalFilters;
    // On Requests tabs an active "Showing: <row>" scope PAUSES the stage's
    // column filters instead of mutating them: a pre-existing filter that
    // doesn't match the scoped row would AND with it and hide the row, and
    // durably clearing saved filters was a review-flagged UserViewState
    // rewrite. The pill strip hides the paused pills (see appliedFilterPills)
    // and any manual column-filter edit dismisses the scope first
    // (updateStageFilter), so the pause can never shadow a fresh user filter.
    // WO stages are exempt: the assignment-search flow sets its stage filters
    // AS the scope in the same gesture, so they're coherent with it. A
    // `?worker=` deep link is the exception on WO — its scope has nothing to
    // do with whatever the user last filtered that stage by, so it asks for
    // the pause explicitly (temporaryQueryFilterPausesStageFilters).
    //
    // GLOBAL filters are deliberately NOT paused by any scope, deep link
    // included. That's the pre-existing contract — the row-scope handoff has
    // always ANDed with them — and it's the more defensible half: globals are
    // view-level and their pills stay visible, so a scope that lands empty
    // against one is at least explicable on screen, whereas paused stage pills
    // are hidden. Pausing them would silently widen a deep link past the view
    // the user is sitting in. Change both scopes together if this is revisited.
    const applicableStageFilters =
      (temporaryQueryFilter && (isRequestEntityType || temporaryQueryFilterPausesStageFilters))
        ? [] : currentStageFilters;
    let allFilters = [...applicableGlobalFilters, ...applicableStageFilters]
      .map(f => f.filter)
      .filter(Boolean);

    // VMS docs don't carry a Stage column; if a saved global / stage filter
    // happens to reference Stage (carry-over from a WorkOrder view), Azure Search
    // throws against the VMS index. Drop those clauses entirely — they're
    // meaningless on VMS rows. Match `Stage <op>` with op in {eq, ne, in} so we
    // only strip filters that compare on Stage, not unrelated text.
    if (isRequestEntityType) {
      const stageClauseRegex = /\bStage\s+(eq|ne|in)\b/i
      allFilters = allFilters.filter(f => !stageClauseRegex.test(f))
    }

    // While a temporary "Showing: <row>" filter is active (cross-tab search
    // handoff / VmsEmail → linked request), skip the hide-completed base
    // clause so the targeted row is visible even when terminal — a transient
    // query modifier scoped to the filter's lifetime, instead of flipping the
    // user's persisted Show Completed toggle (which used to rewrite saved
    // view state and never revert). Dismissing the pill restores the clause
    // on the very next fetch.
    const revealCompleted = showCompleted || !!temporaryQueryFilter;
    let baseFilter: string | null;
    if (stageConfig?.fixedFilter) {
      baseFilter = revealCompleted
        ? stageConfig.fixedFilter
        : `${stageConfig.fixedFilter} and OnboardingStatus ne 'Completed'`;
    } else if (isRequestEntityType) {
      // VMS docs have Status, not Stage. With showCompletedToggle=true AND
      // the user's toggle OFF we exclude terminal statuses so the working
      // set stays focused on rows still needing attention. The terminal-
      // status vocabulary differs between the two VMS entity types:
      //
      //   VmsRequest → Status ne 'RequestAccepted' and Status ne 'Canceled'
      //                (request lifecycle: accepted = done, canceled = dead)
      //   VmsEmail   → Status ne 'Processed' and Status ne 'Discarded'
      //                (email lifecycle: processed = done, discarded = soft-deleted)
      //
      // Mirrors the Client Request grid's TERMINAL_STATUSES treatment — same
      // business intent, different status codes per entity. When toggle is
      // off and the stage doesn't opt in (showCompletedToggle=false) the base
      // filter stays null and every row surfaces.
      const hideCompletedVms = stageConfig?.showCompletedToggle && !revealCompleted;
      baseFilter = hideCompletedVms
        ? getVmsHideCompletedFilter(contextEntityType)
        : null;
    } else {
      baseFilter = `Stage eq '${stage}'`;
    }

    const filterParts = [baseFilter, ...allFilters].filter(
      (p): p is string => typeof p === 'string' && p.length > 0
    );
    const filterArray = filterParts.length > 0 ? [filterParts.join(' and ')] : [];

    const facetKeys = getFacetKeysFromConfiguration(configuration);

    // Visible field IDs feed the SELECT clause in refreshData, so a change here
    // (e.g. Jump-to-Column adding a new field) needs to bypass the dedup below —
    // viewSettingsId stays the same when the user adds/removes columns within a view.
    const visibleFieldsKey = (stageConfig?.fieldConfigurations ?? []).map(f => f.fieldConfigurationId).join(',')

    // Dedup: skip if the exact same query was already sent
    // Include stage, viewSettingsId AND entity type to avoid false dedup across
    // stage/view/entity-type switches. The entity type matters because the same
    // <WorkflowGrid> instance can be reused across VmsRequest/VmsEmail providers
    // (or, defensively, in any future case where entityType changes without a
    // re-mount); without it, the dedup ref would short-circuit a legitimate
    // refetch with a different query shape.
    //
    // temporaryQueryFilter is appended to the OData filter array inside
    // refreshData (see workflow-context-simple.tsx) — NOT into filterArray
    // here — so it doesn't naturally vary the fetchKey. Include it
    // explicitly so cross-tab navigation flows (VmsEmail → linked
    // VmsRequest) actually re-fetch instead of being deduped as a no-op.
    const configId = configuration?.viewSettingsId || ''
    // Include state.itemsPerPage in the dedup key so the user's manual rows-per-page
    // selection re-triggers a fetch even when `pageSize` (the auto-calculated value)
    // hasn't changed. Without this, picking "25" from the dropdown updates
    // state.itemsPerPage → refreshData() would use 25 → but the dedup short-circuits
    // because the auto pageSize is unchanged, so refreshData never runs and the grid
    // stays at the old size until the next manual refresh.
    const effectivePageSizeKey = state.itemsPerPage !== null ? state.itemsPerPage : pageSize
    const fetchKey = `${contextEntityType}-${stage}-${configId}-${filterArray.join('|')}-${temporaryQueryFilter || ''}-${currentOrderBy || ''}-${effectivePageSizeKey}-${currentPage}-${(facetKeys || []).join(',')}-${visibleFieldsKey}`
    if (!options?.force && fetchKey === lastFetchKeyRef.current) return;
    lastFetchKeyRef.current = fetchKey;

    if (typeof refreshData === 'function') {
      // A force=true call here means the user explicitly asked for a
      // refetch (header Refresh icon, or post-mutation reload) — propagate
      // forceLoadingState so the spinner is reliably visible even when the
      // search response returns in <100ms and the spinner would otherwise
      // flash too briefly to register. silent=true (background reconciliation
      // of an optimistic update) wins over that: no spinner at all.
      const loadingOptions = options?.silent
        ? { silent: true }
        : options?.force ? { forceLoadingState: true } : undefined;
      refreshData(filterArray, currentOrderBy, facetKeys, undefined, loadingOptions);
    }
  };

  // The delayed post-mutation refreshes (1.5s/5s/20s timers below) fire long
  // after the render that scheduled them. Routing them through this ref makes
  // the refetch use the CURRENT filters/page/stage instead of the scheduling
  // render's stale closure — which would otherwise re-query an old view and
  // clobber the grid after the user changed filters within the timer window.
  const triggerDataFetchRef = useRef(triggerDataFetch);
  triggerDataFetchRef.current = triggerDataFetch;

  // Main data-fetching effect — THE SINGLE SOURCE OF TRUTH for grid data.
  //
  // This effect is the ONLY place that triggers search API calls for grid data.
  // The context does NOT call refreshData() on init — this effect handles everything.
  //
  // Gates:
  //   - configuration: need it to build filters/select fields
  //   - isHeightCalculated: wait for page size to settle (prevents a wasted call
  //     with default pageSize=5 that gets immediately superseded by the real size)
  //   - !isHandlingClientFilterChange: wait for client filter resolution to avoid
  //     the initial call being suppressed then a second one firing after resolution
  //
  // Always debounced (no immediate first-fetch) to batch rapid dep changes during
  // the mount sequence. triggerDataFetch() has its own fetchKey dedup as a safety net.
  //
  // Two-tier debounce: the FIRST fetch after mount uses a longer window because
  // init state arrives in waves — SET_CONFIGURATION lands first, then the
  // 100ms-delayed height-calc effect dispatches setPageSize / setCurrentPage /
  // setIsHeightCalculated, then any follow-up loadColumns / SET_CURRENT_STAGE
  // settles. Each render with a distinct dep set produces a distinct fetchKey
  // that the lastFetchKeyRef dedup can't collapse, so without a long-enough
  // batching window the grid fires 3-4 searches stacked back-to-back on the
  // VMS tab (one per provider × N init waves). After the first fetch settles
  // we drop back to a snappy 50ms window so user gestures (filter change,
  // sort) feel responsive.
  const dataFetchDebounceRef = useRef<NodeJS.Timeout | null>(null)
  const hasFiredFirstFetchRef = useRef(false)
  useEffect(() => {
    if (dataFetchDebounceRef.current) clearTimeout(dataFetchDebounceRef.current)

    // Wait for all prerequisites before fetching
    if (!configuration || !isHeightCalculated || state.isHandlingClientFilterChange) return

    // For page changes, fetch immediately (user expects instant response)
    if (isJustPageChange.current) {
      triggerDataFetch({ force: true });
      setTimeout(() => { isJustPageChange.current = false }, 100);
      return
    }

    // Pill × removal: fetch immediately and forced. This effect run has the
    // post-removal filters in scope, so the query is guaranteed to reflect
    // the removal regardless of debounce timing or the fetchKey dedup.
    if (pendingRemovalFetchRef.current) {
      pendingRemovalFetchRef.current = false;
      triggerDataFetch({ force: true });
      return
    }

    // First fire after mount gets the longer window to absorb staggered init
    // state changes (see comment above). Subsequent fires (user-driven) use
    // the short 50ms window.
    const debounceMs = hasFiredFirstFetchRef.current ? 50 : 300
    dataFetchDebounceRef.current = setTimeout(() => {
      hasFiredFirstFetchRef.current = true
      triggerDataFetch();
    }, debounceMs)

    return () => {
      if (dataFetchDebounceRef.current) clearTimeout(dataFetchDebounceRef.current)
    }
    // temporaryQueryFilter is a real input to the search query (appended to
    // the OData filter array in refreshData) — without it in the deps list,
    // applying a cross-tab navigation filter (e.g. VmsEmail → linked
    // VmsRequest via APPLY_TEMPORARY_QUERY_FILTER) updates state but never
    // re-runs this effect, so the grid keeps showing the previous (un-
    // narrowed) result set even though the filter is technically "applied".
  }, [isHeightCalculated, currentStageFilters, globalFilters, sortState, stage, currentPage, pageSize, state.itemsPerPage, configuration, showCompleted, state.isHandlingClientFilterChange, contextEntityType, temporaryQueryFilter, temporaryQueryFilterPausesStageFilters]); // eslint-disable-line react-hooks/exhaustive-deps



  // Helper to build the final filter string
  function buildFinalFilter(fieldConfigurationId: string, filterString: string) {
    if (!filterString) return '';
    // Don't include Stage in column filters as it's handled in the main effect
    if (filterString.includes('Stage eq')) return filterString;
    return filterString;
  }


  const handleCellEdit = (rowId: string, columnId: string) => {
    // Find the column configuration
    const column = displayColumns.find(col => col.fieldConfigurationId === columnId);
    
    // Check if this is a checklist column
    if (column && column.columnTypeCode?.toLowerCase() === 'checklist') {
      // Find the work order for this row
      const workOrder = workOrders.find(wo => wo.id === rowId);
      if (workOrder) {
        // Check edit permissions
        const authorizedActions = (configuration?.authorizedActions || []).map((a: any) => typeof a === 'string' ? a : a.actionCode);
        const canEditFieldData = authorizedActions.includes("EditFieldData");
        
        setChecklistPanelState({
          isOpen: true,
          itemId: rowId,
          workOrderId: workOrder.fboWorkOrderNumber || workOrder.workOrderId,
          fieldConfigurationId: columnId,
          itemTitle: workOrder.workerFullName || workOrder.workerName || 'Worker',
          canEdit: canEditFieldData,
        });
      }
      return;
    }
    
    // Normal cell editing
    setEditingCell({ rowId, columnId })
  }

  const handleCellSave = async (rowId: string, columnId: string, value: any) => {
    
    // Special handling for Actions column - update Stage field instead
    const fieldToUpdate = columnId.toLowerCase() === "actions" ? "Stage" : columnId;
    
    // Convert field name to camelCase to match the data structure (same as getCellValue)
    const camelCaseField = fieldToUpdate.charAt(0).toLowerCase() + fieldToUpdate.slice(1);
  
    
    // If this is a stage update and the stage is changing
    if (fieldToUpdate === "Stage" && value !== stage) {
      // Make the API call for stage update
      const success = await updateWorkOrderField(rowId, fieldToUpdate, value);
      
      // Only remove the item from the current view if the update was successful
      if (success) {
        removeWorkOrder(rowId);
      }
    } else if (isRequestEntityType) {
      // Requests entities (VmsRequest/VmsEmail/ClientRequest/AtsPlacementRequest) are
      // status-based and have no stage. Inline edits update the row in place optimistically:
      // the UI changes immediately and the change is reverted to its prior value if the API
      // call fails (see updateWorkOrderFieldOptimistically + the pendingOptimistic overlay).
      updateWorkOrderFieldOptimistically(rowId, camelCaseField, value);
    } else {
      // WorkOrder/Assignment: keep the existing blocking (non-optimistic) update for now.
      await updateWorkOrderField(rowId, camelCaseField, value);
    }

    setEditingCell(null);
  }

  const handleColumnHeaderRightClick = (e: React.MouseEvent, columnId: string) => {
    e.preventDefault()
    setContextMenu({
      position: { top: e.clientY, left: e.clientX },
      columnId,
    })
  }

  const handleEditFieldAccess = (column: any) => {
    setEditFieldConfiguration(column)
    setIsEditFieldModalOpen(true)
  }

  // Helper function to get user initials
  const getUserInitials = (user: any) => {
    const fullName = user.fullName || user.email || '';
    if (user.fullName) {
      // Get initials from full name
      return fullName
        .split(' ')
        .filter((name: string) => name.length > 0)
        .map((name: string) => name.charAt(0).toUpperCase())
        .slice(0, 2)
        .join('');
    } else {
      // Get initials from email
      return user.email ? user.email.charAt(0).toUpperCase() : '?';
    }
  }

  const handlePreviousPage = () => {
    if (hasPreviousPage && currentPage > 1) {
      // Track pagination action
      trackGridAction('page', { direction: 'previous', fromPage: currentPage, toPage: currentPage - 1 });
      
      // Update page - useEffect will handle data fetching
      isJustPageChange.current = true;
      setCurrentPage(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (hasNextPage && currentPage < totalPages) {
      // Track pagination action
      trackGridAction('page', { direction: 'next', fromPage: currentPage, toPage: currentPage + 1 });
      
      // Update page - useEffect will handle data fetching
      isJustPageChange.current = true;
      setCurrentPage(currentPage + 1)
    }
  }

  // Add safety check for workOrders to prevent undefined errors
  const safeItems: any[] = workOrders || []

  // Shared by the selection bar and the row kebab — see hooks/use-message-read-actions.ts.
  const {
    canMarkMessages,
    markMessages,
    isMarking: isMarkingMessages,
    muteMessages,
    isMuting: isMutingMessages
  } = useMessageReadActions(contextEntityType, safeItems)

  // Both menus follow the Message column exactly: no column, or the feature flag off, means no
  // message actions either. Offering "mark as read" on a grid with no visible messages is a control
  // for a feature the user cannot see.
  const messageActionsAvailable = Boolean(messageColumn) && !isMessageColumnHidden

  const { messageNotificationsEnabled } = useMessageNotifications()

  // Badges go stale the moment somebody else posts; this is the only thing that notices.
  useMessageBadgeRefresh(contextEntityType, safeItems, messageActionsAvailable)
  const isAllSelected = safeItems.length > 0 && safeItems.every((item: any) => item.id && selectedItems.includes(item.id))
  // Compute if any filters are active (stage or global)
  const hasActiveFilters = currentStageFilters.length > 0 || globalFilters.length > 0 || !!temporaryQueryFilter;

  // Normalize external sources to camelCase for robust mapping
  const externalSources = (configuration?.externalSourceConfigurations || []).map(src => ({
    link: (src as any).link || (src as any).Link,
    titleEnglish: (src as any).titleEnglish || (src as any).TitleEnglish,
    titleFrench: (src as any).titleFrench || (src as any).TitleFrench,
    condition: (src as any).condition || (src as any).Condition,
  })).filter(src => {
    // Filter out sources that don't have required properties
    const isValid = src.link && src.titleEnglish;
    if (!isValid) {
      console.warn('External source missing required properties:', src);
    }
    return isValid;
  })

  // Initialize the popover's draft state for a column and open it. Shared by
  // the header funnel icon and the applied-filter pills' click-to-edit.
  const openColumnFilterPopover = (col: any, anchor?: HTMLElement | null) => {
    const columnCode = col.columnCode;
    const filterObj = currentStageFilters.find(f => f.fieldConfigurationId === col.fieldConfigurationId);

    // Check if column is facetable first - it takes priority over searchable
    const isFilterableMenu = isFilterableField(col) && (getFilterOptionsForColumn(col).length > 0 || (hasCodeValueSource(col) && codeValues && codeValues.isReady));

    let initValue: any;
    if (filterObj && 'values' in filterObj && filterObj.values !== undefined) {
      // Normalize values for UI display
      let normalizedValues = filterObj.values;

      // If it's the new structured format, convert it back to array format for UI
      if (typeof normalizedValues === 'object' && !Array.isArray(normalizedValues) && 'regularValues' in normalizedValues) {
        const { regularValues, hasBlanks } = normalizedValues;
        normalizedValues = hasBlanks ? ['__BLANK__', ...regularValues] : regularValues;
      }

      initValue = normalizedValues;
    } else if (isFacetableField(col) || isFilterableMenu) {
      // Facetable columns should always use arrays
      initValue = [];
    } else if (isBooleanField(col)) {
      initValue = [];
    } else if (isFilterableField(col) && Array.isArray(col.customValues) && col.customValues.length > 0) {
      initValue = [];
    } else if (isSearchableField(col)) {
      initValue = { blank: false, search: '' };
    } else if (isNumberField(col)) {
      initValue = '';
    } else if (isDateField(col)) {
      initValue = { start: null, end: null };
    }

    // Set the filter values synchronously before opening the popover
    if (initValue !== undefined) {
      setPopoverFilterValues(prev => ({ ...prev, [columnCode]: initValue }));
    }

    // Now open the popover after the state is set
    setFilterAnchor(anchor ?? null);
    setActiveFilterColumn(columnCode);
    setPopoverSearch(""); // Reset search on open
  };

  // When a popover opens, initialize local state for that column
  const handleFilterIconClick = (e: React.MouseEvent<HTMLButtonElement>, columnCode: string) => {
    const col = columns.find(c => c.columnCode === columnCode);
    if (!col) return;

    // Track filter icon click
    trackFilterAction('modify', 'column_filter', columnCode);

    openColumnFilterPopover(col, e.currentTarget);
  };

  // On popover close, commit local state to global filter state
  const handlePopoverClose = () => {
    if (!activeFilterColumn) {
      setFilterAnchor(null);
      setActiveFilterColumn(null);
      return;
    }
    const col = columns.find(c => c.columnCode === activeFilterColumn);
    if (!col) {
      setFilterAnchor(null);
      setActiveFilterColumn(null);
      return;
    }
    const localValue = popoverFilterValues[activeFilterColumn];
    let filterString = '';
    let values: any = localValue;

    // --- Prevent unnecessary filter update if value is unchanged ---
    const currentFilterObj = currentStageFilters.find(f => f.fieldConfigurationId === col.fieldConfigurationId);
    let currentValue = currentFilterObj?.values ?? (col.columnTypeCode?.toLowerCase() === 'boolean' ? [] : (col.isSearchable ? { blank: false, search: '' } : (col.isFilterable ? [] : '')));
    
    // Normalize structured format to array format for comparison
    if (typeof currentValue === 'object' && !Array.isArray(currentValue) && 'regularValues' in currentValue) {
      const { regularValues, hasBlanks } = currentValue;
      currentValue = hasBlanks ? ['__BLANK__', ...regularValues] : regularValues;
    }
    
    let normLocal = localValue;
    let normCurrent = currentValue;
    
    // Universal normalization for all column types
    if (isFacetableField(col) || isBooleanField(col)) {
      // For facetable columns: normalize undefined/null to empty array
      normLocal = Array.isArray(localValue) ? localValue : (localValue == null || localValue == undefined ? [] : [localValue]);
      normCurrent = Array.isArray(currentValue) ? currentValue : (currentValue == null || currentValue == undefined ? [] : [currentValue]);
    } else if (isDateField(col)) {
      // For date columns: normalize to {start: null, end: null} format
      normLocal = localValue || { start: null, end: null };
      normCurrent = currentValue || { start: null, end: null };
      // Also handle case where currentValue is an empty array (from facetable logic)
      if (Array.isArray(normCurrent) && normCurrent.length === 0) {
        normCurrent = { start: null, end: null };
      }
    } else if (isSearchableField(col)) {
      // For searchable columns: normalize to { blank: false, search: '' } format
      normLocal = localValue || { blank: false, search: '' };
      normCurrent = currentValue || { blank: false, search: '' };
    } else {
      // For all other columns: normalize to string, treat empty/null as empty string
      normLocal = localValue || [];
      normCurrent = currentValue || [];
    }
    
    const isUnchanged = JSON.stringify(normLocal) === JSON.stringify(normCurrent);

    // --- Facetable logic ---
    if (col.isFacetable) {
      // If nothing is selected, remove the filter for this field only if it exists
      if (!localValue || localValue.length === 0) {
        const stageId = stageConfig?.stageId;
        const hasFilter = !!currentFilterObj;
        if (stageId && hasFilter) {
          setStageFilters(stageFilters.filter(f => !(f.stageId === stageId && f.fieldConfigurationId === col.fieldConfigurationId)));
        }
        setPopoverFilterValues(prev => ({ ...prev, [activeFilterColumn]: [] }));
        setFilterAnchor(null);
        setActiveFilterColumn(null);
        return;
      }
      if (isUnchanged) {
        setFilterAnchor(null);
        setActiveFilterColumn(null);
        return;
      }
      // Otherwise, update filter
      const searchColumn = getSearchColumnPath(col);
      const hasBlank = localValue.includes('__BLANK__');
      const regularValues = localValue.filter((val: string) => val !== '__BLANK__');
      
      // Check if this is a Collection column that needs /any syntax
      if (col.columnTypeCode?.toLowerCase() === 'collection' && col.subColumnName) {
        if (hasBlank && regularValues.length > 0) {
          // Include empty collections or those matching selected values
          const orConditions = regularValues.map((val: string) => `x/${col.subColumnName} eq '${val.replace(/'/g, "''")}'`).join(' or ');
          filterString = `(not ${col.columnCode}/any() or ${col.columnCode}/any(x: ${orConditions}))`;
        } else if (hasBlank) {
          // Only empty collections
          filterString = `not ${col.columnCode}/any()`;
        } else if (regularValues.length > 0) {
          // Only selected values - use /any syntax for collections
          const orConditions = regularValues.map((val: string) => `x/${col.subColumnName} eq '${val.replace(/'/g, "''")}'`).join(' or ');
          filterString = `${col.columnCode}/any(x: ${orConditions})`;
        }
      } else if (hasBlank) {
        const blankCondition = `${searchColumn} eq null or ${searchColumn} eq ''`;
        if (regularValues.length > 0) {
          const orConditions = regularValues.map((val: string) => `${searchColumn} eq '${val.replace(/'/g, "''")}'`).join(' or ');
          filterString = `(${blankCondition} or ${orConditions})`;
        } else {
          filterString = `(${blankCondition})`;
        }
      } else {
        const orConditions = regularValues.map((val: string) => `${searchColumn} eq '${val.replace(/'/g, "''")}'`).join(' or ');
        filterString = regularValues.length > 1 ? `(${orConditions})` : orConditions;
      }
      updateStageFilter(col.fieldConfigurationId, filterString, { regularValues, hasBlanks: hasBlank });
      setCurrentPage(1); // Reset to first page when adding a new filter
      setFilterAnchor(null);
      setActiveFilterColumn(null);
      setPopoverFilterValues(prev => ({
        ...prev,
        [activeFilterColumn]: localValue
      }));
      return;
    }
    // --- End facetable logic ---

    if (isUnchanged) {
      setFilterAnchor(null);
      setActiveFilterColumn(null);
      return; // Do not update or reload if unchanged
    }

    // Generalized isFacetable logic (including AssignedToUser). Shared canonical
    // builder — same OData as the sidebar / drawer / view designer. This also fixes
    // the popover silently DROPPING a checked "(blank)" on facetable columns (the old
    // inline code computed hasBlank but never put it in the filter string).
    if (col.isFacetable) {
      const hasBlank = localValue && localValue.includes('__BLANK__');
      const regularValues = localValue ? localValue.filter((val: string) => val !== '__BLANK__') : [];
      if (localValue && localValue.length > 0) {
        filterString = buildFacetedValuesFilter(col, localValue);
      }
      updateStageFilter(col.fieldConfigurationId, filterString, { regularValues, hasBlanks: hasBlank });
      setCurrentPage(1); // Reset to first page when adding a new filter
      setFilterAnchor(null);
      setActiveFilterColumn(null);
      setPopoverFilterValues(prev => ({
        ...prev,
        [activeFilterColumn]: localValue
      }));
      return;
    }

    if (col.columnTypeCode?.toLowerCase() === 'boolean') {
      const hasBlank = localValue && localValue.includes('__BLANK__');
      const regularValues = localValue ? localValue.filter((val: string) => val !== '__BLANK__') : [];
      if (localValue && localValue.length > 0) {
        filterString = buildFacetedValuesFilter(col, localValue);
      }
      updateStageFilter(col.fieldConfigurationId, filterString, { regularValues, hasBlanks: hasBlank });
      setCurrentPage(1); // Reset to first page when adding a new filter
      setFilterAnchor(null);
      setActiveFilterColumn(null);
      setPopoverFilterValues(prev => ({
        ...prev,
        [activeFilterColumn]: localValue
      }));
      return;
    }
    if (col.isFilterable && ((Array.isArray(col.customValues) && col.customValues.length > 0) || (col.codeValueSource && codeValues && codeValues.isReady))) {
      const hasBlank = localValue && localValue.includes('__BLANK__');
      const regularValues = localValue ? localValue.filter((val: string) => val !== '__BLANK__') : [];
      if (localValue && localValue.length > 0) {
        // Shared canonical builder — collection//any(), textlist, and type-aware
        // scalar shapes (boolean/number/date blanks are `eq null` only) all live in
        // buildFacetedValuesFilter so every filter surface emits identical OData.
        filterString = buildFacetedValuesFilter(col, localValue);
      }
      updateStageFilter(col.fieldConfigurationId, filterString, { regularValues, hasBlanks: hasBlank });
      setCurrentPage(1); // Reset to first page when adding a new filter
    } else if (col.isSearchable) {
      const searchValue = typeof localValue === 'object' ? localValue : { blank: false, search: '' };
      const hasBlank = searchValue.blank;
      const searchText = (searchValue.search || '').trim();
      
      if (hasBlank || searchText) {
        const searchColumn = getSearchColumnPath(col);
        
        // For TextList fields, use collection empty check
        const blankCondition = col?.columnTypeCode?.toLowerCase() === 'textlist' 
          ? `not ${col.columnCode}/any()`
          : `${searchColumn} eq null or ${searchColumn} eq ''`;
        
        if (hasBlank && searchText) {
          // Both blank and search text: combine with OR - wrap entire expression in parentheses
          const wasSelectedFromSuggestions = selectedFromSuggestions[col.columnCode];
          let searchCondition = '';
          
          if (wasSelectedFromSuggestions) {
            searchCondition = `search.ismatch('${searchText.replace(/'/g, "''")}', '${searchColumn}', 'full', 'all')`;
            setSelectedFromSuggestions(prev => ({ ...prev, [col.columnCode]: false }));
          } else {
            searchCondition = buildSearchableFilter(searchText, searchColumn);
          }

          filterString = `((${blankCondition}) or (${searchCondition}))`;
        } else if (hasBlank) {
          // Only blank
          filterString = `(${blankCondition})`;
        } else if (searchText) {
          // Only search text
          const wasSelectedFromSuggestions = selectedFromSuggestions[col.columnCode];

          if (wasSelectedFromSuggestions) {
            filterString = `search.ismatch('${searchText.replace(/'/g, "''")}', '${searchColumn}', 'full', 'all')`;
            setSelectedFromSuggestions(prev => ({ ...prev, [col.columnCode]: false }));
          } else {
            filterString = buildSearchableFilter(searchText, searchColumn);
          }
        }
        updateStageFilter(col.fieldConfigurationId, filterString, searchValue);
        setCurrentPage(1); // Reset to first page when adding a new filter
      } else {
        // If both blank and search are empty, remove the filter
        const stageId = stageConfig?.stageId;
        if (stageId) {
          setStageFilters(stageFilters.filter(f => !(f.stageId === stageId && f.fieldConfigurationId === col.fieldConfigurationId)));
        }
      }
    } else if (isNumberField(col)) {
      const value = (localValue || '').trim();
      if (value) {
        filterString = `${col.columnCode} eq ${value}`;
      }
      updateStageFilter(col.fieldConfigurationId, filterString, value);
      setCurrentPage(1); // Reset to first page when adding a new filter
    } else if (isDateField(col)) {
      const { start, end } = localValue || {};
      if (start && !end) {
        const startStr = `${start}T00:00:00Z`;
        const nextDay = new Date(Date.UTC(parseInt(start.split('-')[0]), parseInt(start.split('-')[1]) - 1, parseInt(start.split('-')[2]) + 1));
        const nextDayStr = `${nextDay.toISOString().split('T')[0]}T00:00:00Z`;
        filterString = `${col.columnCode} ge ${startStr} and ${col.columnCode} lt ${nextDayStr}`;
      } else if (start && end) {
        const startStr = `${start}T00:00:00Z`;
        const nextDay = new Date(Date.UTC(parseInt(end.split('-')[0]), parseInt(end.split('-')[1]) - 1, parseInt(end.split('-')[2]) + 1));
        const nextDayStr = `${nextDay.toISOString().split('T')[0]}T00:00:00Z`;
        filterString = `${col.columnCode} ge ${startStr} and ${col.columnCode} lt ${nextDayStr}`;
      }
      updateStageFilter(col.fieldConfigurationId, filterString, localValue);
      setCurrentPage(1); // Reset to first page when adding a new filter
      setDateFilter(localValue || { start: null, end: null });
    }
    setFilterAnchor(null);
    setActiveFilterColumn(null);
    setPopoverFilterValues(prev => ({
      ...prev,
      [activeFilterColumn]: localValue
    }));
  };

  // Update Clear Filters handler to clear both stage and global filters and reset globalFilterSelections
  function handleClearAllFilters() {
    // Track clear all filters action
    trackFilterAction('clear', 'all_filters', { stageFilters: currentStageFilters.length, globalFilters: globalFilters.length });
    
    const stageId = stageConfig?.stageId;
    if (stageId) {
      // Only clear filters for the current stage
      setStageFilters(stageFilters.filter(f => f.stageId !== stageId));
    }
    setGlobalFilters([]);
    setGlobalFilterSelections({}); // Reset sidebar/global filter count state
    setSortState({ columnCode: null, direction: null });
    setSortForStage(null); // Persist sort clear to view state
    // Clear persistent searchable filter state (drafts + suggestions cache) for all columns
    clearSearchableFilterState();
    // --- Clear popoverFilterValues for all facetable columns ---
    const facetableColumns = columns.filter(c => c.isFacetable);
    setPopoverFilterValues(prev => {
      const cleared = { ...prev };
      facetableColumns.forEach(col => { cleared[col.columnCode] = []; });
      return cleared;
    });
    // --- End clear ---
    // Cross-tab navigation filter (id eq '<X>' from VmsEmail → linked
    // VmsRequest, the global-search handoff, or FboAssignmentId eq … from the
    // older assignment-search flow) is a real filter to the user, so Clear
    // Filters must drop it — including the pending-search stash, or the
    // handoff effect would immediately revive the chip.
    if (temporaryQueryFilter) {
      dismissTemporaryFilter();
    }
    setCurrentPage(1); // Reset to first page after clearing filters
  }

  // Reset to current view's saved filters
  const handleResetToCurrentView = async () => {
    if (currentView) {
      await setCurrentView(currentView);
    }
  }

  // Reset to default view
  const handleResetToDefault = async () => {
    if (viewsResponse?.defaultView) {
      await setCurrentView(viewsResponse.defaultView);
    }
  }

  // Sorting click handler — persists to view state
  const handleSortClick = (columnCode: string) => {
    // Track sort action
    trackGridAction('sort', { columnCode, currentDirection: sortState.direction });

    let next: { columnCode: string | null, direction: 'asc' | 'desc' | null }
    if (sortState.columnCode !== columnCode) {
      next = { columnCode, direction: 'asc' };
    } else if (sortState.direction === 'asc') {
      next = { columnCode, direction: 'desc' };
    } else if (sortState.direction === 'desc') {
      next = { columnCode: null, direction: null };
    } else {
      next = { columnCode, direction: 'asc' };
    }

    setSortState(next);
    // Persist to context → view state
    setSortForStage(next.columnCode && next.direction
      ? { columnCode: next.columnCode, direction: next.direction as 'asc' | 'desc' }
      : null
    );
  };

  // --- Pending items toggle logic ---
  // Locate status column for current stage
  const statusColumn = useMemo(
    () => columns.find((c: any) => c.columnCode === 'WorkOrderVersionStatusCode'),
    [columns]
  );

  // Check if the pending filter is currently active for this stage
  const isPendingActive = useMemo(() => {
    if (!statusColumn) return false;
    const existing = currentStageFilters.find(
      (f: any) => f.fieldConfigurationId === statusColumn.fieldConfigurationId
    );
    if (!existing) return false;
    if (Array.isArray(existing.values)) {
      const selected = existing.values as string[];
      return (
        selected.length === pendingStatusValues.length &&
        pendingStatusValues.every((v) => selected.includes(v))
      );
    }
    return false;
  }, [currentStageFilters, statusColumn, pendingStatusValues]);

  // Apply or clear the pending filter
  const setPendingFilter = (enabled: boolean) => {
    if (!statusColumn || !stageConfig?.stageId) return;
    if (enabled) {
      const searchColumn = getSearchColumnPath(statusColumn);
      const orConditions = pendingStatusValues
        .map((v) => `${searchColumn} eq '${v.replace(/'/g, "''")}'`)
        .join(' or ');
      const filterString = `(${orConditions})`;
      updateStageFilter(statusColumn.fieldConfigurationId, filterString, { regularValues: pendingStatusValues, hasBlanks: false });
      setCurrentPage(1);
    } else {
      setStageFilters(
        stageFilters.filter(
          (f: any) =>
            !(f.stageId === stageConfig.stageId && f.fieldConfigurationId === statusColumn.fieldConfigurationId)
        )
      );
      setCurrentPage(1);
    }
  };

  // Add this handler function inside WorkflowGrid:
  function handleClearColumnFilter(column: any) {
    // Track clear column filter action
    trackFilterAction('clear', 'column_filter', column.columnCode);
    
    updateFilter(column.columnCode, []);
    const stageId = stageConfig?.stageId;
    if (stageId) {
      setStageFilters(stageFilters.filter((f: { stageId: string; fieldConfigurationId: string, filter: string }) => 
        !(f.stageId === stageId && f.fieldConfigurationId === column.fieldConfigurationId)
      ));
    }
    setPopoverFilterValues(prev => ({ 
      ...prev, 
      [column.columnCode]: isDateField(column) 
        ? { start: null, end: null } 
        : isSearchableField(column)
          ? { blank: false, search: '' }
          : Array.isArray(prev[column.columnCode]) 
            ? [] 
            : '' 
    }));
    setSelectedFromSuggestions(prev => ({ ...prev, [column.columnCode]: false }));
    // Clear the draft ref and cached suggestions for searchable fields
    if (isSearchableField(column)) {
      clearSearchableFilterState(column.columnCode);
    }
    setFilterAnchor(null);
    setActiveFilterColumn(null);
  }


  useEffect(() => {
    if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight || 0);
    if (paginationRef.current) setPaginationHeight(paginationRef.current.offsetHeight || 0);
  }, [columns]);

  // Update fetchGlobalFacets to use the ref
  async function fetchGlobalFacets({ search, facetKeys }: { search: string, facetKeys: string[] }) {
    if (!facetKeys || facetKeys.length === 0) return;
    if (fetchingFacets.current) return; // Prevent concurrent fetches
    // Both VmsRequest and VmsEmail now share the Azure Search code path on
    // the backend (gw-client-request-index for requests, gw-vms-email-index
    // for emails). Facet aggregation works for both — the previous
    // Cosmos-side OData→Cosmos translator + GROUP BY fan-out has been
    // retired in favour of native Azure Search facets.
    fetchingFacets.current = true;
    try {
      // Use column codes for SELECT (to get full objects), facetKeys for facets (to aggregate by sub-properties)
      const selectFields = getSelectFieldsFromConfiguration(configuration);
      const select = selectFields.join(',');
      // VMS docs share the index with ClientRequest rows but don't have a Stage
      // column. Scope by DocumentType (the discriminator) derived from the
      // active entity type so the facets reflect the right slice. RequestType
      // now carries the actual request flavour (Onboarding/Extension/etc.),
      // not the family — using it as the scope here would return nothing.
      //
      // Mirror the main grid's Show-Completed semantics (see baseFilter
      // construction in triggerDataFetch around line 1517): when the toggle
      // is off, exclude terminal statuses. Without this the facet counts
      // surfaced in the sidebar include terminal rows while the grid behind
      // them doesn't — counts wouldn't match the visible rows. The exclusion
      // vocabulary is entity-type aware (RequestAccepted/Canceled for
      // VmsRequest, Processed/Discarded for VmsEmail) so the same call works
      // on both sub-tabs.
      const vmsHideCompleted = isRequestEntityType
        && stageConfig?.showCompletedToggle
        && !showCompleted;
      const vmsHideCompletedClause = vmsHideCompleted
        ? ` and ${getVmsHideCompletedFilter(contextEntityType)}`
        : '';
      let filter = isRequestEntityType
        ? `DocumentType eq '${contextEntityType}'${vmsHideCompletedClause}`
        : `DocumentType eq 'Assignment' and Stage ne '' and Stage ne null and Stage ne 'Discarded' and IsDeleted eq false`;
      const response = await searchWorkflowItems(
        undefined,
        undefined,
        [],
        1,
        0,
        search,
        select,
        filter,
        undefined,
        facetKeys
      );
      setFacetsFetched(true); // Only set after successful fetch
    } catch (err) {
      console.error('Failed to fetch global facets', err);
    } finally {
      fetchingFacets.current = false;
    }
  }


  // Update sidebar open effect: fetch facets if missing when sidebar is opened, and only if not already fetched
  useEffect(() => {
    if (isGlobalFilterSidebarOpen) {
      // VMS entities (VmsEmail / VmsRequest) don't carry the populated assignedUsers
      // list the original WorkflowHub entities use for the AssignedTo facet — emails
      // and VMS requests filter on Status / Client / From / etc. instead. Keep the
      // assignedUsers gate ONLY for non-VMS entities; for VMS we trust the field
      // configuration's IsFacetable flag and fetch facets unconditionally. Without
      // this relaxation the Emails-tab filter panel rendered empty for tenants whose
      // VMS sub-tab had no assignedUsers in the global pool.
      const requiresAssignedUsers = !isRequestEntityType;
      const assignedUsersGateOk = !requiresAssignedUsers || (assignedUsers && assignedUsers.length > 0);
      if (
        !facetsFetched &&
        Array.isArray(configuration?.globalFilterIds) &&
        configuration.globalFilterIds.length > 0 &&
        assignedUsersGateOk
      ) {
        const globalFilterFields = configuration.fieldsInUse.filter(f => Array.isArray(configuration.globalFilterIds) && configuration.globalFilterIds.includes(f.fieldConfigurationId));
        const facetKeys = globalFilterFields.map(f => getSearchColumnPath(f));
        fetchGlobalFacets({ search: '*', facetKeys });
      }
      // Sync globalFilterSelections with globalFilters
      if (Array.isArray(globalFilters)) {
        const selections: Record<string, string[]> = {};
        globalFilters.forEach(f => {
          const field = configuration?.fieldsInUse.find(field => field.fieldConfigurationId === f.fieldConfigurationId);
          if (!field) return;
          
          // Try to use metadata first (for new filters with special flags)
          if (f.values) {
            try {
              const metadata = JSON.parse(f.values);
              const combinedValues = [];
              
              if (metadata.isSelectAll) {
                combinedValues.push('__SELECT_ALL__');
              } else {
                if (metadata.hasBlanks) {
                  combinedValues.push('__BLANK__');
                }
                if (metadata.regularValues && Array.isArray(metadata.regularValues)) {
                  combinedValues.push(...metadata.regularValues);
                }
              }
              
              if (combinedValues.length > 0) {
                selections[f.fieldConfigurationId] = combinedValues;
              }
              return;
            } catch (error) {
              console.warn('Failed to parse filter metadata, falling back to regex parsing:', error);
            }
          }
          
          // Fallback to regex parsing for legacy filters
          const searchColumn = getSearchColumnPath(field);
          const filter = f.filter;
          
          // Check for Select All pattern: "ColumnName ne null and ColumnName ne ''"
          const selectAllPattern = new RegExp(`${searchColumn.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} ne null and ${searchColumn.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} ne ''`);
          if (selectAllPattern.test(filter)) {
            selections[f.fieldConfigurationId] = ['__SELECT_ALL__'];
            return;
          }
          
          // Check for Blank pattern: "ColumnName eq null or ColumnName eq ''"
          const blankPattern = new RegExp(`${searchColumn.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} eq null or ${searchColumn.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} eq ''`);
          const hasBlank = blankPattern.test(filter);
          
          // Extract regular values from the filter
          const regularMatches = Array.from(filter.matchAll(/eq '([^']+)'/g));
          const regularValues = regularMatches.map(m => m[1]);
          
          // Combine blank and regular values
          const combinedValues = [];
          if (hasBlank) {
            combinedValues.push('__BLANK__');
          }
          combinedValues.push(...regularValues);
          
          if (combinedValues.length > 0) {
            selections[f.fieldConfigurationId] = combinedValues;
          }
        });
        setGlobalFilterSelections(selections);
      }
    }
  }, [isGlobalFilterSidebarOpen, facetsFetched, configuration, assignedUsers, globalFilters]);

  // Restore drawerVisible effect
  useEffect(() => {
    if (isGlobalFilterSidebarOpen) {
      setDrawerVisible(true);
    } else if (drawerVisible) {
      const timeout = setTimeout(() => setDrawerVisible(false), 300);
      return () => clearTimeout(timeout);
    }
  }, [isGlobalFilterSidebarOpen]);

  // Handler for show more
  function handleShowMore(fieldId: string, total: number) {
    // Track show more action
    trackFilterAction('modify', 'show_more_options', { fieldId, currentCount: globalFilterShowCount[fieldId] || 3, total });
    
    setGlobalFilterShowCount(prev => ({
      ...prev,
      [fieldId]: Math.min((prev[fieldId] || 3) + 10, total)
    }));
  }

  // Handler for checkbox change
  function handleGlobalCheckboxChange(fieldId: string, value: string) {
    setGlobalFilterSelections(prev => {
      const prevSelected = prev[fieldId] || [];
      
      // Handle special values
      if (value === '__SELECT_ALL__') {
        // Select all: get all available options for this field
        const field = configuration?.fieldsInUse.find(f => f.fieldConfigurationId === fieldId);
        if (field) {
          const facetOptions = getFacetOptionsForColumnSidebar(facets, field);
          const allOptionValues = [
            ...facetOptions.map((opt: { value: string }) => opt.value),
            '__BLANK__'
          ];
          const isCurrentlySelected = prevSelected.length === allOptionValues.length && 
                                   allOptionValues.every(val => prevSelected.includes(val));
          // If Select All is currently selected, unselect all items
          // If Select All is not selected, select all items
          return { ...prev, [fieldId]: isCurrentlySelected ? [] : allOptionValues };
        }
        return { ...prev, [fieldId]: [] };
      } else if (value === '__BLANK__') {
        // (blank): can be combined with other selections
        const next = prevSelected.includes(value)
          ? prevSelected.filter(v => v !== value)
          : [...prevSelected, value];
        return { ...prev, [fieldId]: next };
      } else {
        // Regular value: can be combined with other selections
        const next = prevSelected.includes(value)
          ? prevSelected.filter(v => v !== value)
          : [...prevSelected, value];
        return { ...prev, [fieldId]: next };
      }
    });
  }

  // Handler for Apply
  function handleGlobalApply() {
    // Track global filter apply action
    trackFilterAction('apply', 'global_filters', { filterCount: Object.keys(globalFilterSelections).length });
    
    const newGlobalFilters = Object.entries(globalFilterSelections)
      .filter(([_, values]) => values.length > 0)
      .map(([fieldId, values]) => {
        const field = configuration?.fieldsInUse.find(f => f.fieldConfigurationId === fieldId);
        if (!field) return null;
        let filterString = '';
        // Handle special cases
        const hasSelectAll = values.includes('__SELECT_ALL__');
        const hasBlank = values.includes('__BLANK__');
        const regularValues = values.filter(v => v !== '__SELECT_ALL__' && v !== '__BLANK__');

        // Shared canonical builder (handles __SELECT_ALL__ / __BLANK__ sentinels and
        // all field types) — same OData as the popovers / drawer / view designer.
        filterString = buildFacetedValuesFilter(field, values);
        
        if (filterString) {
          // The Stage prefix only applies to WorkOrder/Assignment rows — VMS
          // docs don't carry a Stage column. If we leave the Stage clause in
          // for VMS, triggerDataFetch's "strip filters containing Stage"
          // safety net (see workflow-grid.tsx::triggerDataFetch) drops the
          // user's entire global filter on the floor as a side-effect.
          // Result: applying a Client / Worker / etc. global filter on the
          // VMS grid silently filtered nothing. Branch the prefix so VMS
          // gets just the IsDeleted scoping it actually needs.
          const stagePrefix = isRequestEntityType
            ? 'IsDeleted eq false'
            : `Stage ne 'Discarded' and IsDeleted eq false`;
          filterString = `(${stagePrefix}) and (${filterString})`;
        }
        
        // Create metadata for reapplication
        const metadata = {
          isSelectAll: hasSelectAll,
          hasBlanks: hasBlank,
          regularValues: regularValues
        };
        
        return { 
          fieldConfigurationId: fieldId, 
          filter: filterString,
          values: JSON.stringify(metadata)
        };
      })
              .filter(Boolean) as { fieldConfigurationId: string, filter: string, values?: string }[];
    // --- Prevent unnecessary global filter update if unchanged ---
    const isUnchanged = JSON.stringify(newGlobalFilters) === JSON.stringify(globalFilters);
    if (isUnchanged) {
      setIsGlobalFilterSidebarOpen(false);
      setGlobalFilterShowCount({});
      return; // Do not update or reload if unchanged
    }
    // --- End check ---
    setGlobalFilters(newGlobalFilters);
    setCurrentPage(1); // Reset to first page when applying global filters
    setIsGlobalFilterSidebarOpen(false);
    setGlobalFilterShowCount({});
  }

  // Handler for Clear all
  function handleGlobalClearAll() {
    // Track global filter clear action
    trackFilterAction('clear', 'global_filters', { filterCount: Object.keys(globalFilterSelections).length });
    
    setGlobalFilterSelections({});
    setGlobalFilters([]);
    setGlobalFilterShowCount({});
  }

  // Helper to get filtered global filter fields and options (use globalFacets)
  function getFilteredGlobalFilterFields(): { field: any; options: { label: string; value: string; count: number }[] }[] {
    if (!Array.isArray(configuration?.globalFilterIds) || configuration.globalFilterIds.length === 0) return [];
    const search = globalFilterSearch.trim().toLowerCase();
    return configuration.globalFilterIds
      .map(fid => {
        const field = configuration.fieldsInUse.find(f => f.fieldConfigurationId === fid);
        if (!field) return undefined;
        const facetOptions = getFacetOptionsForColumn(facets, field);
        if (!facetOptions || facetOptions.length === 0) return undefined;
        // Map options with display label
        const options = facetOptions.map((opt: { value: string; count: number }) => {
          let label = resolveSidebarOptionLabel(opt, field);
          return { ...opt, label };
        });
        // If searching, filter options by label or value
        const filteredOptions = search
          ? options.filter(opt => {
              const matchesLabel = opt.label.toLowerCase().includes(search);
              const matchesValue = opt.value && opt.value.toLowerCase().includes(search);
              
              // For AssignedToUser, only search by label (display name), not email
              const shouldInclude = field.columnCode === 'AssignedToUser' 
                ? matchesLabel 
                : (matchesLabel || matchesValue);
              
              return shouldInclude;
            })
          : options;
        
        // Always include the field if it has selected special values, even if no regular options match the search
        const selected = globalFilterSelections[fid] || [];
        const hasSpecialSelections = selected.includes('__SELECT_ALL__') || selected.includes('__BLANK__');
        
        if (filteredOptions.length === 0 && !hasSpecialSelections) return undefined;
        return { field, options: filteredOptions };
      })
      .filter((x): x is { field: any; options: { label: string; value: string; count: number }[] } => !!x);
  }

  // Use context-provided stageFilters, globalFilters, setStageFilters, setGlobalFilters, clearAllFilters
  // Remove any local state for stageFilters/globalFilters and their updaters
  // Remove or replace any references to setGlobalFilters(prev => ...) with setGlobalFilters([...]) or similar, using the correct type

  // Helper to get facet options for a column, now using globalFacets for sidebar
  function getFacetOptionsForColumnSidebar(facets: any, column: any): { value: string; count: number }[] {
    if (!facets || !column) return [];
    
    const searchPath = getSearchColumnPath(column);
    
    // 1. Direct match with search path
    if (Array.isArray(facets[searchPath]) && facets[searchPath].length > 0) {
      return facets[searchPath].filter((opt: { value: string; count: number }) => opt.value !== "");
    }
    // 2. Direct match with column code
    if (Array.isArray(facets[column.columnCode]) && facets[column.columnCode].length > 0) {
      return facets[column.columnCode].filter((opt: { value: string; count: number }) => opt.value !== "");
    }
    // 3. Case-insensitive match
    const foundKey = Object.keys(facets).find(
      k => k.toLowerCase() === searchPath.toLowerCase()
    );
    if (foundKey && Array.isArray(facets[foundKey])) {
      return facets[foundKey].filter((opt: { value: string; count: number }) => opt.value !== "");
    }
    // 4. Try replacing / with nothing (for edge cases)
    const altKey = searchPath.replace(/\//g, '');
    const foundAlt = Object.keys(facets).find(
      k => k.replace(/\//g, '').toLowerCase() === altKey.toLowerCase()
    );
    if (foundAlt && Array.isArray(facets[foundAlt])) {
      return facets[foundAlt].filter((opt: { value: string; count: number }) => opt.value !== "");
    }
    return [];
  }

  // Use workOrdersFacets from useWorkflow() context directly
  const stageFacets = useMemo(() => workOrdersFacets || {}, [workOrdersFacets]);

  // Sort assignedUsers by fullName (A-Z, case-insensitive)
  const sortedAssignedUsers = Array.isArray(assignedUsers)
    ? [...assignedUsers].sort((a, b) =>
        (a.fullName || '').toLowerCase().localeCompare((b.fullName || '').toLowerCase())
      )
    : [];

  // Resolve a facet option's display label for the global filter sidebar.
  // CustomValues (Code→Title) and AssignedToUser are handled by getOptionLabel;
  // CodeValueSource fields are resolved here via the code-values hook, which the
  // module-level getOptionLabel can't reach. This mirrors the column-filter
  // popover, which switches its option source to code values for CodeValueSource
  // columns — so the sidebar and popover show identical friendly labels.
  const resolveSidebarOptionLabel = (opt: any, field: any): string => {
    if (field.codeValueSource && codeValues && codeValues.isReady) {
      const txt = codeValues.getCodeValueTextByCode(getOptionValue(opt), field.codeValueSource);
      if (txt && txt.trim() !== '') return txt;
    }
    return getOptionLabel(opt, field, sortedAssignedUsers, language);
  };

  const getFilterOptionsForColumn = (column: any) => {
    const isBoolean = column.columnTypeCode?.toLowerCase() === 'boolean';
    const hasCustomValues = Array.isArray(column.customValues) && column.customValues.length > 0;
    const hasCodeValueSource = column.codeValueSource && codeValues && codeValues.isReady;

    // If column has CodeValueSource, use code values from the hook
    if (hasCodeValueSource) {
      try {
        const codeValuesFromHook = codeValues.getCodeValues(column.codeValueSource, false);
        if (codeValuesFromHook && Array.isArray(codeValuesFromHook)) {
          return codeValuesFromHook
            .filter((item: any) => 
              item && 
              item.code && 
              item.code.trim() !== '' && 
              item.text && 
              item.text.trim() !== ''
            )
            .map((item: any) => ({
              code: item.code,
              titleEnglish: item.text,
              titleFrench: item.text,
              color: item.Icon || '',
              isDeleted: false
            }));
        }
      } catch (error) {
        console.warn('Failed to get code values from hook:', error);
      }
    }

    // Otherwise use existing logic
    if (isBoolean) {
      return hasCustomValues ? column.customValues : defaultBooleanOptions;
    }
    return hasCustomValues ? column.customValues : [];
  };

  // ── Applied-filter pills (toolbar strip + Filter sidebar section) ──
  // Defined below resolveSidebarOptionLabel because the pill previews resolve
  // their value labels through it at memo-compute time.

  // Map a persisted filter code to its display label — the same resolution the
  // column popover and sidebar options use (code values, custom values,
  // assigned users), plus a Yes/No fallback for booleans without custom values.
  const resolvePillValueLabel = (field: any, value: string): string => {
    if (!field) return value;
    const label = resolveSidebarOptionLabel({ value }, field);
    if (label && label !== value) return label;
    if (isBooleanField(field)) {
      if (value === 'true') return t('yes') || 'Yes';
      if (value === 'false') return t('no') || 'No';
    }
    return value;
  };

  // Click-to-edit, mirroring the view designer's pills: a global pill opens
  // the Filter sidebar scrolled to that field's section; a stage pill scrolls
  // its column into view (with the jump-to-column flash) and opens the same
  // funnel popover the header icon does.
  const editGlobalFilterFromPill = (fieldConfigurationId: string) => {
    setIsGlobalFilterSidebarOpen(true);
    // Wait out the drawer's slide-in transition before scrolling — the panel
    // is mounted but translated off-screen until the open state lands.
    window.setTimeout(() => {
      document.querySelector(`[data-global-filter-section="${fieldConfigurationId}"]`)
        ?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, 350);
  };

  const editStageFilterFromPill = (fieldConfigurationId: string) => {
    const col = columns.find(c => c.fieldConfigurationId === fieldConfigurationId);
    if (!col) return;
    scrollToColumn({
      fieldConfigurationId,
      displayName: getLocalizedField(col, 'columnName', language) || col.columnCode,
      isHidden: false,
    });
    openColumnFilterPopover(col);
  };

  const appliedFilterPills = useMemo<AppliedFilterPill[]>(() => {
    // Mirror triggerDataFetch's applicability rule: fixed-filter stages drop
    // global filters entirely, so don't advertise pills that aren't applied.
    const applicableGlobal = (stageConfig?.fixedFilter || stageConfig?.excludeGlobalFilters) ? [] : globalFilters;
    const fieldById = (fieldConfigurationId: string) =>
      columns.find(c => c.fieldConfigurationId === fieldConfigurationId)
        || configuration?.fieldsInUse?.find((f: any) => f.fieldConfigurationId === fieldConfigurationId);
    const globalPills = applicableGlobal.map(f => {
      const field = fieldById(f.fieldConfigurationId);
      const { text, overflow } = getFilterValuesPreview(f.values, v => resolvePillValueLabel(field, v));
      return {
        key: `global-${f.fieldConfigurationId}`,
        scope: 'global' as const,
        label: resolveFieldLabel(f.fieldConfigurationId),
        preview: text,
        overflow,
        onRemove: () => removeGlobalFilter(f.fieldConfigurationId),
        onClick: () => editGlobalFilterFromPill(f.fieldConfigurationId),
      };
    });
    // Mirror triggerDataFetch's pause rule: while a "Showing: <row>" scope is
    // active on a Requests tab, the stage's column filters are excluded from
    // the query — so don't advertise pills for filters that aren't applied.
    // They reappear (still saved, untouched) the moment the pill is dismissed.
    const pausedByRowScope = !!temporaryQueryFilter && (isRequestEntityType || !!temporaryQueryFilterPausesStageFilters);
    const stagePills = (pausedByRowScope ? [] : currentStageFilters).map(f => {
      const field = fieldById(f.fieldConfigurationId);
      const { text, overflow } = getFilterValuesPreview(f.values, v => resolvePillValueLabel(field, v));
      // Click-to-edit needs the column's funnel popover, which only exists for
      // displayed columns — filters on hidden columns render non-clickable.
      const isDisplayed = columns.some(c => c.fieldConfigurationId === f.fieldConfigurationId);
      return {
        key: `stage-${f.fieldConfigurationId}`,
        scope: 'stage' as const,
        label: resolveFieldLabel(f.fieldConfigurationId),
        preview: text,
        overflow,
        onRemove: () => removeStageFilter(f.fieldConfigurationId),
        onClick: isDisplayed ? () => editStageFilterFromPill(f.fieldConfigurationId) : undefined,
      };
    });
    // Cross-tab navigation filter ("Showing: <row>" — VmsEmail → linked
    // request) leads the strip as a green pill so all active narrowing reads
    // as one family. Dismissing it must also kill the pending-search stash
    // (dismissTemporaryFilter), or the handoff effect would revive it on the
    // next render. Not clickable — it isn't a column filter.
    const temporaryPills: AppliedFilterPill[] = temporaryQueryFilter ? [{
      key: 'temporary-filter',
      scope: 'temporary' as const,
      label: t('showing') || 'Showing',
      preview: temporaryQueryFilterLabel || temporaryQueryFilter,
      overflow: 0,
      onRemove: dismissTemporaryFilter,
    }] : [];
    return [...temporaryPills, ...globalPills, ...stagePills];
    // codeValues readiness and assigned users are label inputs — without them
    // as deps the pills would keep showing raw codes after those sources load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFilters, currentStageFilters, stageConfig?.fixedFilter, stageConfig?.excludeGlobalFilters, configuration, columns, language, temporaryQueryFilter, temporaryQueryFilterLabel, temporaryQueryFilterPausesStageFilters, t, codeValues?.isReady, sortedAssignedUsers]);

  // Section headings for the expanded pill lists ("+N" popover + Filter
  // sidebar): color already separates the scopes, the headings make it
  // explicit — "Global Filters" vs "<Stage> stage filters".
  const pillGroupLabels = useMemo(() => {
    const stageTitle = (stageConfig ? getLocalizedField(stageConfig as any, 'title', language) : '') || stage || '';
    return {
      global: t('globalFilters') || 'Global Filters',
      stage: (t('stageFiltersHeading') || '{stage} stage filters').replace('{stage}', stageTitle),
    };
  }, [t, stageConfig, stage, language]);

  // Message sidebar state is now managed by MessageTrigger component callbacks

  // Component for searchable fields with suggestions
  const SearchableFieldWithSuggestions = ({ column }: { column: any }) => {
    const currentValue = popoverFilterValues[column.columnCode] || { blank: false, search: '' };
    const currentSearchText = typeof currentValue === 'object' ? (currentValue.search || '') : (typeof currentValue === 'string' ? currentValue : '');
    const currentBlankState = typeof currentValue === 'object' ? (currentValue.blank || false) : false;

    // Use ref-based draft to prevent parent re-render on every keystroke
    const initialDraft = (searchableDraftRef.current[column.columnCode] ?? currentSearchText)?.slice(0, 100) || '';
    const [inputValue, setInputValue] = useState(initialDraft);
    const [isBlankChecked, setIsBlankChecked] = useState(currentBlankState);

    // Suggestions state is local to this component to avoid parent re-renders that cause focus loss.
    // Initialize from the parent-level cache so popover remounts don't lose results.
    const cachedEntry = suggestionsCacheRef.current[column.columnCode];
    const initialSuggestions = cachedEntry && cachedEntry.query === initialDraft.trim()
      ? cachedEntry.suggestions
      : [];
    const [suggestionsList, setSuggestionsList] = useState<any[]>(initialSuggestions);
    const [isLoading, setIsLoading] = useState(false);
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Check if this field should have suggestions
    const shouldShowSuggestions = [
      "WorkerFullName",
      "WorkerEmail",
      "RecruiterFullName",
      "RecruiterEmail",
      "ManagerFullName",
      "ManagerEmail",
      "PrimaryJobOwnerFullName",
      "PrimaryJobOwnerEmail"
    ].includes(column.columnCode);

    const requestIdRef = useRef(0);

    // Cleanup debounce timeout on unmount
    useEffect(() => {
      return () => {
        if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
        requestIdRef.current++; // invalidate any in-flight request
      };
    }, []);

    const fetchSuggestions = async (searchText: string) => {
      const trimmed = searchText.trim();
      if (!trimmed || trimmed.length < 2) {
        // Invalidate any in-flight request so its late response doesn't repopulate
        // the list after we've cleared it, and drop the loading state too.
        requestIdRef.current++;
        setIsLoading(false);
        setSuggestionsList([]);
        delete suggestionsCacheRef.current[column.columnCode];
        return;
      }

      const currentRequestId = ++requestIdRef.current;
      setIsLoading(true);
      try {
        const response = await fetchSearchSuggestions(trimmed, column.columnCode);
        if (currentRequestId !== requestIdRef.current) return; // stale response
        let suggestionsArray: any[] = [];
        if (Array.isArray(response)) {
          suggestionsArray = response;
        } else if (response && response.value && Array.isArray(response.value)) {
          suggestionsArray = response.value;
        }
        const normalized = suggestionsArray.map(s => typeof s === 'string' ? s : (s.text || s));
        setSuggestionsList(normalized);
        suggestionsCacheRef.current[column.columnCode] = { query: trimmed, suggestions: normalized };
      } catch (error) {
        if (currentRequestId !== requestIdRef.current) return; // stale error
        console.error('Failed to fetch suggestions:', error);
        setSuggestionsList([]);
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setInputValue(value);
      // Keep drafts in a ref to prevent parent re-render/caret jump
      searchableDraftRef.current[column.columnCode] = value;
      if (shouldShowSuggestions) {
        if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = setTimeout(() => fetchSuggestions(value), 1000);
      }
    };

    const handleBlankChange = (checked: boolean) => {
      setIsBlankChecked(checked);
    };

    const handleSuggestionClick = (suggestion: any) => {
      const value = (typeof suggestion === 'string' ? suggestion : (suggestion.text || suggestion))?.slice(0, 100) || '';
      
      // Track suggestion selection
      trackFilterAction('apply', 'suggestion_selection', { columnCode: column.columnCode, suggestionValue: value });
      
      setSelectedFromSuggestions(prev => ({ ...prev, [column.columnCode]: true }));
      
      // Clear the draft ref since we're applying the filter
      searchableDraftRef.current[column.columnCode] = value;
      
      // Apply the filter immediately with the selected value
      const searchColumn = getSearchColumnPath(column);
      
      // For TextList fields, use collection empty check
      const blankCondition = column?.columnTypeCode?.toLowerCase() === 'textlist' 
        ? `not ${column.columnCode}/any()`
        : `${searchColumn} eq null or ${searchColumn} eq ''`;
      
      let filterString = '';
      if (isBlankChecked) {
        // Combine blank with search - wrap entire expression in parentheses for proper grouping
        const searchCondition = `search.ismatch('${value.replace(/'/g, "''")}', '${searchColumn}', 'full', 'all')`;
        filterString = `((${blankCondition}) or (${searchCondition}))`;
      } else {
        filterString = `search.ismatch('${value.replace(/'/g, "''")}', '${searchColumn}', 'full', 'all')`;
      }
      
      updateStageFilter(column.fieldConfigurationId, filterString, { blank: isBlankChecked, search: value });
      setCurrentPage(1);
      
      // Close the popover
      setFilterAnchor(null);
      setActiveFilterColumn(null);
    };

    // Handle manual search (similar to suggestions but without selectedFromSuggestions flag)
    const handleManualSearch = () => {
      const value = inputValue.trim().slice(0, 100);
      if (!value && !isBlankChecked) {
        // If both are empty, clear the filter
        handleClearColumnFilter(column);
        return;
      }
      
      // Update the draft ref with the current value
      searchableDraftRef.current[column.columnCode] = value;
      
      // Apply the filter immediately - BYPASS handlePopoverClose()
      const searchColumn = getSearchColumnPath(column);
      
      // For TextList fields, use collection empty check
      const blankCondition = column?.columnTypeCode?.toLowerCase() === 'textlist' 
        ? `not ${column.columnCode}/any()`
        : `${searchColumn} eq null or ${searchColumn} eq ''`;
      
      let filterString = '';
      if (isBlankChecked && value) {
        // Both blank and search - wrap entire expression in parentheses for proper grouping
        const searchCondition = buildSearchableFilter(value, searchColumn);
        filterString = `((${blankCondition}) or (${searchCondition}))`;
      } else if (isBlankChecked) {
        // Only blank
        filterString = `(${blankCondition})`;
      } else if (value) {
        // Only search
        filterString = buildSearchableFilter(value, searchColumn);
      }
      
      updateStageFilter(column.fieldConfigurationId, filterString, { blank: isBlankChecked, search: value });
      setCurrentPage(1);
      
      // Close the popover
      setFilterAnchor(null);
      setActiveFilterColumn(null);
    };


    return (
      <div className="relative">
        <Input
          ref={inputRef}
          autoFocus
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={e => {
            if (e.key === 'Enter' && inputValue.length < 100) {
              handleManualSearch();
            }
          }}
          maxLength={100}
          placeholder={`${t('typeToFilter')}`}
          onClick={e => e.stopPropagation()}
          className="focus:border-gray-300 focus:outline-none"
        />
        {inputValue.length >= 100 && (
          <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>
            {t('searchCharLimit')}
          </div>
        )}
        
        {/* (blank) checkbox option */}
        <label 
          className="flex items-center gap-2 cursor-pointer mt-3" 
          onClick={e => e.stopPropagation()}
        >
          <Checkbox
            checked={isBlankChecked}
            onCheckedChange={handleBlankChange}
          />
          <span style={{ fontStyle: 'italic', color: '#666' }}>{t('blank')}</span>
        </label>
        
        {/* Inline suggestions - similar to other filter types */}
        {shouldShowSuggestions && inputValue.trim().length >= 2 && (
          <div className="mt-3 space-y-2">
            {isLoading ? (
              <div className="px-3 py-2 text-sm text-gray-500">
                {t('loading')}...
              </div>
            ) : suggestionsList.length > 0 ? (
              <div className="space-y-0.5">
                <div className="text-xs text-gray-500 font-medium mb-1">{t('suggestions')}</div>
                {suggestionsList.slice(0, 5).map((suggestion, index) => (
                  <button
                    key={index}
                    className="w-full text-left px-2 py-1 text-sm hover:bg-blue-50 rounded transition-colors"
                    onClick={() => handleSuggestionClick(suggestion)}
                    type="button"
                  >
                    {typeof suggestion === 'string' ? suggestion : (suggestion.text || suggestion)}
                  </button>
                ))}
              </div>
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500">
                {t('noSuggestions')}
              </div>
            )}
          </div>
        )}
        
        <div style={{ borderTop: '1px solid #eee', paddingTop: 8, background: '#fff' }}>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={inputValue.length >= 100} onClick={e => {
              e.stopPropagation();
              // Always call handleManualSearch - it will handle clearing if both are empty
              handleManualSearch();
            }} style={{ flex: 1 }}>
              {t('apply')}
            </Button>
            <Button variant="destructive" size="sm" onClick={e => {
              e.stopPropagation();
              handleClearColumnFilter(column);
            }} style={{ flex: 1 }}>
              {t('clearAll')}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const authorizedActions = (configuration?.authorizedActions || []).map((a: any) => typeof a === 'string' ? a : a.actionCode)
  const hasManageViewsPermission = authorizedActions.includes("ManageViews")
  const canMoveColumnOnUserView = authorizedActions.includes("MoveColumnOnUserViewState")
  const canEditFieldData = authorizedActions.includes("EditFieldData")
  // ExportData gates the workflow-hub Excel export feature. Mirrors the BE
  // gate at POST /api/workflow/v2/export — the button hides without it,
  // and the endpoint additionally rejects a hand-crafted request.
  const hasExportData = authorizedActions.includes("ExportData")
  const canViewNavigationLinks = authorizedActions.includes("ViewNavigationLinks")
  const showCheckbox = authorizedActions.includes("EnableCheckboxColumn")
  // When checkboxes aren't enabled, drop the column entirely for Requests-group entities
  // (ClientRequest/VMS/ATS never expand, so there's no chevron to keep) — don't leave an
  // empty spacer column. WorkOrder keeps the narrow chevron column for row expansion.
  // Previously only the Onboarding stage hid it, so other stages showed an empty column.
  const hideCheckboxColumn = !showCheckbox && (stage?.toLowerCase() === 'onboarding' || isRequestEntityType)
  const checkboxColWidth = hideCheckboxColumn ? 0 : (showCheckbox ? CHECKBOX_COLUMN_WIDTH : CHEVRON_ONLY_WIDTH);
  checkboxColWidthRef.current = checkboxColWidth;
  // Width of the Actions/kebab column for the sticky-offset math below. 0 whenever the column
  // is hidden (clients, or EnableActionsColumn not granted) so the Message column doesn't leave
  // a 50px gap where the kebab would have been.
  const actionsColumnWidth = showActionsColumn ? FIXED_ACTION_COLUMN_WIDTH : 0;

  // Column drag-and-drop reordering
  const canReorderColumns = hasManageViewsPermission || canMoveColumnOnUserView;
  const columnDndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  // IDs of all columns for SortableContext (fixed columns are passed as disabled to SortableTableHead)
  const sortableColumnIds = useMemo(() => {
    return displayColumns.map(col => col.fieldConfigurationId);
  }, [displayColumns]);

  const handleColumnDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !stageConfig || !configuration) return;

    const oldIndex = displayColumns.findIndex(c => c.fieldConfigurationId === active.id);
    const newIndex = displayColumns.findIndex(c => c.fieldConfigurationId === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    // Don't allow dropping onto fixed columns
    const targetCol = displayColumns[newIndex];
    if (targetCol?.isStaticPosition) return;

    // Build new order: only reorder among regular (non-fixed) columns
    const fixedCols = displayColumns.filter(c => c.isStaticPosition);
    const regularCols = displayColumns.filter(c => !c.isStaticPosition);
    const oldRegIdx = regularCols.findIndex(c => c.fieldConfigurationId === active.id);
    const newRegIdx = regularCols.findIndex(c => c.fieldConfigurationId === over.id);
    if (oldRegIdx < 0 || newRegIdx < 0) return;

    const reorderedRegular = arrayMove(regularCols, oldRegIdx, newRegIdx);
    const newFieldOrder = [...fixedCols, ...reorderedRegular].map(c => c.fieldConfigurationId);

    // Optimistic UI update
    const orderedFields = newFieldOrder
      .map(id => configuration.fieldsInUse.find(f => f.fieldConfigurationId === id))
      .filter((f): f is typeof configuration.fieldsInUse[0] => f !== undefined);
    updateVisibleColumns(orderedFields);

    // Build save payload (same shape as edit-columns handleApply).
    // Include the per-stage display preferences (pin / sort / show-completed)
    // on EVERY stage entry: the backend treats this endpoint as a full
    // replacement of stageConfigurations, so any field we omit gets reset to
    // its default. Without these the column reorder would silently clobber
    // the user's pinned column / sort / Show-Completed toggle server-side,
    // even though the client-side reducer preserves them in-memory.
    try {
      const stageConfigurations = configuration.stageConfigurations.map(s => {
        const fieldIds = s.stageCode.toLowerCase() === stage?.toLowerCase()
          ? newFieldOrder.filter(id => id !== 'actions-column')
          : s.fieldConfigurations.map(f => f.fieldConfigurationId);
        return {
          stageConfigurationId: s.stageId,
          fieldConfigurationIds: fieldIds,
          pinnedColumnId: (state.pinnedColumnsByStage || {})[s.stageId] || null,
          sortColumn: (state.sortByStage || {})[s.stageId]?.columnCode || null,
          sortDirection: (state.sortByStage || {})[s.stageId]?.direction || null,
          showCompleted: (showCompletedByStage || {})[s.stageId] ?? false,
        };
      });

      const serializedStageFilters = stageFilters.map((filter: any) => ({
        ...filter,
        values: filter.values ? JSON.stringify(filter.values) : undefined,
      }));

      // The fixed-width icon columns are excluded: persisting them would let a value saved before
      // they were pinned outlive the pinning, and it would travel with the view to other users.
      const fixedWidthColumnIds = new Set<string>(['actions-column']);
      if (messageColumn?.fieldConfigurationId) fixedWidthColumnIds.add(messageColumn.fieldConfigurationId);

      const persistableWidths = Object.fromEntries(
        Object.entries(state.columnWidths || {}).filter(([id]) => !fixedWidthColumnIds.has(id))
      );

      const columnWidthsToSave = Object.keys(persistableWidths).length > 0
        ? JSON.stringify(persistableWidths)
        : JSON.stringify({});

      // Build visible view IDs from viewsResponse
      const visibleViewIds: string[] = [];
      if (viewsResponse) {
        if (viewsResponse.defaultView && viewsResponse.defaultView.isVisible !== false) visibleViewIds.push(viewsResponse.defaultView.viewSettingsId);
        viewsResponse.userViews?.forEach((v: any) => { if (v.isVisible !== false) visibleViewIds.push(v.viewSettingsId); });
        viewsResponse.sharedViews?.forEach((v: any) => { if (v.isVisible !== false) visibleViewIds.push(v.viewSettingsId); });
        viewsResponse.publicViews?.forEach((v: any) => { if (v.isVisible !== false) visibleViewIds.push(v.viewSettingsId); });
      }

      const request = {
        id: currentView?.viewSettingsId,
        title: currentView?.title,
        isPublic: currentView?.isPublic ?? false,
        isVisible: currentView?.isVisible ?? true,
        isDefault: currentView?.isDefault ?? false,
        stageConfigurations,
        stageFilters: serializedStageFilters,
        globalFilters: globalFilters.map(filter => ({
          fieldConfigurationId: filter.fieldConfigurationId,
          filter: filter.filter,
          values: typeof filter.values === 'string' ? filter.values : (filter.values ? JSON.stringify(filter.values) : undefined),
        })),
        visibleViewIds,
        columnWidths: columnWidthsToSave,
        itemsPerPage: state.itemsPerPage,
        isUserViewStateUpdate: true,
        updateFilters: false,
      };

      await saveViewState(request);
      setUnsavedChanges(true);
      await refreshConfiguration(true, null, true);
    } catch (error) {
      console.error('Failed to save column order:', error);
      // Revert by re-fetching configuration
      await refreshConfiguration(true, null, true);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden"
      style={{
        transition: 'margin-right 0.3s cubic-bezier(0.4,0,0.2,1)',
        marginRight: isGlobalFilterSidebarOpen ? FILTER_DRAWER_WIDTH : 0,
      }}
    >
      <GridHeader
        ref={headerRef}
        stage={stage as WorkflowStage}
        stageConfig={stageConfig}
        language={language}
        totalRecords={totalCount}
        selectedCount={selectedItems.length}
        hasActiveFilters={hasActiveFilters}
        canMarkMessages={messageActionsAvailable && canMarkMessages(selectedItems)}
        isMarkingMessages={isMarkingMessages}
        onMarkMessages={(read) => void markMessages(selectedItems, read)}
        isMutingMessages={isMutingMessages}
        onMuteMessages={(subscribed) => void muteMessages(selectedItems, subscribed)}
        onEditColumns={() => setIsEditColumnsOpen(true)}
        onToggleFilter={() => setIsGlobalFilterSidebarOpen(true)}
        filterCount={appliedFilterPills.length}
        isFilterActive={isGlobalFilterSidebarOpen}
        onClearFilters={handleClearAllFilters}
        onResetToCurrentView={handleResetToCurrentView}
        onResetToDefault={viewsResponse?.defaultView ? handleResetToDefault : undefined}
        currentViewName={currentView?.title}
        isDefaultView={currentView?.viewSettingsId === viewsResponse?.defaultView?.viewSettingsId}
        onDeselectAll={deselectAllItems}
        showFilterButton={true}
        isLoadingHeader={(isLoadingWorkOrders && !isJustPageChange.current) || isLoadingConfiguration || !isHeightCalculated}
        onBulkEdit={() => setIsBulkEditOpen(true)}
        onMoveTo={() => setIsMoveToOpen(true)}
        showPinDock={hidePinDock ? undefined : true}
        onPinDockDrop={() => {
          // Drop to dock = unpin for current stage
          const currentStageConfig = stages.find(s => s.stageCode.toLowerCase() === (stage || '').toLowerCase());
          if (!currentStageConfig) return;
          setPinnedColumn(null);
        }}
        onPinDockClick={() => {
          // If no pinned column, clicking dock does nothing for now
        }}
        showEditColumnsButton={hasManageViewsPermission || canMoveColumnOnUserView}
        // Two-key gate for the Export button: the operator must hold the
        // ExportData authorized action (capability) AND the active view must
        // opt in via allowDataExport (curation). The view editor surfaces the
        // toggle in the View Properties panel; defaults to false so views
        // existing before this rolled out stay non-exportable until an admin
        // explicitly turns export on for them. Both halves must agree for the
        // button to render — see BE POST /api/workflow/v2/export which also
        // gates on ExportData independently as a backstop against a hand-
        // crafted request.
        showExportButton={hasExportData && (currentView?.allowDataExport ?? false)}
        onExport={() => setIsExportModalOpen(true)}
        onJumpToColumn={hideJumpToColumn ? undefined : jumpToColumn.open}
        titleSlot={headerTitleSlot}
        extraControls={headerExtraControls}
        leadingControls={headerLeadingControls}
        // Refresh icon next to the title — re-runs the current search
        // (filter / sort / page / facets unchanged). force=true bypasses
        // the fetchKey dedup so a no-op click still hits the index, which
        // is exactly what the user expects from a refresh affordance.
        // Configurations / views / assigned-users are NOT refetched —
        // they're loaded on mount and don't drift between user actions.
        onRefresh={() => triggerDataFetch({ force: true })}
        // Applied-filter pill strip (first pills + "+N" overflow popover) —
        // one visual family for everything narrowing the grid: the sage-green
        // "Showing: <row>" cross-tab navigation pill (global search handoff,
        // VmsEmail → linked VmsRequest), soft-blue global filters, lilac stage
        // filters. Lives inside the header so it visually associates with
        // the totals it's narrowing. The green pill's X dismisses the
        // temporary filter (and its stash); the standard Clear Filters
        // affordance also clears it via handleClearAllFilters.
        filterIndicator={appliedFilterPills.length > 0 ? (
          // maxVisible=1: multi-filter actions (e.g. a global-search row click
          // applies 2-3 stage filters at once) group as "first pill +N" — the
          // +N popover shows the full sectioned list.
          <AppliedFilterPills pills={appliedFilterPills} maxVisible={1} groupLabels={pillGroupLabels} />
        ) : undefined}
        // Show-Completed toggle is rendered natively by the header now
        // (it used to live as a standalone row below). Stage config decides
        // whether the toggle appears; state still flows through the
        // showCompletedByStage map so per-stage prefs persist as before.
        // Toggling materially changes the working set — e.g. turning the
        // VMS toggle off removes RequestAccepted/Canceled rows, which can
        // shrink the result set from 10 pages to 3. Reset to page 1 so the
        // user doesn't end up on an out-of-range page that renders empty
        // (same pattern as filter changes via setStageFilters/setGlobalFilters).
        showCompleted={showCompleted}
        onShowCompletedChange={
          stageConfig?.stageId
            ? (checked) => {
                setCurrentPage(1);
                setShowCompleted(stageConfig.stageId, checked);
              }
            : undefined
        }
      />

      {/* Pending items toggle */}
      {configuration?.showPendingStatusQuickFilter && (
        <div className="flex items-center gap-2 mb-2" style={{ paddingLeft: 2, marginBottom: 20 }}>
          <Switch
            checked={isPendingActive}
            onCheckedChange={(checked) => setPendingFilter(Boolean(checked))}
            aria-label={t('showChangesInProgress')}
            className="data-[state=checked]:bg-[#0F214B] focus-visible:ring-[#0F214B]"
          />
          <span className="text-sm text-gray-700">{t('showChangesInProgress')}</span>
        </div>
      )}

      {/* Show-Completed toggle moved into <GridHeader> — see the
          showCompleted / onShowCompletedChange props on the GridHeader
          instance above. Keeping the comment so future grep-for-the-toggle
          lands on the right spot. */}

      <div ref={mainContainerRef} className="overflow-hidden flex flex-col border rounded-md flex-1" style={{ position: 'relative', background: '#fff', maxHeight: 'calc(100vh)' }}>
        <DndContext sensors={columnDndSensors} collisionDetection={closestCenter} onDragEnd={canReorderColumns ? handleColumnDragEnd : undefined} modifiers={[restrictToHorizontalAxis]}>
        <div ref={tableContainerRef} style={{ width: '100%', position: 'relative', flex: 1, minWidth: '100%', overflow: isMessageSidebarOpen ? 'unset' : 'auto' }}>
          <Table
            className="w-full resizable-table workflow-grid"
            style={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: 14,
              tableLayout: 'fixed',
              borderCollapse: 'separate',
              borderSpacing: 0,
              userSelect: isResizing ? 'none' : 'auto',
              width: (() => {
                const dataColumnsWidth = displayColumns.reduce((total, column) => {
                  return total + getColumnWidth(columnWidths, column);
                }, 0);
                return `${checkboxColWidth + dataColumnsWidth}px`;
              })(),
              minWidth: (() => {
                const dataColumnsWidth = displayColumns.reduce((total, column) => {
                  return total + getColumnWidth(columnWidths, column);
                }, 0);
                return `${checkboxColWidth + dataColumnsWidth}px`;
              })(),
            }}
            ref={(tableRef) => {
              if (tableRef && isResizing) {
                // Force table layout
                if (tableRef.style) {
                  tableRef.style.tableLayout = 'fixed';
                  // V2: Recalculate width during resize (checkbox with chevron + data columns)
                  const dataColumnsWidth = displayColumns.reduce((total, column) => {
                    return total + (getColumnWidth(columnWidths, column));
                  }, 0);
                  tableRef.style.width = `${checkboxColWidthRef.current + dataColumnsWidth}px`;
                }
              }
            }}
          >
            <Header className="sticky top-0" style={{ background: '#F9F9FF', position: 'sticky', top: 0, zIndex: isMessageSidebarOpen ? Z_INDEX.GRID_HEADER_MESSAGE_SIDEBAR_OPEN : Z_INDEX.GRID_HEADER }}>
              <TableRow style={{ background: '#F9F9FF', borderBottom: '1px solid #ECECEC' }}>
                {/* V2: Checkbox column header with space for chevron — hidden entirely for Onboarding when checkboxes are disabled */}
                {!hideCheckboxColumn && <TableHead 
                  className="border-b border-r align-middle whitespace-nowrap font-medium group" 
                  style={{
                    background: isMessageSidebarOpen ? 'rgba(249, 249, 255, 0.6)' : '#F9F9FF', 
                    padding: 0,
                    width: checkboxColWidth,
                    minWidth: checkboxColWidth,
                    maxWidth: checkboxColWidth,
                    position: 'sticky',
                    left: 0,
                    zIndex: isMessageSidebarOpen ? Z_INDEX.FIXED_COLUMN_MESSAGE_SIDEBAR_OPEN : Z_INDEX.FIXED_COLUMN
                  }}
                >
                  <div className="flex items-center h-full">
                    {/* Checkbox area — only rendered when checkbox column is enabled */}
                    {showCheckbox && (
                      <div className="flex items-center justify-end" style={{ width: 56, height: '100%', padding: '12px 10px 12px 4px' }}>
                        <Checkbox
                          checked={isAllSelected ? true : selectedItems.length > 0 ? 'indeterminate' : false}
                          onCheckedChange={() => {
                            if (isAllSelected) {
                              deselectAllItems();
                            } else {
                              selectAllItems();
                            }
                          }}
                          aria-label="Select all rows"
                        />
                      </div>
                    )}
                    {/* Chevron area (header has no chevron — empty spacer) */}
                    <div style={{ width: showCheckbox ? 24 : CHEVRON_ONLY_WIDTH, height: '100%' }} />
                  </div>

                </TableHead>}
                
                {/* Render all dynamic columns */}
                <SortableContext items={sortableColumnIds} strategy={horizontalListSortingStrategy}>
                {displayColumns.map((column, index) => {
                  const showFilterIcon = !!(column.isFacetable || column.isFilterable || column.isSearchable);
                  const isSortable = column.isSortable !== false;
                  const filterOptions = getFilterOptionsForColumn(column);
                  const isFilterableMenu = column.isFilterable && (filterOptions.length > 0 || (column.codeValueSource && codeValues && codeValues.isReady));
                  // Use new helper to determine if this column is filtered for the current stage
                  const filtered = isColumnFiltered(currentStageFilters, column);
                  const columnWidth = getColumnWidth(columnWidths, column);
                  const currentStageConfig = stages.find(s => s.stageCode.toLowerCase() === (stage || '').toLowerCase());
                  const pinnedMap = state.pinnedColumnsByStage || {};
                  const pinnedId = currentStageConfig ? pinnedMap[currentStageConfig.stageId] : undefined;
                  const isSticky = pinnedId ? (displayColumns.findIndex(c => c.fieldConfigurationId === column.fieldConfigurationId) <= displayColumns.findIndex(c => c.fieldConfigurationId === pinnedId)) : false;
                  const isPinned = pinnedId === column.fieldConfigurationId;
                  
                  // V2: Check if this is the Actions or Message column - always make them sticky
                  const isActionsColumn = column.isActionColumn && column.actionType === 'menu';
                  const isMessageColumn = column.isActionColumn && column.actionType === 'message';
                  
                  // V2: Calculate sticky column positions
                  const actionsColumnLeft = checkboxColWidth; // Right after checkbox
                  const messageColumnLeft = actionsColumnLeft + actionsColumnWidth; // Right after Actions
                  const messageColumnWidth = messageColumn ? MESSAGE_COLUMN_WIDTH : 0;
                  const totalStickyWidth = actionsColumnLeft + actionsColumnWidth + (!isMessageColumnHidden && messageColumn ? messageColumnWidth : 0);
                  
                  // V2: Calculate left offset for columns
                  let leftOffset = checkboxColWidth; // Start with checkbox
                  
                  if (isActionsColumn) {
                    leftOffset = actionsColumnLeft;
                  } else if (isMessageColumn) {
                    leftOffset = messageColumnLeft;
                  } else if (isSticky) {
                    // For pinned columns: start after all sticky action columns
                    leftOffset = totalStickyWidth;
                    const currentIndex = displayColumns.findIndex(c => c.fieldConfigurationId === column.fieldConfigurationId);
                    for (let i = 0; i < currentIndex; i++) {
                      const col = displayColumns[i];
                      if (!col.isActionColumn) { // Skip action columns as they're already counted
                        leftOffset += getSafeColumnWidth(columnWidths, col.fieldConfigurationId);
                      }
                    }
                  }
                  
                  const isFixedColumn = !!column.isStaticPosition;
                  const isDraggable = canReorderColumns && !isFixedColumn;

                  return (
                    <SortableTableHead
                      key={column.fieldConfigurationId}
                      id={column.fieldConfigurationId}
                      data-field-configuration-id={column.fieldConfigurationId}
                      disabled={!isDraggable}
                      className="border-b border-r align-middle whitespace-nowrap font-medium group"
                      style={{
                         background: '#F9F9FF',
                        color: '#222',
                        fontWeight: 500,
                        fontSize: 14,
                        padding: '8px 12px',
                        textAlign: 'center',
                        borderBottom: '1px solid #ECECEC',
                        borderRight: '1px solid #ECECEC',
                        borderTopRightRadius: index === columns.length - 1 ? 16 : undefined,
                        position: (isSticky || isActionsColumn || isMessageColumn) ? 'sticky' : 'relative',
                        left: isActionsColumn ? actionsColumnLeft : (isMessageColumn ? messageColumnLeft : (isSticky ? leftOffset : undefined)),
                        zIndex: isActionsColumn ? (isMessageSidebarOpen ? Z_INDEX.FIXED_COLUMN_MESSAGE_SIDEBAR_OPEN : Z_INDEX.FIXED_COLUMN-1) : (isMessageColumn ? (isMessageSidebarOpen ? Z_INDEX.FIXED_COLUMN_MESSAGE_SIDEBAR_OPEN : Z_INDEX.GRID_HEADER-1) : (isSticky ? Z_INDEX.STICKY_COLUMN : (isMessageSidebarOpen ? Z_INDEX.GRID_HEADER_MESSAGE_SIDEBAR_OPEN : Z_INDEX.BASE))),
                        cursor: isSortable ? 'pointer' : 'default',
                        width: columnWidth,
                        minWidth: columnWidth,
                        maxWidth: columnWidth,
                      }}
                      onClick={(e: any) => {
                        if (!isSortable) return;
                        // Don't sort if clicking on resize handle or filter icon
                        if (!(e.target as HTMLElement).closest('.filter-icon-btn') &&
                            !(e.target as HTMLElement).closest('.resize-handle')) {
                          handleSortClick(column.columnCode);
                        }
                      }}
                      onDragOver={(e: any) => {
                        // Always allow drop; we'll validate payload on drop
                        e.preventDefault();
                      }}
                      onDrop={(e: any) => {
                        e.preventDefault();
                        let hasPinPayload = false;
                        try {
                          const data = e.dataTransfer.getData('application/x-workflow-pin');
                          hasPinPayload = !!data;
                        } catch {}
                        if (hasPinPayload) {
                          setPinnedColumn(column.fieldConfigurationId);
                        }
                      }}
                      onContextMenu={(e: any) => handleColumnHeaderRightClick(e, column.fieldConfigurationId)}
                    >
                      <div className="w-full flex items-center" style={{ minHeight: 24 }}
                           onMouseEnter={() => setHoveredHeader(column.columnCode)}
                           onMouseLeave={() => setHoveredHeader(null)}>
                        <div 
                          className="flex items-center justify-center"
                          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, minWidth: 0 }}
                          title={getLocalizedField(column, 'columnName', language)}
                        >
                          {isPinned && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span
                                    className="flex items-center justify-center"
                                    style={{ width: 16, height: 16, marginRight: 4, opacity: 1, transition: 'transform 0.2s ease, opacity 0.2s ease', transform: 'scale(1)', cursor: 'grab' }}
                                    draggable
                                    onDragStart={(e) => {
                                      try { e.dataTransfer.setData('application/x-workflow-pin', column.fieldConfigurationId); } catch {}
                                    }}
                                    onDoubleClick={(e) => { e.stopPropagation(); setPinnedColumn(null); }}
                                    onClick={(e) => { e.stopPropagation(); setPinnedColumn(null); }}
                                  >
                                    <Pin size={16} className="text-blue-600" />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{t('unpinTooltip' as any)}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          
                          {/* Private column indicator */}
                          {column.isPrivate && (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button
                                  type="button"
                                  className="ml-1 p-1 rounded hover:bg-gray-100 transition-opacity"
                                  aria-label={t('updateColumnAccess')}
                                  aria-haspopup="dialog"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Lock size={14} className="text-gray-500" />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent align="center" sideOffset={8} collisionPadding={8} className="w-64 p-4" onClick={e => e.stopPropagation()}>
                                <div className="space-y-3">
                                  <p className="font-medium text-sm">{t('whoCanViewColumn')}</p>
                                  {column.authorizedUserList && column.authorizedUserList.length > 0 ? (
                                    <div className="space-y-1 max-h-32 overflow-y-auto">
                                      {column.authorizedUserList.map((user: any, idx: number) => (
                                        <div 
                                          key={idx} 
                                          className="flex items-center gap-2 text-xs text-gray-700 p-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors cursor-pointer"
                                          title={user.email || user.fullName}
                                        >
                                          <div className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-medium">
                                            {getUserInitials(user)}
                                          </div>
                                          <span className="truncate">{user.fullName || user.email}</span>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-xs text-gray-500">{t('noUsersSelected')}</p>
                                  )}
                                  {(() => {
                                    const authorizedActions = (configuration?.authorizedActions || []).map((a: any) => typeof a === 'string' ? a : a.actionCode);
                                    const canEditColumns = authorizedActions.includes("EditColumns");
                                    return canEditColumns ? (
                                      <Button 
                                        size="sm" 
                                        variant="outline" 
                                        className="w-full mt-2" 
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditFieldAccess(column);
                                        }}
                                      >
                                        {t('updateColumnAccess')}
                                      </Button>
                                    ) : null;
                                  })()}
                                </div>
                              </PopoverContent>
                            </Popover>
                          )}
                          
                          {/* Note info indicator */}
                          {column.displayNote && column.note && (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button
                                  type="button"
                                  className="ml-1 p-1 rounded hover:bg-gray-100 transition-opacity"
                                  aria-label={t('note')}
                                  aria-haspopup="dialog"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Info size={14} className="text-blue-500" />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent align="center" sideOffset={8} collisionPadding={8} className="w-80 p-4" onClick={e => e.stopPropagation()}>
                                <div 
                                  className="text-sm text-gray-700 whitespace-pre-wrap break-words"
                                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(column.note || '') }} 
                                />
                              </PopoverContent>
                            </Popover>
                          )}
                          
                          {/* Render content based on column type */}
                          {column.isActionColumn && column.actionType === 'menu' ? (
                            // V2: Actions column - show custom icon
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center justify-center" aria-label={t('actions')}>
                                    <MoreVertical className="text-gray-500" size={16} />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{t('actions')}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : column.isActionColumn && column.actionType === 'message' ? (
                            // Message column - show icon only
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center justify-center" aria-label={t('viewMessages')}>
                                    <MessageCircle className="h-4 w-4 text-gray-500" />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{t('viewMessages')}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            // Regular data columns
                            <>
                              <span 
                                className="truncate" 
                                style={{ 
                                  lineHeight: '1.2', 
                                  textAlign: 'center',
                                  color: column.mappingSource ? '#9333ea' : (column.isCustomField ? '#2563eb' : undefined) // Purple for mapped fields, blue for custom fields
                                }}
                              >
                                {getLocalizedField(column, 'columnName', language)}
                              </span>
                              {/* Only show arrow on hover or if sorted */}
                              {isSortable && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', width: 16, height: 16, flexShrink: 0, marginLeft: 4 }}>
                                  {sortState.columnCode === column.columnCode ? (
                                    sortState.direction === 'asc' ? <ArrowUp size={16} className="text-blue-600" /> : <ArrowDown size={16} className="text-blue-600" />
                                  ) : (
                                    hoveredHeader === column.columnCode && <ArrowUp size={16} className="text-gray-300" />
                                  )}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                        <span className="flex items-center gap-1" style={{ minWidth: showFilterIcon ? 32 : 0, flexShrink: 0 }}>
                          {/* Filter icon (if available) */}
                          {showFilterIcon && (
                            <Popover open={activeFilterColumn === column.columnCode} onOpenChange={(open) => { if (!open) handlePopoverClose() }}>
                              <PopoverTrigger asChild>
                                <button
                                  type="button"
                                  className={`ml-1 p-1 rounded hover:bg-gray-100 transition-opacity filter-icon-btn ${filtered ? 'text-blue-600' : 'text-gray-400'}`}
                                  tabIndex={-1}
                                  onClick={e => { e.stopPropagation(); handleFilterIconClick(e, column.columnCode); }}
                                >
                                  <CustomFilterIcon size={16} />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent align="center" sideOffset={8} collisionPadding={8} className="w-64 p-4" onClick={e => e.stopPropagation()} style={{ fontFamily: 'Roboto, sans-serif', maxHeight: '60vh', overflowY: 'auto' }}>
                                <div className="flex flex-col gap-3">
                                  {/* Search input for filtering options */}
                                  {(column.isFacetable || isFilterableMenu) && (
                                    <Input
                                      value={popoverSearch}
                                      onChange={e => setPopoverSearch(e.target.value)}
                                      placeholder={t('search')}
                                      autoFocus
                                      className="mb-2 focus:border-gray-300 focus:outline-none"
                                      onClick={e => e.stopPropagation()}
                                    />
                                  )}
                                  {/* Facet filter (isFacetable) */}
                                  {column.isFacetable ? (
                                    (() => {
                                      // If column has codeValueSource, use code values instead of facets
                                      const useCodeValues = column.codeValueSource && codeValues && codeValues.isReady;
                                      const facetOptions = useCodeValues 
                                        ? getFilterOptionsForColumn(column).map((opt: any) => ({
                                            value: opt.code,
                                            count: undefined,
                                            code: opt.code,
                                            titleEnglish: opt.titleEnglish,
                                            titleFrench: opt.titleFrench,
                                            color: opt.color,
                                            isDeleted: opt.isDeleted
                                          }))
                                        : getFacetOptionsForColumn(stageFacets, column);
                                      const rawValue = popoverFilterValues[column.columnCode];
                                      const selectedValues = Array.isArray(rawValue) ? rawValue : [];
                                      const shouldScroll = facetOptions.length > 4;
                                      // Filter options by search (only by label)
                                      const filteredOptions = popoverSearch.trim()
                                        ? facetOptions.filter((opt: any) => {
                                            const label = getOptionLabel(opt, column, sortedAssignedUsers, language);
                                            return label.toLowerCase().includes(popoverSearch.trim().toLowerCase());
                                          }).sort((a: any, b: any) => {
                                            // Sort alphabetically by label after filtering
                                            const labelA = getOptionLabel(a, column, sortedAssignedUsers, language);
                                            const labelB = getOptionLabel(b, column, sortedAssignedUsers, language);
                                            return labelA.toLowerCase().localeCompare(labelB.toLowerCase());
                                          })
                                        : facetOptions;
                                      
                                      // Add (blank) option to the top of the list
                                      const allOptions = [
                                        { value: "__BLANK__", count: undefined, label: t('blank') },
                                        ...filteredOptions
                                      ];
                                      
                                      // Always show at least the (blank) option, even if no facet options are available
                                      if (facetOptions.length === 0) {
                                        const rawValue = popoverFilterValues[column.columnCode];
                                        const selectedValues = Array.isArray(rawValue) ? rawValue : [];
                                        return (
                                          <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 240 }}>
                                            <div style={{ flex: 1, overflowY: 'auto' }}>
                                              <label className="flex items-center gap-2 cursor-pointer" onClick={e => e.stopPropagation()}>
                                                <Checkbox
                                                  checked={selectedValues.includes('__BLANK__')}
                                                  onCheckedChange={() => {
                                                    const next = selectedValues.includes('__BLANK__')
                                                      ? selectedValues.filter((v: string) => v !== '__BLANK__')
                                                      : [...selectedValues, '__BLANK__'];
                                                    setPopoverFilterValues(prev => ({ ...prev, [column.columnCode]: next }));
                                                  }}
                                                />
                                                <span>{t('blank')}</span>
                                              </label>
                                            </div>
                                            <div style={{ borderTop: '1px solid #eee', paddingTop: 8, background: '#fff' }}>
                                              <div className="flex gap-2">
                                                <Button variant="outline" size="sm" onClick={e => {
                                                  e.stopPropagation();
                                                  handlePopoverClose();
                                                }} style={{ flex: 1 }}>
                                                  {t('apply')}
                                                </Button>
                                                <Button variant="destructive" size="sm" onClick={e => {
                                                  e.stopPropagation();
                                                  handleClearColumnFilter(column);
                                                }} style={{ flex: 1 }}>
                                                  {t('clearAll')}
                                                </Button>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      }
                                      
                                      return (
                                        <div style={{ display: 'flex', flexDirection: 'column', maxHeight: shouldScroll ? 240 : undefined }}>
                                          <div style={{ flex: 1, overflowY: shouldScroll ? 'auto' : undefined }}>
                                            {allOptions.map((opt: any) => {
                                              const value = getOptionValue(opt);
                                              let user = null;
                                              if (column.columnCode === 'AssignedToUser' && Array.isArray(sortedAssignedUsers)) {
                                                user = sortedAssignedUsers.find(u => u.email?.toLowerCase() === value.toLowerCase());
                                              }
                                              const label = opt.label || getOptionLabel(opt, column, sortedAssignedUsers, language);
                                              const checked = selectedValues.includes(value);
                                              if (!label) return null;
                                              const tooltip = user && user.email ? user.email : undefined;
                                              return (
                                                <label key={value} className="flex items-center gap-2 cursor-pointer" onClick={e => e.stopPropagation()} title={tooltip}>
                                                  <Checkbox
                                                    checked={checked}
                                                    onCheckedChange={() => {
                                                      const next = checked
                                                        ? selectedValues.filter((v: string) => v !== value)
                                                        : [...selectedValues, value];
                                                      setPopoverFilterValues(prev => ({ ...prev, [column.columnCode]: next }));
                                                    }}
                                                  />
                                                  <span>{label} {'count' in opt && opt.count !== undefined ? `(${opt.count})` : ''}</span>
                                                </label>
                                              );
                                            })}
                                          </div>
                                          <div style={{ borderTop: '1px solid #eee', paddingTop: 8, background: '#fff' }}>
                                            <div className="flex gap-2">
                                              <Button variant="outline" size="sm" onClick={e => {
                                                e.stopPropagation();
                                                handlePopoverClose();
                                              }} style={{ flex: 1 }}>
                                                {t('apply')}
                                              </Button>
                                              <Button variant="destructive" size="sm" onClick={e => {
                                                e.stopPropagation();
                                                handleClearColumnFilter(column);
                                              }} style={{ flex: 1 }}>
                                                {t('clearAll')}
                                              </Button>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })()
                                  ) : isFilterableMenu && !column.isFacetable ? (
                                    (() => {
                                      const shouldScroll = filterOptions.length > 4;
                                      // Filter options by search (only by label)
                                      const filteredOptions = popoverSearch.trim()
                                        ? filterOptions.filter((opt: any) => {
                                            const label = getOptionLabel(opt, column, sortedAssignedUsers, language);
                                            return label.toLowerCase().includes(popoverSearch.trim().toLowerCase());
                                          }).sort((a: any, b: any) => {
                                            // Sort alphabetically by label after filtering
                                            const labelA = getOptionLabel(a, column, sortedAssignedUsers, language);
                                            const labelB = getOptionLabel(b, column, sortedAssignedUsers, language);
                                            return labelA.toLowerCase().localeCompare(labelB.toLowerCase());
                                          })
                                        : filterOptions;
                                      
                                      // Add (blank) option to the top of the list
                                      const allOptions = [
                                        { label: t('blank'), value: "__BLANK__", code: "__BLANK__" },
                                        ...filteredOptions
                                      ];
                                      
                                      return (
                                        <div style={{ display: 'flex', flexDirection: 'column', maxHeight: shouldScroll ? 240 : undefined }}>
                                          <div style={{ flex: 1, overflowY: shouldScroll ? 'auto' : undefined }}>
                                            {allOptions.map((opt: any) => {
                                              const code = typeof opt.code === 'string' ? opt.code : String(opt.code ?? '');
                                              const value = getOptionValue(opt);
                                              const label = getOptionLabel(opt, column, sortedAssignedUsers, language);
                                              const rawValue = popoverFilterValues[column.columnCode];
                                              const selectedValues = Array.isArray(rawValue) ? rawValue : [];
                                              const checked = selectedValues.includes(value);
                                              return (
                                                <label key={code} className="flex items-center gap-2 cursor-pointer" onClick={e => e.stopPropagation()}>
                                                  <Checkbox
                                                    checked={checked}
                                                    onCheckedChange={() => {
                                                      const next = checked
                                                        ? selectedValues.filter((v: string) => v !== value)
                                                        : [...selectedValues, value];
                                                      setPopoverFilterValues(prev => ({ ...prev, [column.columnCode]: next }));
                                                    }}
                                                  />
                                                  <span>{label} {'count' in opt && opt.count !== undefined ? `(${opt.count})` : ''}</span>
                                                </label>
                                              );
                                            })}
                                          </div>
                                          <div style={{ borderTop: '1px solid #eee', paddingTop: 8, background: '#fff' }}>
                                            <div className="flex gap-2">
                                              <Button variant="outline" size="sm" onClick={e => {
                                                e.stopPropagation();
                                                handlePopoverClose();
                                              }} style={{ flex: 1 }}>
                                                {t('apply')}
                                              </Button>
                                              <Button variant="destructive" size="sm" onClick={e => {
                                                e.stopPropagation();
                                                handleClearColumnFilter(column);
                                              }} style={{ flex: 1 }}>
                                                {t('clearAll')}
                                              </Button>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })()
                                  ) : column.isSearchable ? (
                                    <SearchableFieldWithSuggestions column={column} />
                                  ) : isNumberField(column) ? (
                                    <>
                                      <Input
                                        type="number"
                                        value={popoverFilterValues[column.columnCode] || ''}
                                        onChange={e => setPopoverFilterValues(prev => ({ ...prev, [column.columnCode]: e.target.value }))}
                                        onKeyDown={e => { if (e.key === 'Enter') handlePopoverClose(); }}
                                        placeholder={t('enterValue')}
                                        autoFocus
                                        onClick={e => e.stopPropagation()}
                                        className="focus:border-gray-300 focus:outline-none"
                                      />
                                      <div style={{ borderTop: '1px solid #eee', paddingTop: 8, background: '#fff' }}>
                                        <div className="flex gap-2">
                                          <Button variant="outline" size="sm" onClick={e => {
                                            e.stopPropagation();
                                            handlePopoverClose();
                                          }} style={{ flex: 1 }}>
                                            {t('apply')}
                                          </Button>
                                          <Button variant="destructive" size="sm" onClick={e => {
                                            e.stopPropagation();
                                            handleClearColumnFilter(column);
                                          }} style={{ flex: 1 }}>
                                            {t('clearAll')}
                                          </Button>
                                        </div>
                                      </div>
                                    </>
                                  ) : isDateField(column) ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 240 }}>
                                      <div style={{ flex: 1, overflowY: 'auto' }}>
                                        <div className="flex flex-col gap-2" onClick={e => e.stopPropagation()}>
                                          <ModernDatePicker value={popoverFilterValues[column.columnCode]?.start || null} onChange={val => setPopoverFilterValues(prev => ({ ...prev, [column.columnCode]: { ...(prev[column.columnCode] || {}), start: val } }))} placeholder={t('startDate')} />
                                          <ModernDatePicker value={popoverFilterValues[column.columnCode]?.end || null} onChange={val => setPopoverFilterValues(prev => ({ ...prev, [column.columnCode]: { ...(prev[column.columnCode] || {}), end: val } }))} placeholder={t('endDateOptional')} />
                                        </div>
                                      </div>
                                      <div style={{ borderTop: '1px solid #eee', paddingTop: 8, background: '#fff' }}>
                                        <div className="flex gap-2">
                                          <Button variant="outline" size="sm" onClick={e => {
                                            e.stopPropagation();
                                            handlePopoverClose();
                                          }} style={{ flex: 1 }}>
                                            {t('apply')}
                                          </Button>
                                          <Button variant="destructive" size="sm" onClick={e => {
                                            e.stopPropagation();
                                            handleClearColumnFilter(column);
                                          }} style={{ flex: 1 }}>
                                            {t('clearAll')}
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  ) : null}
                                </div>
                              </PopoverContent>
                            </Popover>
                          )}
                        </span>
                        {/* Resize handle — omitted on the fixed-width icon columns (Actions,
                            Message). Rendering it there lets a user drag a column whose width is
                            pinned, so the drag appears to work and is discarded on the next render. */}
                        {isFixedWidthColumn(column) ? null : (
                        <div
                          className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize transition-all duration-200 resize-handle"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleResizeStart(e, column.fieldConfigurationId);
                          }}
                          style={{
                            zIndex: Z_INDEX.FILTER_ICON,
                            background: isResizing && resizingColumn === column.fieldConfigurationId 
                              ? 'rgba(75, 85, 99, 0.8)' // Darker grey when selected
                              : 'rgba(156, 163, 175, 0.1)', // Very subtle grey background
                            borderLeft: '1px solid rgba(156, 163, 175, 0.3)',
                            pointerEvents: 'auto',
                            right: '-1px',
                            transition: 'all 0.2s ease',
                            opacity: 0,
                            width: '8px', // Smaller default width
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = '1';
                            e.currentTarget.style.background = 'rgba(156, 163, 175, 0.6)';
                            e.currentTarget.style.width = '10px'; // Slightly smaller hover width
                          }}
                          onMouseLeave={(e) => {
                            if (!isResizing || resizingColumn !== column.fieldConfigurationId) {
                              e.currentTarget.style.opacity = '0';
                              e.currentTarget.style.background = 'rgba(156, 163, 175, 0.1)';
                              e.currentTarget.style.width = '8px'; // Back to small size
                            }
                          }}
                        />
                        )}
                      </div>
                    </SortableTableHead>
                  );
                })}
                </SortableContext>
              </TableRow>
            </Header>

            <TableBody>
              {/* Show loading spinner only for the rows when isLoadingRows, isLoadingConfiguration, or isLoadingWorkOrders is true */}
              {safeItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + (hideCheckboxColumn ? 0 : 1)} className="text-center text-gray-500 py-8" aria-label={t('noRecordsFound')}>
                    {/* Visual overlay handles the display; this row preserves table semantics for accessibility */}
                  </TableCell>
                </TableRow>
              ) : (
                safeItems.map((item: any) => {
                  if (!item.id) return null
                  // Extract FboId and ObId (case-insensitive, fallback to undefined)
                  const fboId = item.FboId ?? item.fboId ?? undefined
                  const obId = item.ObId ?? item.obId ?? undefined
                  const hasRowActions = !!fboId || !!obId
                  const isChecked = selectedItems.includes(item.id);
                  
                  // Check if context menu should be enabled
                  // V2: Disabled - all actions moved to Actions column
                  const isContextMenuEnabled = false; // hasRowActions && selectedItems.length <= 1 && isDesktop();
                  
                  return (
                    <Fragment key={item.id}>
                      <TableRow
                        key={item.id}
                        onClick={contextEntityType === 'ClientRequest' ? (e) => {
                          // Row-click opens the request for ALL ClientRequest users. On the client
                          // Portal (no kebab) this is the only way in; internal users get it in
                          // addition to the kebab (View Details / Start Onboarding). The
                          // interactive-control guard below keeps inline-edit cells / checkbox /
                          // kebab working without triggering a row open.
                          // Portaled overlays (the kebab DropdownMenu content, popovers, AND the
                          // message sidebar/backdrop — all rendered under document.body) bubble
                          // their click through the React tree to this handler, which was opening
                          // the detail panel instead of running the overlay's own behaviour. Bail
                          // whenever the click didn't originate inside this row's own DOM subtree.
                          // NOTE: this supersedes the old `isMessageSidebarOpen` guard — that state
                          // (driven by a MutationObserver) could get stuck `true` on a backdrop
                          // close and then block every row click until another interaction re-synced
                          // it. The DOM-containment check has no such state dependency.
                          if (!e.currentTarget.contains(e.target as Node)) return;
                          // Ignore clicks that land on interactive controls (checkbox, kebab,
                          // inline-edit inputs, cell links) so those keep their own behaviour.
                          const target = e.target as HTMLElement;
                          if (target.closest('button, a, input, label, [role="menuitem"], [role="checkbox"], [data-no-row-open]')) return;
                          const requestType = (item as any).requestType ?? (item as any).RequestType;
                          if (requestType === 'Onboarding') {
                            // Onboarding rows open the onboarding FORM, not the readonly panel —
                            // exactly as the bespoke grid did (handleRowClick). buildOnboardingPath
                            // resolves the route off parentApp: client Portal users get the portal
                            // onboarding form (/requests/onboarding/{id} or /requests/{id} once
                            // submitted), internal FBO users get the sync onboarding form. Uses
                            // PreVerificationRequestId (the gw-sync request id), falling back to
                            // RequestId.
                            const onbId = ((item as any).preVerificationRequestId
                              ?? (item as any).PreVerificationRequestId
                              ?? (item as any).requestId
                              ?? (item as any).RequestId) as string | undefined;
                            const status = ((item as any).status ?? (item as any).Status) as any;
                            if (onbId) {
                              sendMessageToParent({ action: 'NAVIGATE', route: buildOnboardingPath(parentApp, onbId, status) });
                            }
                            return;
                          }
                          // Non-onboarding rows: open the read-only detail panel (same one the
                          // kebab's View Details opens).
                          const rid = ((item as any).requestId ?? (item as any).RequestId ?? null) as string | null;
                          if (rid) setDetailPanelRequestId(rid);
                        } : undefined}
                        onContextMenu={isContextMenuEnabled ? (e) => {
                          e.preventDefault()

                          // Track context menu opening
                          trackWorkflowEvent('Click Tracking', {
                            action: 'context_menu_open',
                            rowId: item.id,
                            recordType: item.recordType,
                            stage: stage
                          });

                          setRowActionMenu({
                            open: true,
                            fboId,
                            obId,
                            rowId: item.id,
                            x: e.clientX,
                            y: e.clientY,
                          })
                        } : undefined}
                        className={cn(
                          "group hover:bg-slate-100",
                          isChecked && "bg-blue-100 hover:bg-blue-100",
                          expandedAssignments.has(item.id) && "bg-blue-50"
                        )}
                        style={{
                          cursor: isContextMenuEnabled
                            ? 'context-menu'
                            : (contextEntityType === 'ClientRequest' ? 'pointer' : undefined),
                          borderBottom: '1px solid #ECECEC',
                          height: 40,
                          minHeight: 40,
                          maxHeight: 40,
                          fontWeight: expandedAssignments.has(item.id) ? 500 : undefined,
                        }}
                      >
                        {/* V2: Checkbox cell with chevron on the right — hidden entirely for Onboarding when checkboxes are disabled */}
                        {!hideCheckboxColumn && (() => {
                          // Selection/expansion always wins over sidebar-open dimming;
                          // when the sidebar is open we apply the base color with a translucent
                          // alpha so the whole grid still looks faded but selection remains visible.
                          const baseColor = isChecked
                            ? '#DBEAFE'
                            : expandedAssignments.has(item.id)
                              ? '#EFF6FF'
                              : '#fff';
                          const translucent = isChecked
                            ? 'rgba(219, 234, 254, 0.6)'
                            : expandedAssignments.has(item.id)
                              ? 'rgba(239, 246, 255, 0.6)'
                              : 'rgba(255, 255, 255, 0.6)';
                          const background = isMessageSidebarOpen ? translucent : baseColor;
                          return (
                        <TableCell
                        // Checkbox/chevron column — don't open the line item on accidental clicks.
                        data-no-row-open=""
                        className={cn(
                          "text-center",
                          !isMessageSidebarOpen && !isChecked && !expandedAssignments.has(item.id) && "group-hover:!bg-slate-100"
                        )}
                        style={{
                          width: checkboxColWidth,
                          minWidth: checkboxColWidth,
                          maxWidth: checkboxColWidth,
                          padding: 0,
                          position: 'sticky',
                          left: 0,
                          zIndex: isMessageSidebarOpen ? Z_INDEX.FIXED_COLUMN_MESSAGE_SIDEBAR_OPEN : Z_INDEX.FIXED_CELL,
                          background,
                          borderRight: '1px solid #ECECEC',
                          borderBottom: '1px solid rgb(236, 236, 236)'
                        }}
                      >
                        <div className="flex items-center h-full">
                          {/* Checkbox area — only rendered when checkbox column is enabled */}
                          {showCheckbox && (
                            <div className="flex items-center justify-end" style={{ width: 56, height: '100%', padding: '12px 10px 12px 4px' }}>
                              <Checkbox
                                checked={isChecked}
                                onCheckedChange={() => toggleItemSelection(item.id)}
                                aria-label={isChecked ? 'Deselect row' : 'Select row'}
                              />
                            </div>
                          )}
                          {/* Chevron area — always rendered; chevron button appears when row has related work orders */}
                          <div className="flex items-center justify-center" style={{ width: showCheckbox ? 24 : CHEVRON_ONLY_WIDTH, height: '100%', padding: '12px 2px' }}>
                            {item.hasMultipleWorkOrders && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 flex-shrink-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleAssignmentExpansion(item);
                                }}
                                disabled={loadingChildren.has(item.id)}
                              >
                                {loadingChildren.has(item.id) ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <ChevronRight 
                                    className={cn(
                                      "h-3 w-3 transition-transform",
                                      expandedAssignments.has(item.id) && "rotate-90"
                                    )}
                                  />
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                      </TableCell>
                          );
                        })()}

                      {/* Render all dynamic columns */}
                      {displayColumns.map((column) => {
                        const cellValue = getCellValue(item, column);
                        const columnWidth = getColumnWidth(columnWidths, column);
                        const currentStageConfig = stages.find(s => s.stageCode.toLowerCase() === (stage || '').toLowerCase());
                        const pinnedMap = state.pinnedColumnsByStage || {};
                        const pinnedId = currentStageConfig ? pinnedMap[currentStageConfig.stageId] : undefined;
                        const isSticky = pinnedId ? (displayColumns.findIndex(c => c.fieldConfigurationId === column.fieldConfigurationId) <= displayColumns.findIndex(c => c.fieldConfigurationId === pinnedId)) : false;
                        
                        // V2: Calculate sticky column positions
                        const actionsColumnLeft = checkboxColWidth; // Right after checkbox
                        const messageColumnLeft = actionsColumnLeft + actionsColumnWidth; // Right after Actions
                        const messageColumnWidth = messageColumn ? MESSAGE_COLUMN_WIDTH : 0;
                        const totalStickyWidth = actionsColumnLeft + actionsColumnWidth + (!isMessageColumnHidden && messageColumn ? messageColumnWidth : 0);
                        
                        // V2: Calculate left offset for columns
                        let leftOffset = checkboxColWidth; // Start with checkbox
                        
                        // V2: Check if this is the Actions or Message column
                        const isActionsColumn = column.isActionColumn && column.actionType === 'menu';
                        const isMessageColumn = column.isActionColumn && column.actionType === 'message';
                        
                        if (isActionsColumn) {
                          leftOffset = actionsColumnLeft;
                        } else if (isMessageColumn) {
                          leftOffset = messageColumnLeft;
                        } else if (isSticky) {
                          // For pinned columns: start after all sticky action columns
                          leftOffset = totalStickyWidth;
                          const currentIndex = displayColumns.findIndex(c => c.fieldConfigurationId === column.fieldConfigurationId);
                          for (let i = 0; i < currentIndex; i++) {
                            const col = displayColumns[i];
                            if (!col.isActionColumn) { // Skip action columns as they're already counted
                              leftOffset += getSafeColumnWidth(columnWidths, col.fieldConfigurationId);
                            }
                          }
                        }

                        let cellColor = undefined;
                        if (column.color) {
                          cellColor = column.color;
                        }
                        // Deliberately NOT a truthiness check. `false` and `0` are real values that carry
                        // their own CustomValue colour — a Boolean/YesNo column stores an actual boolean, so
                        // `cellValue &&` short-circuited on every "No" and left the cell white while "Yes"
                        // coloured correctly. Empty string still means "no value", so it stays uncoloured.
                        else if (column.customValues && cellValue !== null && cellValue !== undefined && cellValue !== '') {
                          const match = column.customValues.find((cv: any) => cv.code === String(cellValue));
                          if (match && match.color) cellColor = match.color;
                        }
                        // Boolean display mapping
                        let displayValue = cellValue;
                        if (column.columnTypeCode?.toLowerCase() === 'boolean') {
                          if (cellValue === true) {
                            displayValue = getLocalizedField(defaultBooleanOptions.find(opt => opt.code === 'true') || {}, 'title', language);
                          } else if (cellValue === false) {
                            displayValue = getLocalizedField(defaultBooleanOptions.find(opt => opt.code === 'false') || {}, 'title', language);
                          } else {
                            displayValue = '';
                          }
                        }
                        // Handle CodeValueSource for display value
                        else if (column.codeValueSource && codeValues.isReady && cellValue) {
                          try {
                            // Handle TextList (array values)
                            if (column.columnTypeCode?.toLowerCase() === 'textlist' && Array.isArray(cellValue)) {
                              const displayTexts = cellValue.map((code: any) => {
                                const codeStr = String(code);
                                const text = codeValues.getCodeValueTextByCode(codeStr, column.codeValueSource);
                                return text || codeStr;
                              });
                              displayValue = displayTexts.join(', ');
                            } else {
                              // Handle single value
                              const codeValueText = codeValues.getCodeValueTextByCode(String(cellValue), column.codeValueSource);
                              if (codeValueText) {
                                displayValue = codeValueText;
                              }
                            }
                          } catch (error) {
                            console.warn('Failed to get code value text for display:', error);
                          }
                        }
                        // Handle Collection type (arrays of objects like Alerts)
                        else if (column.columnTypeCode?.toLowerCase() === 'collection' && Array.isArray(cellValue)) {
                          // For collection columns, use GridCell component for proper rendering
                          displayValue = null; // Will render GridCell instead of displayValue
                        }
                        // Handle objects (with or without subColumnName)
                        else if (typeof cellValue === 'object' && cellValue !== null) {
                          displayValue = getDisplayValueFromColumn(cellValue, column);
                        }
                        
                        const isStickyLike = isSticky || isActionsColumn || isMessageColumn;
                        const rowHighlighted = isChecked || expandedAssignments.has(item.id);
                        const stickyFallbackBg = isChecked ? '#DBEAFE' : (expandedAssignments.has(item.id) ? '#EFF6FF' : '#fff');
                        return (
                          <TableCell
                            key={`${item.id}-${column.fieldConfigurationId}`}
                            // Non-data columns (Actions/kebab, Message) shouldn't open the line item
                            // on accidental clicks — mark the whole cell so the row handler skips it.
                            data-no-row-open={isActionsColumn || isMessageColumn ? '' : undefined}
                            className={cn(
                              isStickyLike && !cellColor && !rowHighlighted && "group-hover:!bg-slate-100"
                            )}
                            style={{
                              borderRight: '1px solid #ECECEC',
                              borderBottom: '1px solid #ECECEC',
                              // Tighter horizontal padding on the message column: 12px each side left a
                              // 24px icon only 26px of a 50px cell, so its count badge had nowhere to go.
                              padding: isMessageColumn ? '6px 4px' : '6px 12px',
                              fontSize: 14,
                              verticalAlign: 'middle',
                              width: columnWidth,
                              minWidth: columnWidth,
                              maxWidth: columnWidth,
                              whiteSpace: 'nowrap',
                              // The message icon's unread badge sits at the icon's top-right corner and
                              // protrudes past the cell box; a hidden cell clips it (the "count clipped
                              // at the top" report). The inner wrapper is already visible for this column
                              // — the cell itself has to be too, or it re-clips.
                              overflow: isMessageColumn ? 'visible' : 'hidden',
                              textOverflow: 'ellipsis',
                              backgroundColor: isStickyLike ? (cellColor || stickyFallbackBg) : (cellColor || undefined),
                              textAlign: 'center',
                              fontWeight: cellColor ? 500 : undefined,
                              position: (isSticky || isActionsColumn || isMessageColumn) ? 'sticky' : undefined,
                              left: isActionsColumn ? actionsColumnLeft : (isMessageColumn ? messageColumnLeft : (isSticky ? leftOffset : undefined)),
                              zIndex: isActionsColumn ? (isMessageSidebarOpen ? Z_INDEX.FIXED_COLUMN_MESSAGE_SIDEBAR_OPEN : 13) : (isMessageColumn && clickedMessageRowId === item.id ? Z_INDEX.MESSAGE_CELL_ACTIVE : (isMessageColumn && isMessageSidebarOpen ? Z_INDEX.MESSAGE_CELL_MESSAGE_SIDEBAR_OPEN : (isMessageColumn ? Z_INDEX.MESSAGE_CELL_DEFAULT : (isSticky ? Z_INDEX.CELL_CONTENT : undefined)))),
                            }}
                            ref={(cellRef) => {
                            }}
                          >
                            <div
                              className={cn(
                                "w-full text-ellipsis whitespace-nowrap",
                                // Second clipper. Leaving this hidden re-clips the badge and the
                                // preview even once the cell itself is visible.
                                isMessageColumn ? "overflow-visible" : "overflow-hidden"
                              )}
                              style={{ width: '100%' }}
                            >
                              {column.isActionColumn && column.actionType === 'menu' ? (
                                // V2: Actions dropdown menu
                                <div className="flex items-center justify-center">
                                  {/* Kebab visibility = showActionsColumn (internal-only +
                                      EnableActionsColumn). Redundant guard since the whole column
                                      is omitted from displayColumns when hidden, but kept explicit. */}
                                  {!showActionsColumn ? null : processingItemIds.has(item.id) ? (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Loader2 className="h-4 w-4 animate-spin text-blue-900" />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>{t('processingExtension')}</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  ) : (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                        }}
                                      >
                                        <MoreVertical className="text-gray-600" size={16} />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent 
                                      align="start" 
                                      side="right"
                                      className="min-w-[200px]"
                                      sideOffset={5}
                                    >
                                      <div className="px-2 py-1.5 text-sm font-medium text-gray-700">
                                        Actions
                                      </div>
                                      {(() => {
                                        const actions = getAvailableActions(
                                          configuration?.actionConfigurations || [],
                                          stage as WorkflowStage,
                                          item,
                                          actionUserContext
                                        );
                                        const regularActions = actions.filter(a => !a.isDefault);
                                        const defaultActions = actions.filter(a => a.isDefault);
                                        
                                        return (
                                          <>
                                            {regularActions.map((action) => (
                                              <DropdownMenuItem
                                                key={action.actionCode}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleActionClick(action, item);
                                                }}
                                                className="cursor-pointer"
                                              >
                                                {getActionIcon(action)}
                                                {language === 'fr' ? action.titleFrench : action.titleEnglish}
                                              </DropdownMenuItem>
                                            ))}
                                            {defaultActions.length > 0 && regularActions.length > 0 && (
                                              <DropdownMenuSeparator />
                                            )}
                                            {defaultActions.map((action) => (
                                              <DropdownMenuItem
                                                key={action.actionCode}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleActionClick(action, item);
                                                }}
                                                className="cursor-pointer"
                                              >
                                                {getActionIcon(action)}
                                                {language === 'fr' ? action.titleFrench : action.titleEnglish}
                                              </DropdownMenuItem>
                                            ))}
                                            {/* Messages — same rule as the Message column: shown
                                                only when this row actually resolves to a
                                                conversation, so the menu never offers an action
                                                that would silently do nothing. */}
                                            {messageActionsAvailable && canMarkMessages([item]) && (
                                              <>
                                                <DropdownMenuSeparator />
                                                <div className="px-2 py-1.5 text-sm font-medium text-gray-700">
                                                  {t('messages') || 'Messages'}
                                                </div>
                                                <DropdownMenuItem
                                                  disabled={isMarkingMessages}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    void markMessages([item], true);
                                                  }}
                                                  className="cursor-pointer"
                                                >
                                                  <MailOpen className="mr-2 h-4 w-4" />
                                                  {t('markAsRead') || 'Mark as read'}
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                  disabled={isMarkingMessages}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    void markMessages([item], false);
                                                  }}
                                                  className="cursor-pointer"
                                                >
                                                  <Mail className="mr-2 h-4 w-4" />
                                                  {t('markAsUnread') || 'Mark as unread'}
                                                </DropdownMenuItem>
                                                {/* Mute is a different act from marking read: read
                                                    state is about what already arrived, muting is
                                                    about what arrives next. Neither touches access —
                                                    a muted thread is still fully readable from its
                                                    record. */}
                                                <DropdownMenuItem
                                                  disabled={isMutingMessages}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    void muteMessages([item], false);
                                                  }}
                                                  className="cursor-pointer"
                                                >
                                                  <BellOff className="mr-2 h-4 w-4" />
                                                  {t('mute') || 'Mute'}
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                  disabled={isMutingMessages}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    void muteMessages([item], true);
                                                  }}
                                                  className="cursor-pointer"
                                                >
                                                  <Bell className="mr-2 h-4 w-4" />
                                                  {t('unmute') || 'Unmute'}
                                                </DropdownMenuItem>
                                              </>
                                            )}
                                          </>
                                        );
                                      })()}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                  )}
                                </div>
                              ) : column.isActionColumn && column.actionType === 'message' ? (
                                // Message column — entity-aware. For ClientRequest rows the
                                // conversation entity is the request itself (ClientRequest /
                                // Request / OnboardingRequest, computed per-row from
                                // requestType+version). For AtsPlacementRequest / VmsRequest
                                // rows it is the request's own doc id. For WorkOrder /
                                // Assignment rows the conversation entity is the linked
                                // Assignment (the historic default, kept by omitting
                                // entityTypeId on MessageTrigger).
                                // Remaining entity types (VmsEmail, PreOnboardRequest, …) don't
                                // have a Message field config seeded today; if one is added
                                // later, extend the branch below before the column renders.
                                (() => {
                                  // Shared with the bulk mark read/unread actions — see
                                  // lib/message-conversation-key.ts. Do not inline this again.
                                  const messageProps = resolveMessageConversationKey(contextEntityType, item as any);
                                  return (
                                    <div className="flex items-center justify-center" data-no-row-open>
                                      {messageProps && (
                                        <MessageTrigger
                                          markReadOnOpen={messageNotificationsEnabled}
                                          entityId={messageProps.entityId}
                                          entityTypeId={messageProps.entityTypeId}
                                          title={item.workerFullName || item.workerName || 'Worker'}
                                          // md (32px button / 20px icon) not sm (24px / 12px): the
                                          // design bubble is ~17px and the unread badge hangs off the
                                          // corner. On the 24px button the badge overflowed so far it
                                          // clipped; a 32px button in the 68px cell gives it room.
                                          size="md"
                                          zIndex={isSticky ? Z_INDEX.CELL_CONTENT : (column.isActionColumn && column.actionType === 'message' && isMessageSidebarOpen && clickedMessageRowId === item.id ? Z_INDEX.MESSAGE_CELL_ACTIVE : (column.isActionColumn && column.actionType === 'message' && isMessageSidebarOpen ? Z_INDEX.MESSAGE_CELL_MESSAGE_SIDEBAR_OPEN : (column.isActionColumn && column.actionType === 'message' ? Z_INDEX.MESSAGE_CELL_DEFAULT : undefined)))}
                                          onMessageClick={() => setClickedMessageRowId(item.id)}
                                          onSidebarStateChange={(isOpen) => {
                                            setIsMessageSidebarOpen(isOpen);
                                            if (!isOpen) setClickedMessageRowId(null);
                                          }}
                                        />
                                      )}
                                    </div>
                                  );
                                })()
                              ) : (() => {
                                // Regular data columns - V2: Respect isEditingEnabled flag
                                const cellLinkAction = canViewNavigationLinks
                                  ? configuration?.actionConfigurations?.find(
                                      (a: any) =>
                                        a.columnCode === column.columnCode &&
                                        a.actionType === 'external_link' &&
                                        (!a.condition || evaluateCondition(a.condition, item))
                                    )
                                  : undefined;
                                const rawCellLinkUrl = cellLinkAction
                                  ? substituteFieldsInUrl(cellLinkAction.link ?? '', item)
                                  : null;
                                const cellLinkUrl = rawCellLinkUrl && !/\$\{[^}]+\}/.test(rawCellLinkUrl)
                                  ? rawCellLinkUrl
                                  : null;

                                const gridCell = (
                                  <GridCell
                                    order={item}
                                    column={column}
                                    stage={stage as WorkflowStage}
                                    stageConfig={currentStageConfig}
                                    isEditing={editingCell?.rowId === item.id && editingCell?.columnId === column.fieldConfigurationId}
                                    onEdit={() => handleCellEdit(item.id!, column.fieldConfigurationId)}
                                    onSave={(value) => handleCellSave(item.id!, column.columnCode, value)}
                                    onCancel={() => setEditingCell(null)}
                                    fieldNameOverride={column.columnCode}
                                    canEditFieldData={canEditFieldData && isEditingEnabled && column.isEditable}
                                  />
                                );

                                const safeCellLinkUrl = (() => {
                                  if (!cellLinkUrl) return null;
                                  try {
                                    const parsed = new URL(cellLinkUrl);
                                    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? cellLinkUrl : null;
                                  } catch {
                                    return null;
                                  }
                                })();

                                // Editable data cells must NOT trigger the CR row-click (which opens
                                // the detail panel) — clicking one should enter inline-edit mode.
                                // Tag the wrapper with data-no-row-open; the row onClick guard skips
                                // any click whose target is inside a [data-no-row-open] element.
                                const isCellEditable = canEditFieldData && isEditingEnabled && column.isEditable;
                                return safeCellLinkUrl ? (
                                  <a
                                    href={safeCellLinkUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    // draggable=false: a left-button DRAG selects the cell text
                                    // instead of triggering the browser's native link-drag
                                    // (ghost image, no selection). Users can copy the value;
                                    // a plain click still navigates.
                                    draggable={false}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // The mouseup that ends a selection drag still fires a
                                      // click on this anchor — don't navigate away from (and
                                      // thereby discard) a selection the user just made in
                                      // THIS cell. Scoped to the cell so a stale selection
                                      // elsewhere on the page can't eat normal clicks.
                                      const selection = window.getSelection();
                                      if (
                                        selection &&
                                        !selection.isCollapsed &&
                                        selection.anchorNode &&
                                        e.currentTarget.contains(selection.anchorNode)
                                      ) {
                                        e.preventDefault();
                                      }
                                    }}
                                    className="group/link relative text-[#10597e] no-underline hover:text-[#062f47] hover:bg-sky-100 rounded [&:hover_.truncate]:underline transition-all truncate block w-full select-text"
                                  >
                                    {gridCell}
                                    <ExternalLink className="absolute right-1.5 top-1/2 -translate-y-1/2 h-3 w-3 text-[#10597e] pointer-events-none opacity-0 translate-x-2 group-hover/link:opacity-100 group-hover/link:translate-x-0 transition-all duration-200" />
                                  </a>
                                ) : isCellEditable ? (
                                  <span data-no-row-open className="block w-full">{gridCell}</span>
                                ) : gridCell;
                              })()}
                            </div>
                          </TableCell>
                        );
                      })}
                    </TableRow>

                    {/* V2: Child WorkOrder rows */}
                    {expandedAssignments.has(item.id) && childWorkOrders[item.id]?.map((child: any) => {
                      const childRow = mergeRetainedFromParent(child, item, displayColumns);
                      return (
                      <TableRow
                        key={child.id}
                        className="bg-gray-50 text-gray-600"
                        style={{
                          borderBottom: '1px solid #ECECEC',
                          height: 40,
                          minHeight: 40,
                          maxHeight: 40,
                        }}
                      >
                        {/* Checkbox cell for child - with indent indicator — hidden entirely for Onboarding when checkboxes are disabled */}
                        {!hideCheckboxColumn && <TableCell
                          style={{
                            width: checkboxColWidth,
                            minWidth: checkboxColWidth,
                            maxWidth: checkboxColWidth,
                            padding: 0,
                            position: 'sticky',
                            left: 0,
                            zIndex: Z_INDEX.FIXED_CELL,
                            background: '#f9fafb',
                            borderRight: '1px solid #ECECEC',
                            borderBottom: '1px solid rgb(236, 236, 236)'
                          }}
                        >
                          <div className="flex items-center h-full">
                            {showCheckbox && (
                              <div className="flex items-center justify-end" style={{ width: 56, height: '100%', padding: '6px 10px 6px 4px' }}>
                                <span className="text-xs text-gray-400">└─</span>
                              </div>
                            )}
                            <div style={{ width: showCheckbox ? 24 : CHEVRON_ONLY_WIDTH, height: '100%' }} />
                          </div>
                        </TableCell>}

                        {/* Render child data columns (read-only using GridCell) */}
                        {displayColumns.map((column) => {
                          const columnWidth = getColumnWidth(columnWidths, column);
                          
                          // V2: Check if this is the Actions or Message column that should be sticky
                          const isActionsColumn = column.isActionColumn && column.actionType === 'menu';
                          const isMessageColumn = column.isActionColumn && column.actionType === 'message';
                          
                          // V2: Calculate sticky column positions
                          const actionsColumnLeft = checkboxColWidth;
                          const messageColumnLeft = actionsColumnLeft + actionsColumnWidth;

                          return (
                            <TableCell
                              key={`${child.id}-${column.fieldConfigurationId}`}
                              style={{
                                borderRight: '1px solid #ECECEC',
                                borderBottom: '1px solid #ECECEC',
                                padding: '6px 12px',
                                fontSize: 14,
                                verticalAlign: 'middle',
                                width: columnWidth,
                                minWidth: columnWidth,
                                maxWidth: columnWidth,
                                whiteSpace: 'nowrap',
                                overflow: isMessageColumn ? 'visible' : 'hidden',
                                textOverflow: 'ellipsis',
                                backgroundColor: '#f9fafb',
                                textAlign: 'center',
                                pointerEvents: 'none',
                                position: (isActionsColumn || isMessageColumn) ? 'sticky' : undefined,
                                left: isActionsColumn ? actionsColumnLeft : (isMessageColumn ? messageColumnLeft : undefined),
                                zIndex: (isActionsColumn || isMessageColumn) ? Z_INDEX.FIXED_CELL : undefined,
                              }}
                            >
                              <div className={cn(
                                "w-full text-ellipsis whitespace-nowrap",
                                isMessageColumn ? "overflow-visible" : "overflow-hidden"
                              )}>
                                {column.isActionColumn ? (
                                  // No actions for child items
                                  null
                                ) : (
                                  // Use GridCell for proper formatting (read-only, non-interactive)
                                  <GridCell
                                    order={childRow}
                                    column={column}
                                    stage={stage as WorkflowStage}
                                    stageConfig={stages.find(s => s.stageCode.toLowerCase() === (stage || '').toLowerCase())}
                                    isEditing={false}
                                    onEdit={() => {}}
                                    onSave={() => {}}
                                    onCancel={() => {}}
                                    fieldNameOverride={column.columnCode}
                                    canEditFieldData={false}
                                  />
                                )}
                              </div>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    )
                    })}
                  </Fragment>
                  )
                })
              )}
            </TableBody>
          </Table>
          {safeItems.length === 0 && !isLoadingRows && !isLoadingConfiguration && !isLoadingWorkOrders && (
            <div
              style={{
                position: 'sticky',
                left: 0,
                width: tableContainerRef.current ? `${tableContainerRef.current.clientWidth}px` : '100%',
                textAlign: 'center',
                padding: '40px 0',
                color: '#6B7280',
                fontSize: 14,
                pointerEvents: 'none',
              }}
            >
              {t('noRecordsFound')}
            </div>
          )}
          {(isLoadingRows || isLoadingConfiguration || isLoadingWorkOrders) && (
            <div
              style={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(255,255,255,0.6)',
                zIndex: Z_INDEX.AUDIT_PANEL,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'all'
              }}
            >
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                <span className="text-gray-700 font-medium">{t('loading')}</span>
              </div>
            </div>
          )}
          
          {/* Resize indicator */}
          {isResizing && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.1)',
                zIndex: Z_INDEX.AUDIT_PANEL_BACKDROP,
                pointerEvents: 'none',
                cursor: 'col-resize'
              }}
            />
          )}
          

        </div>
        </DndContext>
      </div>

      {/* Pagination below the table */}
      <div ref={paginationRef} className="flex justify-between mt-2" style={{ width: '100%' }}>
        {/* Left side - Rows per page selector */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">{t('rowsPer')}</span>
          <select
            value={state.itemsPerPage === null ? 'auto' : state.itemsPerPage.toString()}
            onChange={(e) => {
              const value = e.target.value;
              if (value === 'auto') {
                setItemsPerPage(null);
              } else {
                setItemsPerPage(Number(value));
              }
            }}
            className="border border-gray-300 rounded px-2 py-1 text-sm bg-white"
            style={{ minWidth: '80px' }}
          >
            <option value="auto">Auto</option>
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
        
        {/* Right side - Showing records and pagination navigation */}
        <div className="flex items-center gap-4">
          {/* Showing records */}
          <div className="text-sm text-gray-500">
            {t('showingRecords', { 
              start: (currentPage - 1) * (state.itemsPerPage !== null ? state.itemsPerPage : pageSize) + 1, 
              end: Math.min((currentPage - 1) * (state.itemsPerPage !== null ? state.itemsPerPage : pageSize) + (state.itemsPerPage !== null ? state.itemsPerPage : pageSize), totalCount), 
              total: totalCount 
            })}
          </div>
          
          {/* Pagination navigation */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className="flex items-center gap-1"
            >
              <ChevronLeft size={14} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="flex items-center gap-1"
            >
              <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      </div>

      {isEditColumnsOpen && <EditColumns isOpen={isEditColumnsOpen} onClose={() => setIsEditColumnsOpen(false)} />}

      {/* Workflow-hub data-export modal. Mounted only while open so the
          inner row-count probes don't fire when the user isn't actively
          configuring an export. The host computes two filter shapes:
            • searchStage / customFilters → fed to searchWorkflowItems for
              the count probe, which lets buildFilterFromStageAndFilters
              prepend `DocumentType eq 'Assignment'` for non-VMS stages.
            • exportFilter → the fully-built OData string sent to the BE
              export endpoint (which doesn't run buildFilter on its side).
          The two paths produce equivalent filters; we keep them separate
          because the search dispatch and the export dispatch consume the
          filter at different layers. */}
      {isExportModalOpen && stageConfig && (() => {
        // ── Column helpers (declared first so the otherStages map can
        //     reference them — JS temporal-dead-zone would throw if these
        //     were positioned below the map call, which is when
        //     otherStages.map's callbacks evaluate). ───────────────────
        //
        // UI-only columns ('Message', 'Actions') are filtered out — they
        // exist in the grid as render slots but aren't backed by the
        // search index. Including them in the export's SELECT crashes the
        // Azure Search call with "field not found". Same exclusion list
        // workflow-grid itself uses when partitioning columns elsewhere.
        const UI_ONLY_COLUMN_CODES = new Set(['Message', 'Actions'])
        const toFieldConfig = (col: any) => ({
          fieldConfigurationId: col.fieldConfigurationId,
          columnCode: col.columnCode,
          columnNameEnglish: col.columnNameEnglish,
          columnNameFrench: col.columnNameFrench,
          isPrivate: col.isPrivate,
          isEnabledForClients: col.isEnabledForClients,
        })
        const isExportableColumn = (col: any) =>
          col && col.columnCode && !UI_ONLY_COLUMN_CODES.has(col.columnCode)

        // ── User-applied filters (globals + per-stage) ─────────────────
        // Union of two sources so we never miss a filter the user is
        // looking at on the grid:
        //   • live context state (globalFilters, currentStageFilters) —
        //     reflects in-session user adjustments (toggle a facet, type a
        //     column filter); this is what triggerDataFetch reads and is
        //     what's actually applied to the grid right now.
        //   • configuration.savedGlobalFilters / savedStageFilters —
        //     post-merge view-default + UserViewState snapshot from the
        //     last configuration fetch. Anything saved on the view but
        //     not yet pulled into context (or removed from context
        //     transiently) lives here.
        // We dedupe by fieldConfigurationId so a filter present in both
        // sources doesn't double-apply. Live context wins on conflict —
        // its value is the most recent user intent.
        const live_globals = globalFilters
        const live_stage = currentStageFilters
        const liveGlobalIds = new Set(live_globals.map(f => f.fieldConfigurationId))
        const liveStageIds = new Set(live_stage.map(f => f.fieldConfigurationId))

        const savedGlobalsExtra = (configuration?.savedGlobalFilters ?? [])
          .filter(f => !liveGlobalIds.has(f.fieldConfigurationId))
        const savedStageExtra = (configuration?.savedStageFilters ?? [])
          .filter(f => f.stageId === stageConfig.stageId && !liveStageIds.has(f.fieldConfigurationId))

        const allGlobals = [...live_globals, ...savedGlobalsExtra]
        const allStageFilters = [...live_stage, ...savedStageExtra]

        const applicableGlobals = (stageConfig.fixedFilter || stageConfig.excludeGlobalFilters)
          ? []
          : allGlobals
        let userFilterClauses = [...applicableGlobals, ...allStageFilters]
          .map(f => f.filter)
          .filter((s): s is string => typeof s === 'string' && s.length > 0)
        if (isRequestEntityType) {
          // VMS docs don't carry Stage; drop any Stage-shaped clauses that
          // might have come from a saved view originally authored on a
          // WorkOrder stage. Same guard triggerDataFetch applies.
          const stageClauseRegex = /\bStage\s+(eq|ne|in)\b/i
          userFilterClauses = userFilterClauses.filter(f => !stageClauseRegex.test(f))
        }

        // ── Active-stage base filter clauses ───────────────────────────
        // For the count probe (uses buildFilterFromStageAndFilters): we
        // pass searchStage + customFilters so DocumentType prefix is
        // added by the FE helper.
        // For the export endpoint (no helper on its side): we pre-build
        // the complete filter including DocumentType.
        let activeSearchStage: string | undefined = undefined
        let activeCustomFilters: string[] = []
        let activeExportFilterParts: string[] = []

        if (stageConfig.fixedFilter) {
          // Tasks-style: fixedFilter is the COMPLETE base — already
          // includes any DocumentType discriminator. searchStage stays
          // undefined so the helper doesn't double-prepend Stage.
          const base = showCompleted
            ? stageConfig.fixedFilter
            : `${stageConfig.fixedFilter} and OnboardingStatus ne 'Completed'`
          activeCustomFilters = [base, ...userFilterClauses]
          activeExportFilterParts = [base, ...userFilterClauses]
        } else if (isRequestEntityType) {
          // VMS sub-tab: BE adds DocumentType eq 'VmsRequest' / 'VmsEmail'
          // server-side (see SearchVmsRequestsAsync). FE sends only the
          // show-completed clause + user filters.
          const hideCompletedVms = stageConfig.showCompletedToggle && !showCompleted
          const vmsBase = hideCompletedVms ? getVmsHideCompletedFilter(contextEntityType) : null
          activeCustomFilters = [vmsBase, ...userFilterClauses].filter(
            (s): s is string => typeof s === 'string' && s.length > 0
          )
          activeExportFilterParts = activeCustomFilters
        } else {
          // WorkOrder-shaped stage: buildFilterFromStageAndFilters will
          // add `DocumentType eq 'Assignment'` when searchStage is set.
          // For the export endpoint we add it manually.
          //
          // Use stageConfig.stageCode (canonical case from the seed) not
          // `stage` (the prop, which can be lowercased from URL routing).
          // The index stores stage values in their canonical case
          // ('Onboarding', 'Active', ...) and an Azure Search equality
          // check is case-SENSITIVE — a mismatch yields zero rows.
          activeSearchStage = stageConfig.stageCode
          activeCustomFilters = userFilterClauses
          activeExportFilterParts = [
            "DocumentType eq 'Assignment'",
            `Stage eq '${stageConfig.stageCode}'`,
            ...userFilterClauses,
          ]
        }
        const activeExportFilter = activeExportFilterParts.length > 0
          ? activeExportFilterParts.join(' and ')
          : undefined

        // ── Other stages (multi-stage picker) ──────────────────────────
        // Each non-active stage carries its OWN base filter only — we
        // intentionally don't propagate the user's per-stage filters to
        // other stages because the user hasn't visually applied them
        // there. Globals are inherited unless the target stage opts out.
        const otherStages = (configuration?.stageConfigurations || [])
          .filter(s => s.stageCode.toLowerCase() !== stage?.toLowerCase())
          .map(s => {
            // Per-stage applicable globals — same gate as the active
            // stage but evaluated against THIS stage's flags. Uses the
            // unioned allGlobals (live context + saved fallback) so we
            // don't drop user-applied filters in the multi-stage path
            // either.
            const stageGlobals = (s.fixedFilter || s.excludeGlobalFilters)
              ? []
              : allGlobals
                  .map(f => f.filter)
                  .filter((c): c is string => typeof c === 'string' && c.length > 0)
                  .filter(c => !isRequestEntityType || !/\bStage\s+(eq|ne|in)\b/i.test(c))

            // Per-stage filters the user has saved on THIS OTHER stage
            // (e.g. they were on Onboarding earlier, applied a filter,
            // are now exporting from Extension and adding Onboarding as
            // an extra sheet — that earlier-applied filter still needs
            // to scope Onboarding's export). Unions live context's
            // stageFilters (filtered by THIS stageId) with
            // savedStageFilters from configuration — same dedupe shape
            // the active stage uses, live wins on conflict. Without
            // this, picking a non-active stage would export 100 rows
            // when the user expected 4 (the filtered count they'd seen
            // last time they were on it).
            const livePerStage = stageFilters.filter(f => f.stageId === s.stageId)
            const livePerStageIds = new Set(livePerStage.map(f => f.fieldConfigurationId))
            const savedPerStageExtra = (configuration?.savedStageFilters ?? [])
              .filter(f => f.stageId === s.stageId && !livePerStageIds.has(f.fieldConfigurationId))
            const stagePerStage = [...livePerStage, ...savedPerStageExtra]
              .map(f => f.filter)
              .filter((c): c is string => typeof c === 'string' && c.length > 0)
              .filter(c => !isRequestEntityType || !/\bStage\s+(eq|ne|in)\b/i.test(c))

            let searchStageArg: string | undefined = undefined
            let custom: string[] = []
            let exportParts: string[] = []
            if (s.fixedFilter) {
              custom = [s.fixedFilter, ...stageGlobals, ...stagePerStage]
              exportParts = [s.fixedFilter, ...stageGlobals, ...stagePerStage]
            } else if (isRequestEntityType) {
              custom = [...stageGlobals, ...stagePerStage]
              exportParts = [...stageGlobals, ...stagePerStage]
            } else {
              // Canonical case from the stage config — case-sensitive
              // match against the index. Same care as the active branch.
              searchStageArg = s.stageCode
              custom = [...stageGlobals, ...stagePerStage]
              exportParts = [
                "DocumentType eq 'Assignment'",
                // s.stageCode is already canonical here (it comes from
                // configuration.stageConfigurations, which is the seed
                // source of truth).
                `Stage eq '${s.stageCode}'`,
                ...stageGlobals,
                ...stagePerStage,
              ]
            }

            return {
              stageCode: s.stageCode,
              stageTitle: getLocalizedField(s, 'title', language) || s.stageCode,
              searchStage: searchStageArg,
              customFilters: custom,
              exportFilter: exportParts.length > 0 ? exportParts.join(' and ') : undefined,
              orderBy: currentOrderBy,
              excludeGlobalFilters: s.excludeGlobalFilters,
              // Each stage carries its own visible-columns list — what the
              // user would see if they navigated to that stage. The modal
              // unions these across selected stages so the column picker
              // surfaces every column the user expects to see when picking
              // multi-stage exports (otherwise the picker only showed the
              // active stage's set and Onboarding-only columns were
              // invisible when exporting Extension + Onboarding together).
              visibleColumns: (s.fieldConfigurations || [])
                .filter(isExportableColumn)
                .map(toFieldConfig),
            }
          })

        // Visible columns + allAuthorizedColumns mappers reference the
        // toFieldConfig / isExportableColumn helpers defined at the top
        // of this IIFE (see hoisted block above).

        return (
          <ExportModal
            isOpen={isExportModalOpen}
            onClose={() => setIsExportModalOpen(false)}
            entityType={contextEntityType || 'WorkOrder'}
            activeStage={{
              stageCode: stageConfig.stageCode,
              stageTitle: getLocalizedField(stageConfig, 'title', language) || stageConfig.stageCode,
              searchStage: activeSearchStage,
              customFilters: activeCustomFilters,
              exportFilter: activeExportFilter,
              orderBy: currentOrderBy,
              // Active stage's visible columns — same exclusion (no
              // Message / Actions) so the modal's column list never
              // surfaces a column that would crash the export.
              visibleColumns: columns.filter(isExportableColumn).map(toFieldConfig),
            }}
            otherStages={otherStages}
            visibleColumns={columns.filter(isExportableColumn).map(toFieldConfig)}
            allAuthorizedColumns={
              (configuration?.fieldsInUse || [])
                .filter(isExportableColumn)
                .map(toFieldConfig)
            }
            language={language}
          />
        )
      })()}

      {isBrowser && contextMenu && (
        <ColumnContextMenu
          position={contextMenu.position}
          columnId={contextMenu.columnId}
          onClose={() => setContextMenu(null)}
        />
      )}

      {/* Right-click row action menu (popover style) */}
      {rowActionMenu?.open && (
        <>
          <div
            style={{
              position: 'fixed',
              top: rowActionMenu.y + 2,
              left: rowActionMenu.x + 2,
              zIndex: Z_INDEX.AUDIT_PANEL_CONTENT,
              minWidth: 220,
              background: 'white',
              borderRadius: 6,
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
              border: '1px solid #e5e7eb',
              padding: 0,
            }}
            onContextMenu={e => e.preventDefault()}
          >
            <div className="flex flex-col divide-y divide-gray-100">
              {/* History option */}
              {(() => {
                // Find the row by rowId
                const row = safeItems.find(i => i.id === rowActionMenu.rowId);
                
                return (
                  <button
                    className="text-left w-full px-4 py-3 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none flex items-center space-x-2"
                    onClick={() => {
                      // Track context menu item selection
                      trackWorkflowEvent('Click Tracking', {
                        action: 'context_menu_item_selected',
                        sourceCode: 'history',
                        rowId: rowActionMenu.rowId,
                        recordType: row?.recordType,
                      });
                      
                      // Open audit history panel
                      const itemTitle = row?.workerFullName || row?.jobTitle || row?.clientName || 'Workflow Item';
                      // Same dispatch as the action-callback path above —
                      // documentType verbatim, Assignment defaults to legacy.
                      const documentType = (row?.documentType || row?.DocumentType) as string | undefined;
                      const entityType = documentType && documentType !== 'Assignment' ? documentType : undefined;
                      // For VmsRequest / ClientRequest the safeItems row's
                      // `id` is the SHA256-derived search-index guid; the
                      // audit backend addresses the source Cosmos doc and
                      // would 404 against it. Prefer `requestId` for those
                      // families — matches the action-utils.ts history case
                      // and the V2 report endpoint resolution.
                      const docTypeLower = (documentType || '').toLowerCase();
                      const rowRequestId = (row as any)?.requestId || (row as any)?.RequestId;
                      // Unified-index families (VmsRequest/ClientRequest/AtsPlacementRequest)
                      // carry the SHA index guid as `id`; audit/automation-emails address the
                      // source Cosmos doc, so use RequestId. Empty docType covers legacy
                      // ClientRequest rows (DocumentType == null) and stays safe for WorkOrder
                      // (no requestId → falls back to the row id).
                      const auditItemId = (docTypeLower === 'vmsrequest' || docTypeLower === 'clientrequest' || docTypeLower === 'atsplacementrequest' || docTypeLower === '') && rowRequestId
                        ? rowRequestId
                        : rowActionMenu.rowId;
                      setAuditHistoryPanel({
                        isOpen: true,
                        itemId: auditItemId,
                        itemTitle,
                        entityType
                      });
                      setRowActionMenu(null);
                    }}
                    type="button"
                  >
                    <span>{t('viewHistory')}</span>
                  </button>
                );
              })()}
              
              {/* Dynamic external source links */}
              {(() => {
                // Find the row by rowId
                const row = safeItems.find(i => i.id === rowActionMenu.rowId)
                
                if (!row) {
                  return (
                    <div className="px-4 py-3 text-gray-500 text-sm">
                      {t('noActionsAvailable')}
                    </div>
                  )
                }
                
                // Process external sources using the new utility function
                // Condition evaluation handles which sources should be displayed
                const rowWithContext = buildConditionContext(row, stage as WorkflowStage, actionUserContext)
                const validSources = processExternalSources(externalSources, rowWithContext)
                
                // If no valid sources, show a message
                if (validSources.length === 0 && !rowActionMenu?.fboId) {
                  return (
                    <div className="px-4 py-3 text-gray-500 text-sm">
                      {t('noActionsAvailable')}
                    </div>
                  )
                }
                
                return validSources.map((externalSource, index) => (
                  <button
                    key={`${externalSource.titleEnglish}-${index}`}
                    className="text-left w-full px-4 py-3 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                    onClick={() => {
                      // Track context menu item selection
                      trackWorkflowEvent('Click Tracking', {
                        action: 'context_menu_item_selected',
                        title: getLocalizedField(externalSource, 'title', language),
                        rowId: rowActionMenu.rowId,
                        recordType: row?.recordType,
                      });
                      
                      // Use WindowRefService for consistent window opening
                      WindowRefService.openUrl(externalSource.url);
                      setRowActionMenu(null);
                    }}
                    type="button"
                  >
                    {getLocalizedField(externalSource, 'title', language)}
                  </button>
                ))
              })()}
            </div>
          </div>
          {/* Overlay to close menu on click outside */}
          <div
            style={{ position: 'fixed', inset: 0, zIndex: Z_INDEX.AUDIT_PANEL_OVERLAY }}
            onClick={() => setRowActionMenu(null)}
            onContextMenu={e => {
              e.preventDefault();
              setRowActionMenu(null);
            }}
          />
        </>
      )}

      {/* Global Filter Sidebar */}
      {drawerVisible && (
        <>
          {/* Overlay for click outside */}
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: Z_INDEX.AUDIT_PANEL_CLOSE,
              background: 'transparent',
            }}
            onClick={() => setIsGlobalFilterSidebarOpen(false)}
          />
          <div
            style={{
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              padding: 16,
              gap: 24,
              width: FILTER_DRAWER_WIDTH,
              height: '100vh',
              background: '#FFFFFF',
              border: '1px solid rgba(2, 1, 13, 0.1)',
              borderRadius: 16,
              position: 'fixed',
              top: 0,
              right: 0,
              zIndex: Z_INDEX.AUDIT_PANEL_HEADER,
              boxShadow: '-2px 0 8px rgba(0,0,0,0.08)',
              transform: isGlobalFilterSidebarOpen ? 'translateX(0)' : `translateX(${FILTER_DRAWER_WIDTH}px)`,
              opacity: isGlobalFilterSidebarOpen ? 1 : 0,
              transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.3s cubic-bezier(0.4,0,0.2,1)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <span style={{ fontWeight: 700, fontSize: 22, color: '#02010D99' }}>
                {t('filter')}{appliedFilterPills.length > 0 ? ` (${appliedFilterPills.length})` : ''}
                </span>
                <button onClick={() => setIsGlobalFilterSidebarOpen(false)} style={{ background: 'none', border: 'none', fontSize: 28, cursor: 'pointer', color: '#23213D', lineHeight: 1 }}>×</button>
              </div>
              <div style={{ width: '100%', height: 1, background: 'rgba(2, 1, 13, 0.1)', margin: '8px 0 16px 0' }} />
              {/* Active filters — every filter narrowing the grid right now, in one
                  place, sectioned: view-level "Global Filters" (blue) and this
                  stage's column filters (purple). Same list as the toolbar strip's
                  +N expansion. */}
              {appliedFilterPills.length > 0 && (
                <div style={{ width: '100%', marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#02010D99', marginBottom: 8 }}>
                    {t('activeFilters') || 'Active filters'}
                  </div>
                  <GroupedPillList pills={appliedFilterPills} groupLabels={pillGroupLabels} />
                </div>
              )}
              {/* Search bar */}
              <div style={{ width: '100%', marginBottom: 16 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  background: '#fff',
                  border: '1px solid #E0E3EB',
                  borderRadius: 8,
                  padding: '0 12px',
                  height: 40,
                  width: '100%',
                }}>
                  <input
                    type="text"
                    placeholder={t('search')}
                    style={{
                      border: 'none',
                      outline: 'none',
                      flex: 1,
                      fontSize: 16,
                      background: 'transparent',
                    }}
                    value={globalFilterSearch}
                    onChange={e => setGlobalFilterSearch(e.target.value)}
                  />
                  <svg width="18" height="18" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', marginLeft: 4 }}>
                    <path d="M15.5 15.5l-3-3m1.5-5A6 6 0 1 1 3 3a6 6 0 0 1 11 4.5Z" stroke="#9795B5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', width: '100%' }}>
              {/* List global filter fields */}
              {getFilteredGlobalFilterFields().map(({ field, options }) => {
                const showCount = globalFilterShowCount[field.fieldConfigurationId] || 3;
                const selected = globalFilterSelections[field.fieldConfigurationId] || [];
                const facetOptions = getFacetOptionsForColumnSidebar(facets, field);
                // For Select All logic, use filtered options when searching, otherwise use all facetOptions
                const optionsForSelectAll = globalFilterSearch.trim() ? options : facetOptions;
                const allOptionValues = [
                  ...optionsForSelectAll.map((opt: { value: string }) => opt.value),
                  '__BLANK__'
                ];
                const hasSelectAll = selected.length === allOptionValues.length && 
                                   allOptionValues.every(val => selected.includes(val));
                const hasSomeSelected = selected.length > 0;
                
                // Determine the state of the Select All checkbox
                let selectAllChecked: boolean | 'indeterminate';
                if (hasSelectAll) {
                  selectAllChecked = true;
                } else if (hasSomeSelected) {
                  selectAllChecked = 'indeterminate';
                } else {
                  selectAllChecked = false;
                }
                
                // Use filtered options from search, not unfiltered facetOptions
                // Map options with labels first
                const optionsWithLabels = options.map((opt: { value: string; count: number }) => {
                  let label = resolveSidebarOptionLabel(opt, field);
                  return { ...opt, label };
                });
                
                // Sort options: selected items first, then unselected, each group sorted alphabetically
                const sortedOptions = optionsWithLabels.sort((a, b) => {
                  const aSelected = selected.includes(a.value);
                  const bSelected = selected.includes(b.value);
                  
                  // If selection status is different, selected items come first
                  if (aSelected !== bSelected) {
                    return aSelected ? -1 : 1;
                  }
                  
                  // If both have same selection status, sort alphabetically by label
                  return (a.label || '').localeCompare(b.label || '');
                });
                
                return (
                  // data-global-filter-section: scroll anchor for the applied-filter
                  // pills' click-to-edit (editGlobalFilterFromPill).
                  <div key={field.fieldConfigurationId} data-global-filter-section={field.fieldConfigurationId} style={{ marginBottom: 24, width: '100%' }}>
                    <div style={{ fontWeight: 700, marginBottom: 8, color: '#23213D', fontSize: 16 }}>{getLocalizedField(field, 'columnName', language)}{selected.length > 0 ? ` (${selected.length})` : ''}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {/* Select All option */}
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 16, color: '#23213D', fontWeight: 400 }}>
                        <Checkbox
                          checked={selectAllChecked}
                          onCheckedChange={() => handleGlobalCheckboxChange(field.fieldConfigurationId, '__SELECT_ALL__')}
                        />
                        <span style={{ fontSize: 16, color: '#23213D', fontWeight: 400 }}>{t('selectAll')}</span>
                      </label>
                      
                      {/* Separator line after Select all */}
                      <div style={{ width: '100%', height: 1, background: 'rgba(2, 1, 13, 0.1)', margin: '4px 0' }} />
                      
                      {/* (blank) option */}
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 16, color: '#23213D', fontWeight: 400 }}>
                        <Checkbox
                          checked={selected.includes('__BLANK__')}
                          onCheckedChange={() => handleGlobalCheckboxChange(field.fieldConfigurationId, '__BLANK__')}
                        />
                        <span style={{ fontStyle: 'italic', color: '#666' }}>{t('blank')}</span>
                      </label>
                      
                      {/* Regular options */}
                      {sortedOptions.slice(0, showCount).map(opt => (
                        <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 16, color: '#23213D', fontWeight: 400 }}>
                          <Checkbox
                            checked={selected.includes(opt.value)}
                            onCheckedChange={() => handleGlobalCheckboxChange(field.fieldConfigurationId, opt.value)}
                          />
                          <span>{opt.label} {'count' in opt && opt.count !== undefined ? `(${opt.count})` : ''}</span>
                        </label>
                      ))}
                      {sortedOptions.length > showCount && (
                        <button
                          style={{ background: 'none', border: 'none', color: '#2B345E', cursor: 'pointer', padding: 0, marginTop: 4, fontSize: 15, fontWeight: 500 }}
                          onClick={() => handleShowMore(field.fieldConfigurationId, sortedOptions.length)}
                        >
                          {t('showMore')}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Bottom buttons */}
            <div style={{ width: '100%', marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 0 }}>
              <div style={{ width: '100%', height: 1, background: 'rgba(2, 1, 13, 0.1)', margin: '0 0 0 0' }} />
              <button
                style={{
                  width: '100%',
                  height: 48,
                  background: 'none',
                  border: 'none',
                  color: '#2B345E',
                  fontWeight: 500,
                  fontSize: 17,
                  cursor: 'pointer',
                  textAlign: 'center',
                  margin: 0,
                  padding: 0,
                  transition: 'color 0.2s',
                  borderBottom: '1px solid #E0E3EB',
                  borderRadius: 0,
                  letterSpacing: '0.1px',
                }}
                onClick={() => {
                  handleGlobalApply();
                }}
              >
                {t('apply')}
              </button>
              <button
                style={{
                  width: '100%',
                  height: 48,
                  background: 'none',
                  border: 'none',
                  color: '#B71C1C',
                  fontWeight: 500,
                  fontSize: 17,
                  cursor: 'pointer',
                  textAlign: 'center',
                  margin: 0,
                  padding: 0,
                  transition: 'color 0.2s',
                  borderRadius: 0,
                  letterSpacing: '0.1px',
                }}
                onClick={() => {
                  handleGlobalClearAll();
                }}
              >
                {t('clearAll')}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Bulk Edit Modal */}
      <BulkEditModal
        isOpen={isBulkEditOpen}
        onClose={() => setIsBulkEditOpen(false)}
        selectedItems={selectedItems}
        bulkUpdateReports={bulkUpdateReports}
        applyOptimisticUpdates={applyOptimisticUpdates}
        setSelectedItems={setSelectedItems}
        t={t as (key: string) => string}
        fields={editableFields}
        language={language}
        assignedUsers={sortedAssignedUsers}
        // Refresh via the grid's canonical fetch so the CURRENT filters (global + stage +
        // entity-type base) are preserved. The modal's own filter rebuild was producing an
        // empty filter for shared-index entities (ClientRequest/VMS) → "returns everything".
        // Silent: the modal applies its own optimistic overlay on success.
        refreshData={() => triggerDataFetch({ force: true, silent: true })}
      />
      {/* Move To Modal (to be implemented) */}
      <MoveToModal
        isOpen={isMoveToOpen}
        onClose={() => setIsMoveToOpen(false)}
        selectedItems={selectedItems}
        configuration={configuration}
        currentStage={stage}
        // Same as bulk-edit above: reuse triggerDataFetch so post-action reload keeps the
        // active filters instead of the modal rebuilding (and dropping) them.
        // Silent: succeeded rows were already optimistically removed/flipped.
        refreshData={() => triggerDataFetch({ force: true, silent: true })}
        setSelectedItems={setSelectedItems}
        language={language}
        t={t as (key: string) => string}
        currentOrderBy={currentOrderBy}
      />

      {/* Audit History Panel */}
      {auditHistoryPanel && (
        <AuditHistoryPanel
          isOpen={auditHistoryPanel.isOpen}
          onClose={() => setAuditHistoryPanel(null)}
          itemId={auditHistoryPanel.itemId}
          itemTitle={auditHistoryPanel.itemTitle}
          entityType={auditHistoryPanel.entityType}
        />
      )}

      {/* VMS Email Preview slide-out — opened by the ViewEmail (VmsRequest)
          and Preview (VmsEmail) kebab actions. Single instance: state is
          cleared on close so the next open refetches the body. */}
      {emailPreviewPanel && (
        <VmsEmailPreviewPanel
          isOpen={emailPreviewPanel.isOpen}
          onClose={() => setEmailPreviewPanel(null)}
          emailId={emailPreviewPanel.emailId}
          title={emailPreviewPanel.title}
        />
      )}

      {/* Action Confirmation Modal */}
      {actionConfirmationModal && (
        <ActionConfirmationModal
          isOpen={actionConfirmationModal.isOpen}
          onClose={() => setActionConfirmationModal(null)}
          action={actionConfirmationModal.action}
          items={actionConfirmationModal.items}
          // Custom action handlers pass their own onConfirm; stage transitions
          // fall back to the shared handler.
          onConfirm={actionConfirmationModal.onConfirm ?? handlePerformStageTransition}
          language={language}
        />
      )}

      {/* Extend Work Order Modal */}
      <ExtendWorkOrderModal
        isOpen={isExtendModalOpen}
        onClose={() => {
          const wasSuccess = extendSucceededRef.current;
          const processedItem = extendModalItem;
          setIsExtendModalOpen(false);
          setExtendModalItem(null);
          extendSucceededRef.current = false;
          const processedItemId = processedItem?.id;
          if (wasSuccess && processedItemId) {
            // Mark the item as processing to disable its actions
            setProcessingItemIds(prev => new Set(prev).add(processedItemId));
            // Delay data and stage count refreshes to give the backend time to process
            refreshTimersRef.current.forEach(clearTimeout)
            refreshTimersRef.current = [
              setTimeout(() => { triggerDataFetch({ force: true, silent: true }); refreshStageCounts(); }, 10000),
              setTimeout(() => {
                triggerDataFetch({ force: true, silent: true }); refreshStageCounts();
                // Clear processing state after final refresh
                setProcessingItemIds(prev => {
                  const next = new Set(prev);
                  next.delete(processedItemId);
                  return next;
                });
              }, 25000),
            ]
          }
        }}
        item={extendModalItem}
        configuration={configuration}
        onValidateAtsPlacement={async (atsSourceId, atsPlacementId) => {
          const response = await validateAtsPlacement(atsSourceId, atsPlacementId);
          return response;
        }}
        onExtendWorkOrder={async (fboAssignmentId, atsPlacementId, confirmExtendExistingExtension) => {
          const response = await extendWorkOrder(fboAssignmentId, atsPlacementId, confirmExtendExistingExtension);
          if (response.success) {
            extendSucceededRef.current = true;
          }
          return response;
        }}
      />

      {/* Jump-to-column command palette (Cmd/Ctrl+K) */}
      <JumpToColumnDialog
        isOpen={jumpToColumn.isOpen}
        onOpenChange={jumpToColumn.setIsOpen}
        columns={jumpToColumnOptions}
        onPick={handleJumpToColumnPick}
      />

      {/* Client Request Modal */}
      <ClientRequestModal
        isOpen={isClientRequestModalOpen}
        onClose={() => {
          setIsClientRequestModalOpen(false);
          setClientRequestModalItem(null);
        }}
        item={clientRequestModalItem}
        requestType={clientRequestModalType}
      />

      {/* Related Requests Panel */}
      {/* Read-only ClientRequest detail side panel (info + documents), opened from the kebab. */}
      <ClientRequestReadonlyDetail
        requestId={detailPanelRequestId}
        isOpen={detailPanelRequestId !== null}
        onClose={() => setDetailPanelRequestId(null)}
      />

      <RelatedRequestsPanel
        isOpen={relatedRequestsPanelItem !== null}
        onClose={() => setRelatedRequestsPanelItem(null)}
        fboAssignmentId={(relatedRequestsPanelItem?.fboAssignmentId ?? relatedRequestsPanelItem?.FboAssignmentId) as number | undefined}
        workerName={(relatedRequestsPanelItem?.workerFullName ?? relatedRequestsPanelItem?.workerName) as string | undefined}
      />

      {/* VMS Start Onboarding iframe — opens the same onboarding-form
          microapp ClientRequest uses, just with VMS source. The drawer's
          header shows "VMS-{shortId}" and the form is labelled with our
          custom sourceName. On submit success we only refresh the grid —
          no status change on the VMS doc (lifecycle stays on the
          OnboardingSession). */}
      <OnboardingIframePanel
        isOpen={vmsOnboardingContext !== null}
        onboardingId={vmsOnboardingContext?.onboardingId ?? null}
        idDisplayText={
          vmsOnboardingContext?.idDisplayText
            ?? (vmsOnboardingContext
              ? `VMS-${vmsOnboardingContext.externalRequestId ?? vmsOnboardingContext.requestId}`
              : undefined)
        }
        sourceName={vmsOnboardingContext?.sourceName ?? "VMS"}
        clientRequestStatus={vmsOnboardingContext?.clientRequestStatus}
        onClose={() => setVmsOnboardingContext(null)}
        onSubmitted={(result: SubmitResult) => {
          const ctx = vmsOnboardingContext;
          if (result.success) {
            // OnboardingFormService completes the source on the server as part of the submit: it accepts an ATS
            // placement, closes a ClientRequest's gw-sync Request (discards it, or completes it), which the
            // mirror sync maps to RequestAccepted, and links a VmsRequest back (VmsRequestLinkService). Nothing
            // here calls out; the grid only reflects the outcome, with one toast for it. A source the submit did
            // not accept keeps its status; an accepted one flips to the canonical 'RequestAccepted' at once (the
            // durable change lands via the refresh below; the field matches the wire shape, camelCase `status`).
            const feedback = getOnboardingSubmitFeedback(ctx?.entityType, result.outcome);
            const optimisticStatus = feedback.sourceAccepted && (ctx?.entityType === 'AtsPlacementRequest'
              || ctx?.entityType === 'ClientRequest'
              || ctx?.entityType === 'VmsRequest')
              ? 'RequestAccepted'
              : null;
            if (ctx?.rowId && optimisticStatus) {
              applyOptimisticUpdates([{ id: ctx.rowId, updates: { status: optimisticStatus } }], { silent: true });
            }
            toast({ variant: feedback.variant, title: t(feedback.titleKey), description: t(feedback.descriptionKey) });
            triggerDataFetch({ force: true, silent: true });
          } else {
            toast({ variant: 'destructive', title: t('error'), description: t('onboardingFailed') || 'Onboarding submission failed' });
          }
          setVmsOnboardingContext(null);
        }}
      />

      {/* Checklist Panel */}
      {checklistPanelState && (
        <ChecklistPanel
          isOpen={checklistPanelState.isOpen}
          onClose={() => setChecklistPanelState(null)}
          itemId={checklistPanelState.itemId}
          workOrderId={checklistPanelState.workOrderId}
          fieldConfigurationId={checklistPanelState.fieldConfigurationId}
          itemTitle={checklistPanelState.itemTitle}
          columnTitle={(() => {
            const column = displayColumns.find(col => col.fieldConfigurationId === checklistPanelState.fieldConfigurationId);
            return column ? getLocalizedField(column, 'columnName', language) : 'Checklist';
          })()}
          canEdit={checklistPanelState.canEdit ?? true}
          initialItems={(() => {
            const workOrder = workOrders.find(wo => wo.id === checklistPanelState.itemId);
            if (!workOrder) return [];
            const column = displayColumns.find(col => col.fieldConfigurationId === checklistPanelState.fieldConfigurationId);
            if (!column) return [];
            const value = getCellValue(workOrder, column);
            try {
              if (Array.isArray(value)) {
                // Backend returns array of items directly
                return value;
              } else if (value && typeof value === 'object' && value.items) {
                // Legacy format with items property
                return value.items;
              } else if (value && typeof value === 'string') {
                const parsed = JSON.parse(value);
                // Check if parsed is array or has items property
                if (Array.isArray(parsed)) {
                  return parsed;
                }
                return parsed.items || [];
              }
            } catch (error) {
              console.warn('Failed to parse checklist data:', error);
            }
            return [];
          })()}
          onSave={async (items) => {
            try {
              // Find the column to get the columnCode
              const column = displayColumns.find(col => col.fieldConfigurationId === checklistPanelState.fieldConfigurationId);
              if (column) {
                // Send the items array directly
                await handleCellSave(checklistPanelState.itemId, column.columnCode, items);
              }
            } catch (error) {
              console.error('Failed to save checklist:', error);
            }
          }}
        />
      )}

      {/* Edit Field Modal */}
      {isEditFieldModalOpen && editFieldConfiguration && (
        <EditFieldModal
          isOpen={isEditFieldModalOpen}
          onClose={() => {
            setIsEditFieldModalOpen(false);
            setEditFieldConfiguration(null);
          }}
          fieldConfiguration={editFieldConfiguration}
          addMode={false}
          canEditCustomColumns={true}
        />
      )}

    </div>
  );
}
